import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, {
    type MutatorOptions,
    type SWRConfiguration,
    useSWRConfig,
} from 'swr';
import useSWRInfinite, { type SWRInfiniteConfiguration } from 'swr/infinite';

import {
    type OutreachUrgencyFilter,
    type OutreachWindowSelection,
} from '@apps/web/components/admin/outreach_observability/constants';
import { useSessionState } from '@apps/web/context/session_state_provider';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';
import { shouldAutoloadNextConversationPage } from '@apps/web/lib/conversation_page_autoload';
import {
    notificationFollowsRequest,
    notificationPreferencesRequest,
} from '@apps/web/lib/member_settings_api';
import { useMode } from '@apps/web/lib/mode_store';
import {
    normalizeRecommendationDefaultOptionsResponse,
    normalizeRecommendationDefaults,
    type RecommendationAvailabilitySource,
    type RecommendationDefaultColumnFilterValue,
    type RecommendationDefaultFilterOptions,
    type RecommendationDefaultOptions,
    type RecommendationDefaults,
} from '@apps/web/lib/recommendation_defaults';
import { type RecommendationUnitType } from '@apps/web/lib/recommendation_selection_api';
import { getRuntimeConfig } from '@apps/web/lib/runtime_config';
import { NONTERMINAL_EXECUTION_STATUSES } from '@apps/web/lib/voice_eval_execution';
import {
    type ConversationPagesMutator,
    type OrganizationConversationsResponse,
} from '@apps/web/swr_data_fetching/conversation_pages';
import { markConversationAsRead } from '@apps/web/swr_data_fetching/conversation_read_state';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type AdminOutboxEntryListResponse,
    type AdminOutreachListResponse,
    type AdminRecentRunItem,
    type AdminSyncSettingsUpdate,
    type AgentNotesForRunResponse,
    type AgentTraceRunCursorRead,
    type AgreementStatus,
    type ApiBodyType,
    ApiError,
    type ApiQueryParamsType,
    type ApiResponseType,
    type ClockReminderReport,
    type components,
    type DataSourceType,
    type FranchiseCreate,
    type FranchiseUpdate,
    type GetAutoCalloutBoardResponse,
    type GetOutreachObservabilityResponse,
    type GetOutreachTvResponse,
    type JoinedOutreachBucketRequest,
    type JoinedOutreachListResponse,
    type Mode,
    type OrganizationConversationMetadata,
    type OrgMemberRole,
    type OutreachStatus,
    type OutreachStatusFilter,
    type RevenueAccountStatus,
    type StagedMessageDomain,
    type VoipCxOwner,
    type VoipEngOwner,
    type VoipIntegrationType,
    type VoipPhoneProvider,
} from '@libraries/typescript/api_client';
import {
    type PhoebeAgentRunStatus,
    type RecommendationFilterNode,
    type RecurringOutreachKind,
    type TagDefinitionCell,
    type TagDefinitionEntityType,
    type TagDefinitionUpdate,
} from '@libraries/typescript/api_client/schema_types';

export type {
    ConversationPagesMutator,
    OrganizationConversationsResponse,
} from '@apps/web/swr_data_fetching/conversation_pages';

// Kept here for its callers; it lives in its own module so the v3 chat can
// import it without this file.
export { useJoinedOutreach } from '@apps/web/swr_data_fetching/use_joined_outreach';
export { useOutreachDetail } from '@apps/web/swr_data_fetching/use_outreach_detail';

type TraceParams = {
    readonly reason: string;
};

export type {
    RecommendationAvailabilitySource,
    RecommendationDefaultColumnFilterValue,
    RecommendationDefaultFilterOptions,
    RecommendationDefaultOptions,
    RecommendationDefaults,
};

export function useAdminTamCoverage() {
    return useApiQuery({
        path: '/admin/tam/coverage',
        method: 'get',
        select: (response) => response,
        reason: 'Load unified TAM coverage',
    });
}

export function useAdminTamFilterOptions() {
    return useApiQuery({
        path: '/admin/tam/filter-options',
        method: 'get',
        select: (response) => response,
        reason: 'Load unified TAM filters',
    });
}

export function useAdminTamAccounts(
    queryParams: ApiQueryParamsType<'/admin/tam/accounts', 'get'>,
) {
    return useApiQuery({
        path: '/admin/tam/accounts',
        method: 'get',
        queryParams,
        select: (response) => response,
        keepPreviousData: true,
        reason: 'Browse unified TAM agencies',
    });
}

export function useAdminTamAccount(parentDomain: string | null) {
    return useApiQuery({
        path: '/admin/tam/accounts/{parent_domain}',
        method: 'get',
        pathParams: parentDomain ? { parent_domain: parentDomain } : undefined,
        enabled: parentDomain !== null,
        select: (response) => response.account ?? null,
        reason: 'Open unified TAM agency',
    });
}

const RECOMMENDATION_DEFAULTS_PATH =
    '/organizations/{organization_id}/recommendation-defaults';
const RECOMMENDATION_DEFAULT_OPTIONS_PATH =
    '/organizations/{organization_id}/recommendation-defaults/options';
const REFERRAL_PROGRAM_ME_PATH =
    '/organizations/{organization_id}/referral-program/me';
const ORGANIZATION_POST_SETUP_ONBOARDING_PATH =
    '/organizations/{organization_id}/post-setup-onboarding';

type OrganizationRecommendationDefaultsResponse = ApiResponseType<
    typeof RECOMMENDATION_DEFAULTS_PATH,
    'get'
>;
export type ReferralProgramMeResponse = ApiResponseType<
    typeof REFERRAL_PROGRAM_ME_PATH,
    'get'
>;

function selectOrganizationRecommendationDefaults(
    response: OrganizationRecommendationDefaultsResponse,
): RecommendationDefaults | null {
    return response === null
        ? null
        : normalizeRecommendationDefaults(response as RecommendationDefaults);
}

export function useUser(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me',
        method: 'get',
        select: (response) => response.user,
        reason: traceParams.reason,
    });
}

export function useEmailNotificationPreferences(
    organizationId: string | null,
    reason?: string,
) {
    return useApiQuery({
        path: '/users/me/email-notification-preferences',
        method: 'get',
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason,
    });
}

export function useNotificationPreferences(
    organizationId: string | null,
    reason?: string,
    mode?: Mode,
) {
    return useApiQuery({
        ...notificationPreferencesRequest(organizationId),
        method: 'get',
        enabled: !!organizationId,
        select: (response) => response,
        reason,
        mode,
    });
}

// This gets user and member profile transactionally.
export function useUserProfile(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me/profile',
        method: 'get',
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrgMemberProfiles(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me/org-member-profiles',
        method: 'get',
        select: (response) => response.profiles,
        reason: traceParams.reason,
    });
}

// Update the authenticated user's own membership for one organization
// (e.g. their assigned office). Only set fields are persisted.
export async function updateMeOrgMemberProfile(
    organizationId: string,
    body: ApiBodyType<
        '/users/me/org-member-profiles/{organization_id}',
        'patch'
    >,
) {
    return getApiClient().patch({
        url: '/users/me/org-member-profiles/{organization_id}',
        pathParams: { organization_id: organizationId },
        body,
    });
}

export async function updateMeTestingRole(
    body: ApiBodyType<'/users/me/testing-role', 'post'>,
) {
    return getApiClient().post({
        url: '/users/me/testing-role',
        body,
    });
}

export function useTestingRoleSwitchAccess(traceParams: TraceParams) {
    return useApiQuery({
        path: '/users/me/testing-role-access',
        method: 'get',
        select: (response) => response.can_switch_testing_role,
        reason: traceParams.reason,
        dedupingInterval: 0,
    });
}

export function useOrganizationUsers(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/users',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.users,
        reason: traceParams.reason,
    });
}

export function useOrganizationMemberProfiles(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/members',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.members,
        reason: traceParams.reason,
    });
}

export function useReferralProgramMe(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: REFERRAL_PROGRAM_ME_PATH,
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// Normalized offices that exist in the organization, derived from the
// EHR-synced caregiver, client, and shift office fields.
export function useOrganizationOffices(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/offices',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.offices,
        reason: traceParams.reason,
    });
}

// Same endpoint as useOrganizationOffices, but returns the full response so
// callers can also read default_office_ids (the offices to pre-select by
// default, scoped to the org's data source account location_ids). Kept
// separate so existing useOrganizationOffices callers keep their array shape.
export function useOrganizationOfficesWithDefaults(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/offices',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// Per-office conversation counts for the inbox office filter. Counts are
// stable per-office totals (non-hidden conversations), not narrowed by the
// active tab or search.
export function useOrganizationConversationOfficeCounts(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/conversations/office-counts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationNotifications(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/notifications',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.notifications,
        reason: traceParams.reason,
    });
}

export function useConversation(
    conversationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/conversations/{conversation_id}',
        method: 'get',
        pathParams: conversationId
            ? { conversation_id: conversationId }
            : undefined,
        enabled: !!conversationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useAdminFlags(enabled = true) {
    return useApiQuery({
        path: '/admin/admin-flags',
        method: 'get',
        enabled,
        select: (response) => response,
    });
}

export function useAgentModelOptions(enabled = true) {
    return useApiQuery({
        path: '/admin/agent/model-options',
        method: 'get',
        enabled,
        select: (response) => response.options,
    });
}

export function usePromptRollout() {
    return useApiQuery({
        path: '/admin/prompt-rollout',
        method: 'get',
        select: (response) => response,
    });
}

export function useAgentPrompts() {
    return useApiQuery({
        path: '/admin/agent-traces/prompts',
        method: 'get',
        select: (response) => response.prompts,
    });
}

export function useAgentPrompt(promptKey: string | null) {
    return useApiQuery({
        path: '/admin/agent-traces/prompts/{prompt_key}',
        method: 'get',
        pathParams: promptKey ? { prompt_key: promptKey } : undefined,
        enabled: promptKey !== null,
        select: (response) => response,
    });
}

export function useAdminAgentSkillVersion(versionId: string | null) {
    return useApiQuery({
        path: '/admin/agent/skills/{override_id}',
        method: 'get',
        pathParams: versionId ? { override_id: versionId } : undefined,
        enabled: versionId !== null,
        select: (response) => response,
    });
}

export function useAdminAgentSkillHistory(skillName: string | null) {
    return useApiQuery({
        path: '/admin/agent/skills/{skill_name}/history',
        method: 'get',
        pathParams: skillName ? { skill_name: skillName } : undefined,
        enabled: skillName !== null,
        select: (response) => response.versions,
    });
}

export async function revertAdminAgentSkillVersion(versionId: string) {
    return getApiClient().post({
        url: '/admin/agent/skills/{override_id}/revert',
        pathParams: { override_id: versionId },
    });
}

/**
 * Mint a single-use ticket authorizing one notification WebSocket handshake.
 *
 * A WebSocket cannot send an `Authorization` header, and the extension
 * sidepanel renders in a storage-partitioned frame that sends no cookie on the
 * handshake either — so it has no credential it can attach to the socket
 * itself. This call carries the ordinary bearer/cookie credential over HTTP,
 * where it does work, and returns a ticket the socket can present as `?token`.
 *
 * The ticket is single-use and expires in 60s, so callers must mint a fresh
 * one per connection attempt rather than caching it across reconnects.
 */
export async function createOrganizationWebsocketTicket(
    organizationId: string,
) {
    // Deliberately not `idempotent: true`. That is the only way to get the
    // client's own timeout, but it also enables the retry ladder — and issuing
    // is a write, so each attempt mints another live ticket. `useWebSocket`
    // bounds the call instead, which covers every resolver rather than this
    // one.
    return getApiClient().post({
        url: '/organizations/{organization_id}/events/ws-ticket',
        pathParams: { organization_id: organizationId },
    });
}

export function usePromptVersionFilterOptions() {
    return useApiQuery({
        path: '/admin/prompt-rollout/version-filter-options',
        method: 'get',
        select: (response) => response,
    });
}

export function usePromptQuality(promptKey: string | null) {
    return useApiQuery({
        path: '/admin/prompt-rollout/quality',
        method: 'get',
        queryParams: promptKey ? { prompt_key: promptKey } : undefined,
        enabled: !!promptKey,
        select: (response) => response,
    });
}

export function useCanonicalTagRules() {
    return useApiQuery({
        path: '/admin/canonical-tag-rules',
        method: 'get',
        select: (response) => response,
    });
}

export function useCanonicalCaregiverVocab() {
    return useApiQuery({
        path: '/admin/canonical-tag-rules/caregiver-vocab',
        method: 'get',
        select: (response) => response,
    });
}

export function useDemoOrgStatus() {
    return useApiQuery({
        path: '/admin/demo-org',
        method: 'get',
        select: (response) => response,
    });
}

export function useRecommendationBatchCandidates(
    organizationId: string | null,
    batchId: string | null,
    options?: {
        readonly refreshIntervalMs?: number;
        readonly adminTrace?: boolean;
    },
) {
    const organizationQuery = useApiQuery({
        path: '/organizations/{organization_id}/recommendation-batches/{batch_id}/candidates',
        method: 'get',
        pathParams:
            !options?.adminTrace && organizationId && batchId
                ? { organization_id: organizationId, batch_id: batchId }
                : undefined,
        enabled: !options?.adminTrace && !!organizationId && !!batchId,
        select: (response) => response,
        refreshInterval: options?.refreshIntervalMs,
    });
    const adminQuery = useApiQuery({
        path: '/admin/agent-traces/recommendation-batches/{batch_id}/candidates',
        method: 'get',
        pathParams:
            options?.adminTrace && batchId ? { batch_id: batchId } : undefined,
        queryParams:
            options?.adminTrace && organizationId
                ? { organization_id: organizationId }
                : undefined,
        enabled: !!options?.adminTrace && !!organizationId && !!batchId,
        select: (response) => response,
        refreshInterval: options?.refreshIntervalMs,
    });
    return options?.adminTrace ? adminQuery : organizationQuery;
}

// A note's routing lands a few seconds after the run finishes (p90 ~22s in
// production), so the receipt polls at this cadence and stops the moment every
// note has settled.
//
// Polling is a v1 stand-in, not the intended design. The worker knows exactly
// when a note settles and this panel already sits beside a run event stream —
// the pipeline just has no event to emit yet. Revisit once it does: subscribe
// instead, and this interval goes away entirely.
const AGENT_NOTES_POLL_INTERVAL_MS = 5_000;
// Every stop condition is bounded by a per-turn window rather than by
// `all_settled` alone, because `all_settled` cannot be trusted on its own:
// it is vacuously true over an empty note list, and SWR re-evaluates it
// against the PREVIOUS turn's cached response the moment a new turn re-enables
// the query. Both readings say "finished" when nothing has been fetched yet.
// A note that never settles is bounded by the same window.
const AGENT_NOTES_MAX_POLLS_PER_TURN = 8;
// A note KNOWN to be unsettled earns a longer watch at a slower cadence:
// classify + item processing can take a minute under load, and dying at the
// 40s mark forces a manual refresh to see the outcome. Still
// bounded, so a note that never settles cannot poll forever.
const AGENT_NOTES_SETTLING_POLL_INTERVAL_MS = 10_000;
const AGENT_NOTES_MAX_EXTENDED_POLLS_PER_TURN = 24;

/**
 * Notes an agent run saved, with where each one landed.
 *
 * `enabled` must be false while a run is active: the notes resolve after the
 * turn ends, and mutating chips underneath a live conversation is exactly the
 * interruption this surface exists to avoid.
 */
export function useAgentNotesForConversation({
    organizationId,
    agentConversationId,
    agentRunId,
    enabled,
}: {
    readonly organizationId: string | null;
    // Exactly one of these. The run-native chat backend models a transcript as
    // a run, so its "conversation id" is a run id; the legacy backend has a
    // real conversation spanning several runs.
    readonly agentConversationId: string | null;
    readonly agentRunId: string | null;
    readonly enabled: boolean;
}) {
    const queryParams = agentConversationId
        ? { agent_conversation_id: agentConversationId }
        : agentRunId
          ? { agent_run_id: agentRunId }
          : undefined;
    // Reset per poll window, not per conversation. `enabled` goes false while a
    // run is active and true again when it ends, so it marks the turn boundary
    // — keying on the conversation alone would let turn 1's exhausted budget
    // stop every later turn from ever polling.
    const pollsThisTurnRef = useRef(0);
    // Stable identity is load-bearing, not tidiness: SWR keys its polling
    // effect on the callback's identity, so an inline arrow is re-invoked on
    // every render — which would count renders instead of polls and re-arm the
    // timer each time. Reads only refs and module constants.
    const refreshIntervalForNotes = useCallback(
        (latest: AgentNotesForRunResponse | undefined) => {
            pollsThisTurnRef.current += 1;
            // Notes we can SEE are unsettled get the extended window: the
            // pipeline is demonstrably still working, and stopping here is
            // what forced a manual refresh to surface late outcomes.
            if (latest && latest.notes.length > 0 && !latest.all_settled) {
                if (
                    pollsThisTurnRef.current >=
                    AGENT_NOTES_MAX_EXTENDED_POLLS_PER_TURN
                ) {
                    return 0;
                }
                return pollsThisTurnRef.current >=
                    AGENT_NOTES_MAX_POLLS_PER_TURN
                    ? AGENT_NOTES_SETTLING_POLL_INTERVAL_MS
                    : AGENT_NOTES_POLL_INTERVAL_MS;
            }
            if (pollsThisTurnRef.current >= AGENT_NOTES_MAX_POLLS_PER_TURN) {
                return 0;
            }
            // A settled conversation response can contain only the previous
            // turn's notes while the new note is still being created. Keep the
            // bounded base window open even when everything currently visible
            // is settled; otherwise the new note appears only on refresh.
            return AGENT_NOTES_POLL_INTERVAL_MS;
        },
        [],
    );
    const scopeKey = `${organizationId ?? ''}:${agentConversationId ?? ''}:${
        agentRunId ?? ''
    }:${enabled}`;
    const lastScopeKeyRef = useRef(scopeKey);
    if (lastScopeKeyRef.current !== scopeKey) {
        lastScopeKeyRef.current = scopeKey;
        pollsThisTurnRef.current = 0;
    }
    const query = useApiQuery({
        path: '/organizations/{organization_id}/agent-notes',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams,
        enabled: enabled && !!organizationId && queryParams !== undefined,
        select: (response) => response,
        refreshInterval: refreshIntervalForNotes,
    });
    return query;
}

/**
 * The organization's agency context — durable facts Phoebe uses across every
 * channel. Training-mode only, which the endpoint enforces.
 */
export function useAgencyContext({
    organizationId,
    enabled,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/agency-context',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: enabled && !!organizationId,
        select: (response) => response,
    });
}

export function useOrgSnapshotsStatus(refreshInterval?: number) {
    return useApiQuery({
        path: '/admin/org-snapshots',
        method: 'get',
        select: (response) => response,
        refreshInterval,
    });
}

export function useProdOrganizationSearch(search: string, enabled: boolean) {
    return useApiQuery({
        path: '/admin/org-snapshots/prod-organizations',
        method: 'get',
        queryParams: { search },
        enabled,
        select: (response) => response.organizations,
    });
}

export function useOrgSnapshotImportJob(
    jobId: string | null,
    refreshInterval?: number,
) {
    return useApiQuery({
        path: '/admin/org-snapshots/imports/{job_id}',
        method: 'get',
        pathParams: jobId ? { job_id: jobId } : undefined,
        enabled: jobId !== null,
        select: (response) => response,
        refreshInterval,
    });
}

export function useCaregiverSimState() {
    return useApiQuery({
        path: '/admin/caregiver-sim/personalities/state',
        method: 'get',
        select: (response) => response,
        refreshInterval: 5000,
    });
}

export function useCaregiverSimLogs(
    identifier: string | null,
    counterpartyPhone: string | null,
    callSid: string | null = null,
) {
    return useApiQuery({
        path: '/admin/caregiver-sim/logs/{identifier}',
        method: 'get',
        pathParams: identifier ? { identifier } : undefined,
        queryParams:
            counterpartyPhone || callSid
                ? {
                      ...(counterpartyPhone
                          ? { counterparty_phone: counterpartyPhone }
                          : {}),
                      ...(callSid ? { call_sid: callSid } : {}),
                  }
                : undefined,
        enabled: !!identifier,
        select: (response) => response,
        refreshInterval: 5000,
    });
}

export function useCaregiverSimCaregiverDetails(
    identifier: string | null,
    enabled = true,
) {
    return useApiQuery({
        path: '/admin/caregiver-sim/caregiver/{identifier}/details',
        method: 'get',
        pathParams: identifier ? { identifier } : undefined,
        enabled: !!identifier && enabled,
        select: (response) => response,
    });
}

export function useCaregiverSimMemoryEvents(
    identifier: string | null,
    counterpartyPhone: string | null,
    enabled = true,
) {
    return useApiQuery({
        path: '/admin/caregiver-sim/caregiver/{identifier}/memory-events',
        method: 'get',
        pathParams: identifier ? { identifier } : undefined,
        queryParams: counterpartyPhone
            ? { counterparty_phone: counterpartyPhone }
            : undefined,
        enabled: !!identifier && enabled,
        select: (response) => response,
        refreshInterval: 5000,
    });
}

export function useCaregiverSimLatestVoiceCall(
    identifier: string | null,
    counterpartyPhone: string | null,
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/admin/caregiver-sim/caregiver/{identifier}/latest-voice-call',
        method: 'get',
        pathParams: identifier ? { identifier } : undefined,
        queryParams: counterpartyPhone
            ? { counterparty_phone: counterpartyPhone }
            : undefined,
        enabled: !!identifier && (options.enabled ?? true),
        select: (response) => response,
        refreshInterval: 5000,
    });
}

export async function createCaregiverSimMemoryEvent(
    identifier: string,
    body: ApiBodyType<
        '/admin/caregiver-sim/caregiver/{identifier}/memory-events',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/caregiver/{identifier}/memory-events',
        pathParams: { identifier },
        body,
    });
}

export async function reflectCaregiverSimMemory(
    identifier: string,
    body: ApiBodyType<
        '/admin/caregiver-sim/caregiver/{identifier}/reflect',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/caregiver/{identifier}/reflect',
        pathParams: { identifier },
        body,
    });
}

export async function updateCaregiverSimCaregiverDetails(
    identifier: string,
    body: ApiBodyType<
        '/admin/caregiver-sim/caregiver/{identifier}/details',
        'patch'
    >,
) {
    return getApiClient().patch({
        url: '/admin/caregiver-sim/caregiver/{identifier}/details',
        pathParams: { identifier },
        body,
    });
}

export async function resetCaregiverSimPersonality(
    identifier: string,
    body: ApiBodyType<
        '/admin/caregiver-sim/personality/{identifier}/reset',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/personality/{identifier}/reset',
        pathParams: { identifier },
        body,
    });
}

export async function simulateCaregiverSimMessage(
    body: ApiBodyType<'/admin/caregiver-sim/simulate', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/simulate',
        body,
    });
}

export async function runCaregiverSimScenario(
    body: ApiBodyType<'/admin/caregiver-sim/scenario/run', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/scenario/run',
        body,
    });
}

export async function startCaregiverSimVoiceSession(
    body: ApiBodyType<'/admin/caregiver-sim/voice-sim/start', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/voice-sim/start',
        body,
    });
}

export async function startCaregiverSimRealVoiceCall(
    body: ApiBodyType<'/admin/caregiver-sim/voice-sim/start-real', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/voice-sim/start-real',
        body,
    });
}

export async function injectCaregiverSimVoiceUtterance(
    callId: string,
    body: ApiBodyType<
        '/admin/caregiver-sim/voice-sim/{call_id}/inject-utterance',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/voice-sim/{call_id}/inject-utterance',
        pathParams: { call_id: callId },
        body,
    });
}

export async function synthesizeCaregiverSimVoiceUtterance(
    callId: string,
    body: ApiBodyType<
        '/admin/caregiver-sim/voice-sim/{call_id}/synthesize-utterance',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/voice-sim/{call_id}/synthesize-utterance',
        pathParams: { call_id: callId },
        body,
    });
}

export async function endCaregiverSimVoiceSession(
    callId: string,
    body: ApiBodyType<'/admin/caregiver-sim/voice-sim/{call_id}/end', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/voice-sim/{call_id}/end',
        pathParams: { call_id: callId },
        body,
    });
}

export async function getCaregiverSimVoiceTimeline(callId: string) {
    return getApiClient().get({
        url: '/admin/caregiver-sim/voice-sim/{call_id}/timeline',
        pathParams: { call_id: callId },
    });
}

export async function reseedCaregiverSimCaregivers(
    body: ApiBodyType<'/admin/caregiver-sim/reseed-caregivers', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/reseed-caregivers',
        body,
    });
}

export function useCaregiverSimDailyRuns() {
    return useApiQuery({
        path: '/admin/caregiver-sim/daily-runs',
        method: 'get',
        select: (response) => response,
    });
}

export function useCaregiverSimDailyPersonas() {
    return useApiQuery({
        path: '/admin/caregiver-sim/daily-personas',
        method: 'get',
        select: (response) => response,
    });
}

export function useCaregiverSimWorldNow() {
    return useApiQuery({
        path: '/admin/caregiver-sim/world-now',
        method: 'get',
        select: (response) => response,
        refreshInterval: 15_000,
    });
}

export async function getCaregiverSimDailyRunReplay(runId: string) {
    return getApiClient().get({
        url: '/admin/caregiver-sim/daily-runs/{run_id}/replay',
        pathParams: { run_id: runId },
    });
}

type SimulatorWorldDisplayPersona = {
    readonly identifier: string;
    readonly name: string;
};

type SimulatorWorldDisplayShift = {
    readonly persona_identifier: string | null;
    readonly persona_name: string | null;
    readonly patient_name: string;
    readonly status: string;
    readonly start_at: string;
    readonly end_at: string;
};

type SimulatorWorldDisplayEvent = {
    readonly timestamp: string;
    readonly event_type: string;
    readonly label: string;
    readonly detail: string | null;
    readonly persona_identifier: string | null;
    readonly persona_name: string | null;
    readonly shift_id: string | null;
};

export type SimulatorWorldDisplayLiveResponse = {
    readonly simulated_at: string;
    readonly personas: ReadonlyArray<SimulatorWorldDisplayPersona>;
    readonly active_shifts: ReadonlyArray<SimulatorWorldDisplayShift>;
    readonly recent_events: ReadonlyArray<SimulatorWorldDisplayEvent>;
    readonly upcoming_events: ReadonlyArray<SimulatorWorldDisplayEvent>;
};

export async function getSimulatorWorldDisplayLive(
    token: string | null,
): Promise<SimulatorWorldDisplayLiveResponse> {
    const url = new URL(
        token
            ? '/simulator-world-display/live'
            : '/simulator-world-display/public-live',
        getRuntimeConfig().apiUrl,
    );
    const response = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });
    if (!response.ok) {
        const body = await response.json().catch(() => undefined);
        const detail =
            typeof body === 'object' &&
            body !== null &&
            'detail' in body &&
            typeof body.detail === 'string'
                ? body.detail
                : 'Unable to load Simulator World display.';
        throw new ApiError(detail, response.status, undefined, body);
    }
    return (await response.json()) as SimulatorWorldDisplayLiveResponse;
}

export async function triggerCaregiverSimDailyRun(
    body: ApiBodyType<'/admin/caregiver-sim/trigger-daily-run', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/caregiver-sim/trigger-daily-run',
        body,
    });
}

export function useAppConfig(organizationId: string | null) {
    return useApiQuery({
        path: '/app-config',
        method: 'get',
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useUsers() {
    return useApiQuery({
        path: '/admin/users',
        method: 'get',
        select: (response) => response.users,
    });
}

export function useAdminUsers(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    role?: OrgMemberRole;
    admin?: boolean;
}) {
    return useApiQuery({
        path: '/admin/users',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useAgentTraceCreatedByOptions(search?: string) {
    return useApiQuery({
        path: '/admin/agent-traces/created-by-options',
        method: 'get',
        queryParams: search ? { search } : undefined,
        select: (response) => response,
    });
}

export function useAdminUser(userId: string | null) {
    return useApiQuery({
        path: '/admin/users/{user_id}',
        method: 'get',
        pathParams: userId ? { user_id: userId } : undefined,
        enabled: !!userId,
        select: (response) => response,
    });
}

export function useAdminUserNotificationPreferences(
    userId: string | null,
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/users/{user_id}/notification-preferences',
        method: 'get',
        pathParams: userId ? { user_id: userId } : undefined,
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!userId && !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizations() {
    return useApiQuery({
        path: '/admin/organizations',
        method: 'get',
        select: (response) => response.organizations,
    });
}

export function useAdminOrganizations(params?: {
    page?: number;
    per_page?: number;
    search?: string;
}) {
    return useApiQuery({
        path: '/admin/organizations',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

const ADMIN_ORGANIZATIONS_PAGE_SIZE = 200;

type AdminOrganizationsResponse = ApiResponseType<
    '/admin/organizations',
    'get'
>;

export function useAllAdminOrganizations(params?: {
    readonly search?: string;
    readonly autoLoadPages?: boolean;
    readonly orgDataOnly?: boolean;
    // Optional mode override for admin-scoped callers (e.g. the
    // dock) so the SWR key and outgoing X-Mode header follow the
    // admin toggle rather than the customer `modeStore`. Omitting
    // the option preserves the legacy default.
    readonly mode?: Mode;
}) {
    const apiClient = getApiClient();
    const search = params?.search?.trim() ?? '';
    const orgDataOnly = params?.orgDataOnly ?? false;
    const modeOverride = params?.mode;

    const getKey = (
        pageIndex: number,
        previousPageData: AdminOrganizationsResponse | null,
    ) => {
        if (previousPageData && previousPageData.organizations.length === 0) {
            return null;
        }

        if (
            previousPageData &&
            pageIndex * ADMIN_ORGANIZATIONS_PAGE_SIZE >= previousPageData.total
        ) {
            return null;
        }

        return [
            'admin-organizations-all',
            pageIndex + 1,
            search,
            orgDataOnly,
            modeOverride ?? null,
        ] as const;
    };

    const { data, error, size, setSize, isValidating, mutate } = useSWRInfinite<
        AdminOrganizationsResponse,
        ApiError
    >(
        getKey,
        async ([_key, page, searchTerm, orgDataOnlyParam, requestMode]: [
            string,
            number,
            string,
            boolean,
            Mode | null,
        ]) => {
            return apiClient.get({
                url: '/admin/organizations',
                queryParams: {
                    page,
                    per_page: ADMIN_ORGANIZATIONS_PAGE_SIZE,
                    ...(searchTerm ? { search: searchTerm } : {}),
                    ...(orgDataOnlyParam
                        ? { org_data_only: orgDataOnlyParam }
                        : {}),
                },
                mode: requestMode ?? undefined,
            });
        },
        {
            revalidateFirstPage: false,
            shouldRetryOnError: false,
            persistSize: true,
        },
    );

    const organizations = useMemo(
        () => (data ? data.flatMap((pageData) => pageData.organizations) : []),
        [data],
    );
    const total = data?.[0]?.total ?? 0;
    const isLoadingInitialData = !data && !error;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const isReachingEnd = !!data && organizations.length >= total;

    useEffect(() => {
        if (!params?.autoLoadPages) {
            return;
        }

        if (!data || data.length === 0) {
            return;
        }

        if (isReachingEnd) {
            return;
        }

        if (data.length < size) {
            return;
        }

        if (isValidating || isLoadingMore) {
            return;
        }

        setSize((currentSize) => currentSize + 1);
    }, [
        data,
        isLoadingMore,
        isReachingEnd,
        isValidating,
        params?.autoLoadPages,
        setSize,
        size,
    ]);

    return {
        organizations,
        total,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        isValidating,
        isReachingEnd,
        size,
        setSize,
        mutate,
    };
}

export function useAdminOrganization(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrganizationClientTags(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/client-tags',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizationClientTags(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/client-tags',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizationCallerTags(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caller-tags',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export type AdminOrganizationOnboardingReview = ApiResponseType<
    '/admin/organizations/{organization_id}/onboarding-review',
    'get'
>;

export function useAdminOrganizationOnboardingReview(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/onboarding-review',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export type AdminOrganizationSubAccount = ApiResponseType<
    '/admin/organizations/{organization_id}/sub-account',
    'get'
>;

type AdminOrganizationSubAccountUpdate = ApiBodyType<
    '/admin/organizations/{organization_id}/sub-account',
    'patch'
>;

export function useAdminOrganizationSubAccount(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/sub-account',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export async function updateAdminOrganizationSubAccount(
    organizationId: string,
    body: AdminOrganizationSubAccountUpdate,
) {
    return getApiClient().patch({
        url: '/admin/organizations/{organization_id}/sub-account',
        pathParams: {
            organization_id: organizationId,
        },
        body,
    });
}

export type AdminSchedulerAssessment = ApiResponseType<
    '/admin/organizations/{organization_id}/scheduler-assessments',
    'get'
>['assessments'][number];

export function useAdminOrganizationSchedulerAssessments(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/scheduler-assessments',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export async function reviewAdminSchedulerAssessment(
    organizationId: string,
    assessmentId: string,
    action: 'mark_scheduler' | 'mark_not_scheduler',
) {
    return getApiClient().post({
        url: '/admin/organizations/{organization_id}/scheduler-assessments/{assessment_id}/review',
        pathParams: {
            organization_id: organizationId,
            assessment_id: assessmentId,
        },
        body: { action },
    });
}

export function useSchedulerSelfIdentification(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/scheduler-self-identification',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export async function submitSchedulerSelfIdentification(
    organizationId: string,
    isScheduler: boolean,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/scheduler-self-identification',
        pathParams: { organization_id: organizationId },
        body: { is_scheduler: isScheduler },
    });
}

export type AdminOrganizationChecklist = ApiResponseType<
    '/admin/organizations/{organization_id}/checklists/{checklist_id}',
    'get'
>;
export type AdminOrganizationChecklists = ApiResponseType<
    '/admin/organizations/{organization_id}/checklists',
    'get'
>;
export type OrganizationChecklist = ApiResponseType<
    '/organizations/{organization_id}/checklists/{checklist_id}',
    'get'
>;
export type OrganizationAboutYou = ApiResponseType<
    '/organizations/{organization_id}/checklists/{checklist_id}/about-you',
    'get'
>;
export type OrganizationPhoneSmsSetup = ApiResponseType<
    '/organizations/{organization_id}/checklists/{checklist_id}/phone-sms-setup',
    'get'
>;
export type OrganizationKickoffBooking = ApiResponseType<
    '/organizations/{organization_id}/kickoff-booking',
    'get'
>;

type AdminOrganizationChecklistTaskUpdate = ApiBodyType<
    '/admin/organizations/{organization_id}/checklists/{checklist_id}/tasks/{task_id}',
    'patch'
>;

export function useAdminOrganizationChecklists(organizationId: string | null) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/organizations/{organization_id}/checklists',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { mode: adminMode },
        mode: adminMode,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrganizationChecklist(
    organizationId: string | null,
    checklistId: string | null,
) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/organizations/{organization_id}/checklists/{checklist_id}',
        method: 'get',
        pathParams:
            organizationId && checklistId
                ? {
                      organization_id: organizationId,
                      checklist_id: checklistId,
                  }
                : undefined,
        queryParams: { mode: adminMode },
        mode: adminMode,
        enabled: !!organizationId && !!checklistId,
        select: (response) => response,
    });
}

export function useAdminOrganizationRecentRuns(
    organizationId: string | null,
    params?: {
        readonly flaggedOnly?: boolean;
        readonly runType?: AdminRecentRunItem['run_type'];
    },
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/recent-runs',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            params?.flaggedOnly || params?.runType
                ? {
                      ...(params.flaggedOnly && { flagged_only: true }),
                      ...(params.runType && { run_type: params.runType }),
                  }
                : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrganizationSettingsAudit(
    organizationId: string | null,
    params?: {
        readonly tableName?: string;
        readonly limit?: number;
    },
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/settings-audit',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(params?.tableName && { table_name: params.tableName }),
            ...(params?.limit && { limit: params.limit }),
        },
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminCoordinatorPresence(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/coordinator-presence',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        refreshInterval: 5_000,
        select: (response) => response,
    });
}

export function useAdminOrganizationTrainingStats(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/training/stats',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export async function updateAdminOrganizationChecklistTask(
    organizationId: string,
    checklistId: string,
    taskId: string,
    body: AdminOrganizationChecklistTaskUpdate,
    mode: Mode,
) {
    return getApiClient().patch({
        url: '/admin/organizations/{organization_id}/checklists/{checklist_id}/tasks/{task_id}',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
            task_id: taskId,
        },
        queryParams: { mode },
        mode,
        body,
    });
}

type OrganizationChecklistTaskUpdate = ApiBodyType<
    '/organizations/{organization_id}/checklists/{checklist_id}/tasks/{task_id}',
    'patch'
>;
type OrganizationAboutYouUpdate = ApiBodyType<
    '/organizations/{organization_id}/checklists/{checklist_id}/about-you',
    'patch'
>;
type OrganizationPhoneSmsSetupUpdate = ApiBodyType<
    '/organizations/{organization_id}/checklists/{checklist_id}/phone-sms-setup',
    'patch'
>;

export function useOrganizationChecklist(
    organizationId: string | null,
    checklistId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/checklists/{checklist_id}',
        method: 'get',
        pathParams:
            organizationId && checklistId
                ? {
                      organization_id: organizationId,
                      checklist_id: checklistId,
                  }
                : undefined,
        enabled: !!organizationId && !!checklistId,
        select: (response) => response,
        reason: 'organization-checklist',
    });
}

export function useOrganizationIntroCall(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/intro-call',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'organization-intro-call',
    });
}

export function useOrganizationKickoffBooking(
    organizationId: string | null,
    options: { readonly refreshInterval?: number } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/kickoff-booking',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        refreshInterval: options.refreshInterval,
        select: (response) => response,
        reason: 'organization-kickoff-booking',
    });
}

export async function createOrganizationKickoffBooking(organizationId: string) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/kickoff-booking',
        pathParams: { organization_id: organizationId },
    });
}

export async function updateOrganizationChecklistTask(
    organizationId: string,
    checklistId: string,
    taskId: string,
    body: OrganizationChecklistTaskUpdate,
) {
    return getApiClient().patch({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/tasks/{task_id}',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
            task_id: taskId,
        },
        body,
    });
}

export type OrganizationPaymentSetupIntent = ApiResponseType<
    '/organizations/{organization_id}/checklists/{checklist_id}/payment/setup-intent',
    'post'
>;
type OrganizationPaymentConfirm = ApiBodyType<
    '/organizations/{organization_id}/checklists/{checklist_id}/payment/confirm',
    'post'
>;
type OrganizationBillingContactEmail = ApiBodyType<
    '/organizations/{organization_id}/checklists/{checklist_id}/payment/billing-contact-email',
    'post'
>;

export async function createOrganizationPaymentSetupIntent(
    organizationId: string,
    checklistId: string,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/payment/setup-intent',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
    });
}

export async function confirmOrganizationPayment(
    organizationId: string,
    checklistId: string,
    body: OrganizationPaymentConfirm,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/payment/confirm',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
        body,
    });
}

export async function setOrganizationBillingContactEmail(
    organizationId: string,
    checklistId: string,
    body: OrganizationBillingContactEmail,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/payment/billing-contact-email',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
        body,
    });
}

export function useOrganizationAboutYou(
    organizationId: string | null,
    checklistId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/checklists/{checklist_id}/about-you',
        method: 'get',
        pathParams:
            organizationId && checklistId
                ? {
                      organization_id: organizationId,
                      checklist_id: checklistId,
                  }
                : undefined,
        enabled: !!organizationId && !!checklistId,
        select: (response) => response,
        reason: 'organization-about-you',
    });
}

export async function updateOrganizationAboutYou(
    organizationId: string,
    checklistId: string,
    body: OrganizationAboutYouUpdate,
) {
    return getApiClient().patch({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/about-you',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
        body,
    });
}

export function useOrganizationPhoneSmsSetup(
    organizationId: string | null,
    checklistId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/checklists/{checklist_id}/phone-sms-setup',
        method: 'get',
        pathParams:
            organizationId && checklistId
                ? {
                      organization_id: organizationId,
                      checklist_id: checklistId,
                  }
                : undefined,
        enabled: !!organizationId && !!checklistId,
        select: (response) => response,
        reason: 'organization-phone-sms-setup',
    });
}

export async function updateOrganizationPhoneSmsSetup(
    organizationId: string,
    checklistId: string,
    body: OrganizationPhoneSmsSetupUpdate,
) {
    return getApiClient().patch({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/phone-sms-setup',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
        body,
    });
}

export type OrganizationConnectEhr = ApiResponseType<
    '/organizations/{organization_id}/checklists/{checklist_id}/connect-ehr',
    'get'
>;
type OrganizationConnectEhrUpdate = ApiBodyType<
    '/organizations/{organization_id}/checklists/{checklist_id}/connect-ehr',
    'patch'
>;
export type OrganizationConnectEhrProvisionAlias = ApiResponseType<
    '/organizations/{organization_id}/checklists/{checklist_id}/connect-ehr/provision-alias',
    'post'
>;
export type OrganizationPostSetupOnboarding = ApiResponseType<
    typeof ORGANIZATION_POST_SETUP_ONBOARDING_PATH,
    'get'
>;
type OrganizationPostSetupOnboardingUpdate = ApiBodyType<
    typeof ORGANIZATION_POST_SETUP_ONBOARDING_PATH,
    'patch'
>;

export function useOrganizationConnectEhr(
    organizationId: string | null,
    checklistId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/checklists/{checklist_id}/connect-ehr',
        method: 'get',
        pathParams:
            organizationId && checklistId
                ? {
                      organization_id: organizationId,
                      checklist_id: checklistId,
                  }
                : undefined,
        enabled: !!organizationId && !!checklistId,
        select: (response) => response,
        reason: 'organization-connect-ehr',
    });
}

export async function updateOrganizationConnectEhr(
    organizationId: string,
    checklistId: string,
    body: OrganizationConnectEhrUpdate,
) {
    return getApiClient().patch({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/connect-ehr',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
        body,
    });
}

export async function provisionOrganizationConnectEhrAlias(
    organizationId: string,
    checklistId: string,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/checklists/{checklist_id}/connect-ehr/provision-alias',
        pathParams: {
            organization_id: organizationId,
            checklist_id: checklistId,
        },
    });
}

export function useOrganizationPostSetupOnboarding(
    organizationId: string | null,
) {
    return useApiQuery({
        path: ORGANIZATION_POST_SETUP_ONBOARDING_PATH,
        method: 'get',
        pathParams: organizationId
            ? {
                  organization_id: organizationId,
              }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'organization-post-setup-onboarding',
    });
}

export async function updateOrganizationPostSetupOnboarding(
    organizationId: string,
    body: OrganizationPostSetupOnboardingUpdate,
) {
    return getApiClient().patch({
        url: ORGANIZATION_POST_SETUP_ONBOARDING_PATH,
        pathParams: {
            organization_id: organizationId,
        },
        body,
    });
}

export type AdminOrganizationEhrCredentials = ApiResponseType<
    '/admin/organizations/{organization_id}/ehr-credentials',
    'get'
>;
type AdminEhrCredentialRevealBody = ApiBodyType<
    '/admin/organizations/{organization_id}/ehr-credentials/reveal',
    'post'
>;

export function useAdminOrganizationEhrCredentials(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/ehr-credentials',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export async function revealAdminOrganizationEhrCredential(
    organizationId: string,
    body: AdminEhrCredentialRevealBody,
) {
    return getApiClient().post({
        url: '/admin/organizations/{organization_id}/ehr-credentials/reveal',
        pathParams: { organization_id: organizationId },
        body,
    });
}

export function useAdminOrganizationTimezones() {
    return useApiQuery({
        path: '/admin/organizations/timezones',
        method: 'get',
        select: (response) => response,
    });
}

export function useAdminFranchises() {
    return useApiQuery({
        path: '/admin/franchises',
        method: 'get',
        select: (response) => response,
    });
}

export function useInternalFranchises() {
    return useApiQuery({
        path: '/admin/internal/franchises',
        method: 'get',
        select: (response) => response,
    });
}

export function useCreateAdminFranchise() {
    const apiClient = getApiClient();

    const createFranchise = useCallback(
        async (data: FranchiseCreate) => {
            return apiClient.post({
                url: '/admin/franchises',
                body: data,
            });
        },
        [apiClient],
    );

    return { createFranchise };
}

export function useUpdateAdminFranchise() {
    const apiClient = getApiClient();

    const updateFranchise = useCallback(
        async (franchiseId: string, data: FranchiseUpdate) => {
            return apiClient.put({
                url: '/admin/franchises/{franchise_id}',
                pathParams: { franchise_id: franchiseId },
                body: data,
            });
        },
        [apiClient],
    );

    return { updateFranchise };
}

export function useDeleteAdminFranchise() {
    const apiClient = getApiClient();

    const deleteFranchise = useCallback(
        async (franchiseId: string) => {
            return apiClient.delete({
                url: '/admin/franchises/{franchise_id}',
                pathParams: { franchise_id: franchiseId },
            });
        },
        [apiClient],
    );

    return { deleteFranchise };
}

type SmsDebugContactsQueryParams = ApiQueryParamsType<
    '/admin/sms-debug/contacts',
    'get'
>;

export function useAdminSmsDebugContacts(
    params?: SmsDebugContactsQueryParams,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/sms-debug/contacts',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
    });
}

type SmsDebugConversationsQueryParams = ApiQueryParamsType<
    '/admin/sms-debug/conversations',
    'get'
>;

export function useAdminSmsDebugConversations(
    params?: SmsDebugConversationsQueryParams,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/sms-debug/conversations',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
    });
}

export function useAdminTwilioA2pRegistration(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/twilio-a2p-registration',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminTwilioA2pRegistrations() {
    return useApiQuery({
        path: '/admin/organizations/twilio-a2p-registrations',
        method: 'get',
        select: (response) => response.organizations,
    });
}

export function useAdminPhoneNumberPortRequests(
    organizationId: string | null,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/phone-number-port-requests',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response) => response,
    });
}

export function useAdminPhoneNumberInventory() {
    return useApiQuery({
        path: '/admin/phone-number-inventory',
        method: 'get',
        select: (response) => response.numbers,
    });
}

export function useAdminA2pComplianceProfile(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/a2p-compliance-profile',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrganizationDefaultSettings() {
    return useApiQuery({
        path: '/admin/organizations/default-settings',
        method: 'get',
        select: (response) => response,
    });
}

export function usePlaygroundCalls(
    organizationId: string | null,
    traceParams: TraceParams,
    mode?: Mode,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-playground/calls',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        mode,
    });
}

export function useAgentConversations(organizationId: string | null) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/agent-conversations',
        'get'
    >;

    const apiClient = getApiClient();

    const key = organizationId ? ['agent-conversations', organizationId] : null;

    const fetchAgentConversations = useCallback(async (): Promise<
        ResponseType['conversations']
    > => {
        if (!organizationId) {
            return [];
        }

        const response = await apiClient.get({
            url: '/organizations/{organization_id}/agent-conversations',
            pathParams: { organization_id: organizationId },
        });

        return response.conversations;
    }, [apiClient, organizationId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType['conversations'],
        ApiError
    >(key, fetchAgentConversations, { revalidateOnFocus: false });

    const loading = !!organizationId && !data && !error;

    return {
        data: data ?? [],
        error,
        isLoading: loading || Boolean(isLoading),
        isValidating,
        mutate,
    };
}

export function useAgentConversation(
    organizationId: string | null,
    agentConversationId: string | null,
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}',
        'get'
    >;

    const apiClient = getApiClient();

    const key =
        organizationId && agentConversationId
            ? ['agent-conversation', organizationId, agentConversationId]
            : null;

    const fetchAgentConversation = useCallback(async (): Promise<
        ResponseType | undefined
    > => {
        if (!organizationId || !agentConversationId) {
            return undefined;
        }

        return apiClient.get({
            url: '/organizations/{organization_id}/agent-conversations/{agent_conversation_id}',
            pathParams: {
                organization_id: organizationId,
                agent_conversation_id: agentConversationId,
            },
        });
    }, [agentConversationId, apiClient, organizationId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType | undefined,
        ApiError
    >(key, fetchAgentConversation, {
        revalidateOnFocus: false,
    });

    const loading =
        !!organizationId && !!agentConversationId && !data && !error;

    return {
        data,
        error,
        isLoading: loading || Boolean(isLoading),
        isValidating,
        mutate,
    };
}

export function useOrganizationTrainingOverview(
    organizationId: string | null,
    reason?: string,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/training/overview',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason,
    });
}

// Polls rather than relying solely on mount/focus/mutate revalidation, so a
// run created by someone else's activity (an inbound call, an SMS reply, an
// automated outreach) shows up while the viewer's tab stays open, not just
// after a hard refresh.
const NEEDS_REVIEW_POLL_INTERVAL_MS = 15_000;

export function useOrganizationTrainingNeedsReview(
    organizationId: string | null,
    reason?: string,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/training/needs-review',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason,
        refreshInterval: NEEDS_REVIEW_POLL_INTERVAL_MS,
    });
}

type KeysetPage = { readonly next_cursor?: string | null };

/**
 * Cursor paging for a keyset feed. `buildKey` receives the next page's cursor
 * and returns that page's SWR key, or null while the feed cannot be read.
 * `resetToken` names the scope the pages belong to, so a new scope restarts
 * from the first page instead of showing the previous scope's rows.
 */
export function useKeysetPages<
    Page extends KeysetPage,
    Key extends readonly [unknown, ...Array<unknown>],
>(options: {
    readonly buildKey: (cursor: string | null) => Key | null;
    readonly read: (key: Key) => Promise<Page>;
    readonly isReadable: boolean;
    readonly resetToken: string;
    readonly swr?: SWRInfiniteConfiguration<Page, ApiError>;
}) {
    const { buildKey, read, isReadable, resetToken, swr } = options;

    const { data, error, size, setSize, isValidating, mutate } = useSWRInfinite<
        Page,
        ApiError
    >(
        (pageIndex: number, previousPageData: Page | null) => {
            if (
                pageIndex > 0 &&
                previousPageData &&
                !previousPageData.next_cursor
            ) {
                return null;
            }
            return buildKey(
                pageIndex === 0
                    ? null
                    : (previousPageData?.next_cursor ?? null),
            );
        },
        read,
        swr,
    );

    // Skipped on mount: `setSize` forces a revalidation, which would fetch
    // the first page a second time right after its initial request.
    const previousResetTokenRef = useRef(resetToken);
    useEffect(() => {
        if (previousResetTokenRef.current === resetToken) {
            return;
        }
        previousResetTokenRef.current = resetToken;
        void setSize(1);
    }, [resetToken, setSize]);

    const isLoading = !data && !error && isReadable;
    const isLoadingMore =
        isLoading ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const hasMore = Boolean(data?.[data.length - 1]?.next_cursor);
    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore) {
            setSize((currentSize) => currentSize + 1);
        }
    }, [hasMore, isLoadingMore, setSize]);

    return {
        pages: data,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        isValidating,
        mutate,
    };
}

export function useOrganizationRuns(
    organizationId: string | null,
    automationId: string | null = null,
    options: Readonly<{
        readonly mode?: Mode;
        readonly refreshInterval?: number;
        readonly revalidateOnFocus?: boolean;
        readonly revalidateAll?: boolean;
        readonly limit?: number;
        readonly status?: ReadonlyArray<PhoebeAgentRunStatus>;
    }> = {},
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/runs',
        'get'
    >;

    const apiClient = getApiClient();
    const { session } = useSessionState();
    const currentMode = useMode();
    const mode = options.mode ?? currentMode;
    const userId = session?.identity.id;
    const limit = options.limit ?? null;
    const status = options.status?.length ? [...options.status].sort() : null;
    const statusKey = status?.join(',') ?? null;

    const {
        pages,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        isValidating,
        mutate,
    } = useKeysetPages({
        buildKey: (cursor) =>
            organizationId && userId && mode
                ? ([
                      'organization-runs',
                      organizationId,
                      automationId,
                      cursor,
                      userId,
                      mode,
                      statusKey,
                      limit,
                  ] as const)
                : null,
        read: ([
            ,
            organizationIdForPage,
            automationIdForPage,
            cursor,
            ,
            modeForPage,
            statusForPage,
            limitForPage,
        ]) =>
            apiClient.get({
                url: '/organizations/{organization_id}/runs',
                pathParams: {
                    organization_id: organizationIdForPage,
                },
                mode: modeForPage,
                queryParams: {
                    ...(automationIdForPage
                        ? { automation_id: automationIdForPage }
                        : {}),
                    ...(statusForPage
                        ? {
                              status: statusForPage.split(
                                  ',',
                              ) as Array<PhoebeAgentRunStatus>,
                          }
                        : {}),
                    ...(limitForPage === null ? {} : { limit: limitForPage }),
                    ...(cursor ? { cursor } : {}),
                },
            }),
        isReadable: !!organizationId,
        resetToken: [
            organizationId,
            automationId,
            mode,
            statusKey,
            userId,
        ].join('|'),
        swr: {
            revalidateOnFocus: options.revalidateOnFocus ?? false,
            revalidateAll: options.revalidateAll ?? false,
            refreshInterval: options.refreshInterval ?? 0,
        },
    });

    const runs = useMemo(() => {
        // A chat can move across a cursor boundary while another page
        // loads. Prefer its first (newest-page) summary and show it once.
        const uniqueRuns = new Map<string, ResponseType['runs'][number]>();
        for (const page of pages ?? []) {
            for (const run of page.runs) {
                if (!uniqueRuns.has(run.id)) {
                    uniqueRuns.set(run.id, run);
                }
            }
        }
        return [...uniqueRuns.values()].sort(
            (left, right) =>
                Date.parse(right.last_activity_at) -
                    Date.parse(left.last_activity_at) ||
                Date.parse(right.created_at) - Date.parse(left.created_at) ||
                right.id.localeCompare(left.id),
        );
    }, [pages]);

    return {
        data: runs,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        isValidating,
        mutate,
    };
}

const COORDINATOR_AUTOMATIONS_PATH =
    '/organizations/{organization_id}/automations/scheduled' as const;
const COORDINATOR_AUTOMATION_PATH =
    '/organizations/{organization_id}/automations/scheduled/{automation_id}' as const;
const COORDINATOR_AUTOMATION_SKIPS_PATH =
    '/organizations/{organization_id}/automations/scheduled/{automation_id}/skips' as const;

export function useCoordinatorAutomations(
    organizationId: string | null,
    traceParams: TraceParams,
    options: { readonly refreshInterval?: number } = {},
) {
    return useApiQuery({
        path: COORDINATOR_AUTOMATIONS_PATH,
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        refreshInterval: options.refreshInterval,
    });
}

export function useCoordinatorAutomation(
    organizationId: string | null,
    automationId: string | null,
    traceParams: TraceParams,
    options: { readonly refreshInterval?: number } = {},
) {
    return useApiQuery({
        path: COORDINATOR_AUTOMATION_PATH,
        method: 'get',
        pathParams:
            organizationId && automationId
                ? {
                      organization_id: organizationId,
                      automation_id: automationId,
                  }
                : undefined,
        enabled: !!organizationId && !!automationId,
        select: (response) => response,
        reason: traceParams.reason,
        refreshInterval: options.refreshInterval,
    });
}

/** Refreshes the automations list only where a page already holds it. */
export function useRevalidateCoordinatorAutomations(): () => Promise<void> {
    const { mutate: mutateCache } = useSWRConfig();
    return useCallback(async () => {
        await mutateCache(
            (key) =>
                Array.isArray(key) &&
                key[0] === 'get' &&
                key[1] === COORDINATOR_AUTOMATIONS_PATH,
        );
    }, [mutateCache]);
}

export function useCoordinatorAutomationSkips(
    organizationId: string | null,
    automationId: string | null,
    options: Readonly<{
        readonly enabled?: boolean;
        readonly limit?: number;
    }> = {},
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const limit = options.limit ?? null;
    const enabled = options.enabled ?? true;

    const {
        pages,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    } = useKeysetPages({
        buildKey: (cursor) =>
            enabled && organizationId && automationId && mode
                ? ([
                      'coordinator-automation-skips',
                      organizationId,
                      automationId,
                      cursor,
                      mode,
                      limit,
                  ] as const)
                : null,
        read: ([
            ,
            organizationIdForPage,
            automationIdForPage,
            cursor,
            modeForPage,
            limitForPage,
        ]) =>
            apiClient.get({
                url: COORDINATOR_AUTOMATION_SKIPS_PATH,
                pathParams: {
                    organization_id: organizationIdForPage,
                    automation_id: automationIdForPage,
                },
                mode: modeForPage,
                queryParams: {
                    ...(limitForPage === null ? {} : { limit: limitForPage }),
                    ...(cursor ? { cursor } : {}),
                },
            }),
        isReadable: enabled && !!automationId,
        resetToken: [organizationId, automationId, mode].join('|'),
        swr: { revalidateOnFocus: false, revalidateAll: false },
    });

    const skips = useMemo(
        () => (pages ?? []).flatMap((page) => page.skips),
        [pages],
    );

    return {
        data: skips,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    };
}

export function useCoordinatorAutomationActions() {
    const apiClient = getApiClient();

    const createAutomation = useCallback(
        async (
            organizationId: string,
            body: ApiBodyType<typeof COORDINATOR_AUTOMATIONS_PATH, 'post'>,
        ) => {
            return apiClient.post({
                url: COORDINATOR_AUTOMATIONS_PATH,
                pathParams: { organization_id: organizationId },
                body,
            });
        },
        [apiClient],
    );
    const updateAutomation = useCallback(
        async (
            organizationId: string,
            automationId: string,
            body: ApiBodyType<typeof COORDINATOR_AUTOMATION_PATH, 'patch'>,
        ) => {
            return apiClient.patch({
                url: COORDINATOR_AUTOMATION_PATH,
                pathParams: {
                    organization_id: organizationId,
                    automation_id: automationId,
                },
                body,
            });
        },
        [apiClient],
    );
    const deleteAutomation = useCallback(
        async (organizationId: string, automationId: string) => {
            return apiClient.delete({
                url: COORDINATOR_AUTOMATION_PATH,
                pathParams: {
                    organization_id: organizationId,
                    automation_id: automationId,
                },
            });
        },
        [apiClient],
    );

    return { createAutomation, updateAutomation, deleteAutomation };
}

export function useOrganizationRun(
    organizationId: string | null,
    runId: string | null,
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/runs/{run_id}',
        'get'
    >;

    const apiClient = getApiClient();
    const mode = useMode();

    const key =
        organizationId && runId && mode
            ? ['organization-run', organizationId, runId, mode]
            : null;

    const fetchOrganizationRun = useCallback(async (): Promise<
        ResponseType | undefined
    > => {
        if (!organizationId || !runId || !mode) {
            return undefined;
        }

        return apiClient.get({
            url: '/organizations/{organization_id}/runs/{run_id}',
            pathParams: {
                organization_id: organizationId,
                run_id: runId,
            },
            mode,
        });
    }, [apiClient, mode, organizationId, runId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType | undefined,
        ApiError
    >(key, fetchOrganizationRun, {
        revalidateOnFocus: false,
    });

    const loading = !!organizationId && !!runId && !!mode && !data && !error;

    return {
        data,
        error,
        isLoading: loading || Boolean(isLoading),
        isValidating,
        mutate,
    };
}

/** Every rating the current user has left on messages in a run. */
export function useOrganizationRunMessageFeedback(
    organizationId: string | null,
    runId: string | null,
    enabled: boolean,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/runs/{run_id}/message-feedback',
        method: 'get',
        pathParams:
            organizationId && runId
                ? { organization_id: organizationId, run_id: runId }
                : undefined,
        enabled: enabled && !!organizationId && !!runId,
        select: (response) => response.feedback,
        reason: 'agent-message-feedback',
    });
}

/** The current user's run-level feedback (null until they review the run). */
export function useOrganizationRunFeedback(
    organizationId: string | null,
    runId: string | null,
    enabled: boolean,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/runs/{run_id}/feedback',
        method: 'get',
        pathParams:
            organizationId && runId
                ? { organization_id: organizationId, run_id: runId }
                : undefined,
        enabled: enabled && !!organizationId && !!runId,
        select: (response) => response,
        reason: 'agent-run-feedback',
    });
}

// Poll while the debrief is empty: generation is async, so the card may
// mount before its judgment calls exist.
const RUN_DEBRIEF_POLL_INTERVAL_MS = 15_000;

/** Generated judgment calls for a run's end-of-run debrief card. */
export function useOrganizationRunDebrief(
    organizationId: string | null,
    runId: string | null,
    enabled: boolean,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/runs/{run_id}/debrief',
        method: 'get',
        pathParams:
            organizationId && runId
                ? { organization_id: organizationId, run_id: runId }
                : undefined,
        enabled: enabled && !!organizationId && !!runId,
        select: (response) => response,
        reason: 'agent-run-debrief',
        refreshInterval: (latestData) =>
            latestData?.decisions.length ? 0 : RUN_DEBRIEF_POLL_INTERVAL_MS,
    });
}

export function useAgentRunFiles(
    organizationId: string | null,
    runId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/runs/{run_id}/files',
        method: 'get',
        pathParams:
            organizationId && runId
                ? { organization_id: organizationId, run_id: runId }
                : undefined,
        enabled: !!organizationId && !!runId,
        select: (response) => response.files,
        reason: 'agent-run-files',
    });
}

export function usePhoebeAgentRunState(
    organizationId: string | null,
    phoebeAgentRunId: string | null,
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}',
        'get'
    >;

    const apiClient = getApiClient();

    const key =
        organizationId && phoebeAgentRunId
            ? ['phoebe-agent-run-state', organizationId, phoebeAgentRunId]
            : null;

    const fetchPhoebeAgentRunState = useCallback(async (): Promise<
        ResponseType | null | undefined
    > => {
        if (!organizationId || !phoebeAgentRunId) {
            return undefined;
        }

        try {
            return await apiClient.get({
                url: '/organizations/{organization_id}/phoebe_agent_runs/{phoebe_agent_run_id}',
                pathParams: {
                    organization_id: organizationId,
                    phoebe_agent_run_id: phoebeAgentRunId,
                },
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) {
                return null;
            }
            throw error;
        }
    }, [apiClient, organizationId, phoebeAgentRunId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType | null | undefined,
        ApiError
    >(key, fetchPhoebeAgentRunState, {
        revalidateOnFocus: false,
    });

    const loading =
        !!organizationId && !!phoebeAgentRunId && data === undefined && !error;

    return {
        data: data ?? undefined,
        error,
        isLoading: loading || Boolean(isLoading),
        isValidating,
        mutate,
    };
}

export function useOutreachAnalysisRunByOutreach(
    organizationId: string | null,
    outreachId: string | null,
    options?: {
        enabled?: boolean;
        refreshInterval?: number;
    },
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/runs/by-resource',
        'get'
    >;

    const apiClient = getApiClient();
    const mode = useMode();
    const enabled =
        (options?.enabled ?? true) && !!organizationId && !!outreachId;
    // Runs are mode-scoped server-side, and this hook never revalidates on
    // its own (no focus/interval revalidation), so without mode in the key
    // a sandbox/live flip would keep serving the cached cross-mode run and
    // pin the callout chat to a stream that can only 404.
    const key = enabled
        ? [
              'outreach-analysis-run-by-outreach',
              organizationId,
              outreachId,
              mode,
          ]
        : null;

    const fetchOutreachAnalysisRunByOutreach = useCallback(async (): Promise<
        ResponseType | undefined
    > => {
        if (!organizationId || !outreachId) {
            return undefined;
        }

        try {
            return await apiClient.get({
                url: '/organizations/{organization_id}/runs/by-resource',
                pathParams: {
                    organization_id: organizationId,
                },
                queryParams: {
                    resource_type: 'outreach',
                    resource_id: outreachId,
                    role: 'runner',
                },
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) {
                return undefined;
            }
            throw error;
        }
    }, [apiClient, organizationId, outreachId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType | undefined,
        ApiError
    >(key, fetchOutreachAnalysisRunByOutreach, {
        refreshInterval: options?.refreshInterval,
        // A 404 is cached as `undefined` data (no run yet). Focus
        // revalidation keeps that from becoming a permanent verdict when
        // the run appears (or a transient 404 poisoned the cache).
        revalidateOnFocus: true,
    });

    return {
        data,
        error,
        isLoading: Boolean(isLoading),
        isValidating,
        mutate,
    };
}

export function useOutreachRunnerRunByOutreach(
    organizationId: string | null,
    outreachId: string | null,
    options?: {
        enabled?: boolean;
        refreshInterval?: SWRConfiguration<
            | ApiResponseType<
                  '/organizations/{organization_id}/runs/by-resource',
                  'get'
              >
            | undefined
        >['refreshInterval'];
    },
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/runs/by-resource',
        'get'
    >;

    const apiClient = getApiClient();
    const mode = useMode();
    const enabled =
        (options?.enabled ?? true) && !!organizationId && !!outreachId;
    // Mode belongs in the key for the same reason as the analysis hook: runs
    // are mode-scoped server-side and this hook never revalidates on its own.
    const key = enabled
        ? ['outreach-runner-run-by-outreach', organizationId, outreachId, mode]
        : null;

    const fetchOutreachRunnerRunByOutreach = useCallback(async (): Promise<
        ResponseType | undefined
    > => {
        if (!organizationId || !outreachId) {
            return undefined;
        }

        try {
            return await apiClient.get({
                url: '/organizations/{organization_id}/runs/by-resource',
                pathParams: {
                    organization_id: organizationId,
                },
                queryParams: {
                    resource_type: 'outreach',
                    resource_id: outreachId,
                    role: 'runner',
                },
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) {
                return undefined;
            }
            throw error;
        }
    }, [apiClient, organizationId, outreachId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType | undefined,
        ApiError
    >(key, fetchOutreachRunnerRunByOutreach, {
        refreshInterval: options?.refreshInterval,
        revalidateOnFocus: true,
    });

    return {
        data,
        error,
        isLoading: Boolean(isLoading),
        isValidating,
        mutate,
    };
}

export function usePhoebeAgentRunEvents(
    organizationId: string | null,
    phoebeAgentRunId: string | null,
    isBusy: boolean = false,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/runs/{run_id}/events',
        method: 'get',
        pathParams:
            organizationId && phoebeAgentRunId
                ? {
                      organization_id: organizationId,
                      run_id: phoebeAgentRunId,
                  }
                : undefined,
        enabled: !!organizationId && !!phoebeAgentRunId,
        dedupingInterval: isBusy ? 0 : undefined,
        refreshInterval: isBusy ? 1000 : undefined,
        select: (response) => response.events,
    });
}

export function useOutreachAnalysisConversationByOutreach(
    organizationId: string | null,
    outreachId: string | null,
    options?: {
        enabled?: boolean;
        refreshInterval?: number;
    },
) {
    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/agent-conversations/by-outreach/{outreach_id}',
        'get'
    >;

    const apiClient = getApiClient();
    const enabled =
        (options?.enabled ?? true) && !!organizationId && !!outreachId;
    const key = enabled
        ? ['agent-conversation-by-outreach', organizationId, outreachId]
        : null;

    const fetchOutreachConversation = useCallback(async (): Promise<
        ResponseType | undefined
    > => {
        if (!organizationId || !outreachId) {
            return undefined;
        }

        try {
            return await apiClient.get({
                url: '/organizations/{organization_id}/agent-conversations/by-outreach/{outreach_id}',
                pathParams: {
                    organization_id: organizationId,
                    outreach_id: outreachId,
                },
            });
        } catch (error) {
            if (error instanceof ApiError && error.status === 404) {
                return undefined;
            }
            throw error;
        }
    }, [apiClient, organizationId, outreachId]);

    const { data, error, isLoading, isValidating, mutate } = useSWR<
        ResponseType | undefined,
        ApiError
    >(key, fetchOutreachConversation, {
        refreshInterval: options?.refreshInterval,
        revalidateOnFocus: false,
    });

    return {
        data,
        error,
        isLoading: Boolean(isLoading),
        isValidating,
        mutate,
    };
}

export function useAdminOrganizationShiftConfirmationSettings(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/shift-confirmation-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrganizationCarePlanSettings(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/care-plan-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizationCarePlanSettings(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/care-plan-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export async function updateOrganizationRecommendationDefaults(
    organizationId: string,
    body: RecommendationDefaults,
) {
    const response = await getApiClient().put({
        url: RECOMMENDATION_DEFAULTS_PATH,
        pathParams: { organization_id: organizationId },
        body,
        reason: 'organization-recommendation-defaults-save',
    });

    return normalizeRecommendationDefaults(response as RecommendationDefaults);
}

export function useOrganizationRecommendationDefaults(
    organizationId: string | null,
) {
    return useApiQuery({
        path: RECOMMENDATION_DEFAULTS_PATH,
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: selectOrganizationRecommendationDefaults,
        reason: 'organization-recommendation-defaults',
        revalidateOnFocus: false,
    });
}

const TAG_DEFINITIONS_PATH = '/organizations/{organization_id}/tag-definitions';
const TAG_DEFINITION_PATH =
    '/organizations/{organization_id}/tag-definitions/{entity_type}/{tag_key}';

export function useOrganizationTagDefinitions(organizationId: string | null) {
    return useApiQuery({
        path: TAG_DEFINITIONS_PATH,
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'organization-tag-definitions',
        // A background refetch mid-sentence would swap the row out from
        // under an open editor.
        revalidateOnFocus: false,
    });
}

/**
 * Writes a tag's meaning, its importance, or both. A field left out of
 * `body` keeps whatever the row already holds, and `importance: null`
 * clears the weight — so build the body explicitly rather than spreading
 * one with `undefined` values, which serialize to "unchanged". An empty
 * body is a 422.
 */
export async function updateOrganizationTagDefinition(
    organizationId: string,
    entityType: TagDefinitionEntityType,
    tagKey: string,
    body: TagDefinitionUpdate,
): Promise<TagDefinitionCell> {
    const response = await getApiClient().put({
        url: TAG_DEFINITION_PATH,
        pathParams: {
            organization_id: organizationId,
            entity_type: entityType,
            tag_key: tagKey,
        },
        body,
        reason: 'organization-tag-definition-save',
    });

    return response as TagDefinitionCell;
}

/**
 * Drops the agency's own sentence so the tag falls back to the EHR's text.
 * The row survives, keeping the synced text and when it was synced.
 */
export async function resetOrganizationTagDefinition(
    organizationId: string,
    entityType: TagDefinitionEntityType,
    tagKey: string,
): Promise<TagDefinitionCell> {
    const response = await getApiClient().delete({
        url: TAG_DEFINITION_PATH,
        pathParams: {
            organization_id: organizationId,
            entity_type: entityType,
            tag_key: tagKey,
        },
        reason: 'organization-tag-definition-reset',
    });

    return response as TagDefinitionCell;
}

type UntypedApiGet = (params: {
    readonly url: string;
    readonly pathParams?: Record<string, string>;
    readonly reason?: string;
}) => Promise<unknown>;

async function getOrganizationRecommendationDefaultOptions(
    organizationId: string,
): Promise<RecommendationDefaultOptions | null> {
    const apiClient = getApiClient() as unknown as {
        readonly get: UntypedApiGet;
    };

    try {
        const response = await apiClient.get({
            url: RECOMMENDATION_DEFAULT_OPTIONS_PATH,
            pathParams: { organization_id: organizationId },
            reason: 'organization-recommendation-default-options',
        });

        return normalizeRecommendationDefaultOptionsResponse(response);
    } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
            return null;
        }
        throw error;
    }
}

export function useOrganizationRecommendationDefaultOptions(
    organizationId: string | null,
) {
    return useSWR<RecommendationDefaultOptions | null, ApiError>(
        organizationId
            ? ['organization-recommendation-default-options', organizationId]
            : null,
        async ([, resolvedOrganizationId]: [string, string]) =>
            getOrganizationRecommendationDefaultOptions(resolvedOrganizationId),
        {
            // The server has explicitly asked for a delay on 429, and the
            // client cooldown handles recovery. Retrying is useless and harmful.
            shouldRetryOnError: (error) =>
                !(
                    error instanceof ApiError &&
                    (error.status === 404 || error.status === 429)
                ),
        },
    );
}

export function useOrganizationVoiceSettings(
    organizationId: string | null,
    mode?: Mode,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        mode,
    });
}

export function useOrganizationVoiceLanguageCapabilities(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-language-capabilities',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizationExternalSmsProviderAccounts(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/external-sms-provider-accounts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.external_sms_provider_accounts,
    });
}

export function useOrganizationExternalPhoneForwardings(
    organizationId: string | null,
) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/organizations/{organization_id}/external-phone-forwardings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { mode: adminMode },
        mode: adminMode,
        enabled: !!organizationId,
        select: (response) => response.external_phone_forwardings,
    });
}

export function useOrganizationForAdmin(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useQaVoiceAgentSettings(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/qa-voice-agent-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizationMembers(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/members',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useOrganizationInvitations(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/invitations',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useShifts(
    organizationId: string | null,
    traceParams: TraceParams,
    params?: {
        status?: 'open' | 'assigned' | 'completed' | 'cancelled';
        shift_start_date?: string;
        shift_end_date?: string;
        limit?: number;
        offset?: number;
        order?: 'asc' | 'desc';
        include_caregiver_contacts?: boolean;
        include_active_fill_outreach?: boolean;
        care_coordinator_ids?: Array<string>;
        include_unassigned_care_coordinators?: boolean;
        client_ids?: Array<string>;
    },
    options?: {
        keepPreviousData?: boolean;
        revalidateOnFocus?: boolean;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shifts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: params,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: options?.keepPreviousData ?? true,
        revalidateOnFocus: options?.revalidateOnFocus,
    });
}

const CALENDAR_SHIFTS_PATH = '/organizations/{organization_id}/shifts/calendar';
type CalendarShiftsResponse = ApiResponseType<
    typeof CALENDAR_SHIFTS_PATH,
    'get'
>;
const EMPTY_CALENDAR_SHIFTS: CalendarShiftsResponse['shifts'] = [];
const CALENDAR_FOCUS_REVALIDATE_THROTTLE_MS = 15_000;

/**
 * Loads the complete shift projection for the displayed calendar range.
 *
 * The API returns the range one client page at a time; this walks every
 * page and reports `hasPendingPages` until the last one lands, so callers
 * can paint a complete range instead of one that grows page by page.
 */
export function useCalendarShiftsInRange(
    organizationId: string | null,
    traceParams: TraceParams,
    params: {
        readonly shiftStartDate: string;
        readonly shiftEndDate: string;
        readonly caregiverId?: string;
        readonly clientId?: string;
        readonly overlapsRange?: boolean;
    },
    options?: {
        readonly enabled?: boolean;
        readonly revalidateOnFocus?: boolean;
    },
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const isEnabled = (options?.enabled ?? true) && !!organizationId;
    const resetToken = [
        organizationId ?? 'none',
        mode ?? 'none',
        params.shiftStartDate,
        params.shiftEndDate,
        params.caregiverId ?? '',
        params.clientId ?? '',
        params.overlapsRange ?? '',
    ].join(':');

    const {
        pages,
        error,
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
    } = useKeysetPages({
        buildKey: (cursor) =>
            isEnabled && organizationId
                ? ([
                      'calendar-shifts',
                      organizationId,
                      mode ?? null,
                      params.shiftStartDate,
                      params.shiftEndDate,
                      params.caregiverId ?? null,
                      params.clientId ?? null,
                      params.overlapsRange ?? null,
                      cursor,
                      traceParams.reason,
                  ] as const)
                : null,
        read: ([
            ,
            orgId,
            requestMode,
            shiftStartDate,
            shiftEndDate,
            caregiverId,
            clientId,
            overlapsRange,
            clientCursor,
            reason,
        ]) =>
            apiClient.get({
                url: CALENDAR_SHIFTS_PATH,
                pathParams: { organization_id: orgId },
                queryParams: {
                    shift_start_date: shiftStartDate,
                    shift_end_date: shiftEndDate,
                    ...(caregiverId ? { caregiver_id: caregiverId } : {}),
                    ...(clientId ? { client_id: clientId } : {}),
                    ...(overlapsRange !== null
                        ? { overlaps_range: overlapsRange }
                        : {}),
                    ...(clientCursor ? { client_cursor: clientCursor } : {}),
                },
                mode: requestMode ?? undefined,
                reason,
            }),
        isReadable: isEnabled,
        resetToken,
        swr: {
            // Each size bump must fetch only the new page: SWR's own
            // first-page and focus revalidation would refetch O(pages)
            // per bump. `mutate()` still refetches every page, which is
            // what focus handling below and the realtime wrapper rely on.
            revalidateFirstPage: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            shouldRetryOnError: false,
        },
    });

    // The calendar wants the whole range, so keep asking for the next page
    // until the API reports there is none.
    const hasPendingPages = isEnabled && !error && (isLoadingMore || hasMore);
    useEffect(() => {
        if (error === undefined) {
            loadMore();
        }
    }, [error, loadMore]);

    const shifts = useMemo(
        () =>
            pages === undefined || pages.length === 0
                ? EMPTY_CALENDAR_SHIFTS
                : pages.flatMap((page) => page.shifts),
        [pages],
    );

    const shouldRevalidateOnFocus = options?.revalidateOnFocus ?? false;
    const hasPendingPagesRef = useRef(hasPendingPages);
    hasPendingPagesRef.current = hasPendingPages;
    const lastFocusRevalidateAtRef = useRef(0);
    useEffect(() => {
        if (!shouldRevalidateOnFocus || !isEnabled) {
            return;
        }
        function revalidateAllPages(): void {
            // A focus mid-load would re-request every loaded page and
            // compete with the pages still being fetched.
            if (
                document.visibilityState !== 'visible' ||
                hasPendingPagesRef.current
            ) {
                return;
            }
            const now = Date.now();
            if (
                now - lastFocusRevalidateAtRef.current <
                CALENDAR_FOCUS_REVALIDATE_THROTTLE_MS
            ) {
                return;
            }
            lastFocusRevalidateAtRef.current = now;
            void mutate();
        }
        window.addEventListener('focus', revalidateAllPages);
        document.addEventListener('visibilitychange', revalidateAllPages);
        return () => {
            window.removeEventListener('focus', revalidateAllPages);
            document.removeEventListener(
                'visibilitychange',
                revalidateAllPages,
            );
        };
    }, [isEnabled, mutate, shouldRevalidateOnFocus]);

    return {
        data: pages,
        shifts,
        error,
        isLoading,
        hasPendingPages,
        mutate,
    };
}

type ShiftSearchQueryParams = ApiQueryParamsType<
    '/organizations/{organization_id}/shifts/search',
    'get'
>;

type ShiftSearchOptionalParams = Omit<ShiftSearchQueryParams, 'search'>;

export function useShiftSearch(
    organizationId: string | null,
    search: string | null,
    traceParams: TraceParams,
    params?: ShiftSearchOptionalParams,
    options?: {
        revalidateOnFocus?: boolean;
        enabled?: boolean;
    },
) {
    const trimmedSearch = search?.trim() ?? '';
    const hasValidSearch = trimmedSearch.length >= 2;

    // Only include search in query params if it's valid
    const queryParams: ShiftSearchQueryParams = {
        ...(hasValidSearch && { search: trimmedSearch }),
        ...params,
    };

    // Allow caller to override enabled state (for filter-only queries)
    const isEnabled =
        options?.enabled !== undefined
            ? options.enabled
            : !!organizationId && hasValidSearch;

    return useApiQuery({
        path: '/organizations/{organization_id}/shifts/search',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams,
        enabled: isEnabled,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
        revalidateOnFocus: options?.revalidateOnFocus,
    });
}

export function useShift(
    organizationId: string | null,
    shiftId: string | null,
    traceParams: TraceParams,
    params?: ApiQueryParamsType<
        '/organizations/{organization_id}/shifts/{shift_id}',
        'get'
    >,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shifts/{shift_id}',
        method: 'get',
        pathParams:
            organizationId && shiftId
                ? { organization_id: organizationId, shift_id: shiftId }
                : undefined,
        queryParams: params,
        enabled: !!organizationId && !!shiftId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useShiftSourceSystem(
    organizationId: string | null,
    shiftId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shifts/{shift_id}/source-system',
        method: 'get',
        pathParams:
            organizationId && shiftId
                ? { organization_id: organizationId, shift_id: shiftId }
                : undefined,
        enabled: !!organizationId && !!shiftId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useShiftNotes(
    organizationId: string | null,
    shiftIds: ReadonlyArray<string>,
    options: {
        readonly enabled: boolean;
        readonly reason: string;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shifts/notes',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { shift_ids: [...shiftIds] },
        enabled: options.enabled && !!organizationId && shiftIds.length > 0,
        select: (response) => response,
        reason: options.reason,
        revalidateOnFocus: false,
    });
}

export function useOutreaches(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: {
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
        outreachTypes?: Array<string>;
        statuses?: Array<OutreachStatus>;
        statusFilters?: Array<OutreachStatusFilter>;
        patientTags?: Array<string>;
        excludedPatientTags?: Array<string>;
        limit?: number;
        search?: string;
        cursor?: string;
        dateFrom?: string;
        dateTo?: string;
        scheduledOnly?: boolean;
        scheduledLedger?: boolean;
    },
) {
    const queryParams: {
        care_coordinator_ids?: Array<string>;
        include_unassigned_care_coordinators?: boolean;
        outreach_types?: Array<OutreachType>;
        statuses?: Array<OutreachStatus>;
        status_filters?: Array<OutreachStatusFilter>;
        client_tags?: Array<string>;
        excluded_client_tags?: Array<string>;
        limit?: number;
        search?: string;
        cursor?: string;
        date_from?: string;
        date_to?: string;
        scheduled_only?: boolean;
        scheduled_ledger?: boolean;
    } = {};

    // When includeUnassigned is true, always pass care_coordinator_ids (even if empty)
    // to ensure proper filtering. Otherwise only pass if there are actual IDs.
    if (options?.includeUnassignedCareCoordinators) {
        queryParams.care_coordinator_ids = options.careCoordinatorIds ?? [];
        queryParams.include_unassigned_care_coordinators = true;
    } else if (
        options?.careCoordinatorIds &&
        options.careCoordinatorIds.length > 0
    ) {
        queryParams.care_coordinator_ids = options.careCoordinatorIds;
    }

    if (options?.outreachTypes && options.outreachTypes.length > 0) {
        queryParams.outreach_types =
            options.outreachTypes as Array<OutreachType>;
    }

    if (options?.statuses && options.statuses.length > 0) {
        queryParams.statuses = options.statuses;
    }

    if (options?.statusFilters && options.statusFilters.length > 0) {
        queryParams.status_filters = options.statusFilters;
    }

    if (options?.patientTags && options.patientTags.length > 0) {
        queryParams.client_tags = options.patientTags;
    }

    if (
        options?.excludedPatientTags &&
        options.excludedPatientTags.length > 0
    ) {
        queryParams.excluded_client_tags = options.excludedPatientTags;
    }

    if (options?.limit !== undefined) {
        queryParams.limit = options.limit;
    }

    if (options?.search) {
        queryParams.search = options.search;
    }

    if (options?.cursor) {
        queryParams.cursor = options.cursor;
    }

    if (options?.dateFrom) {
        queryParams.date_from = options.dateFrom;
    }

    if (options?.dateTo) {
        queryParams.date_to = options.dateTo;
    }

    if (options?.scheduledOnly) {
        queryParams.scheduled_only = true;
    }

    if (options?.scheduledLedger) {
        queryParams.scheduled_ledger = true;
    }

    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/joined' as const,
        method: 'get' as const,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
        enabled: !!organizationId,
        select: (response: JoinedOutreachListResponse) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
    });
}

// Batched sibling of useOutreaches: fetches several filter buckets in one POST
// so a list can load every section in a single request instead of one per
// section. Each bucket carries its own filters/cursor/search and comes back
// keyed by the caller-supplied key.
export function useOutreachesJoinedBatch(
    organizationId: string | null,
    buckets: Array<JoinedOutreachBucketRequest>,
    traceParams: TraceParams,
    options?: {
        enabled?: boolean;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/joined/batch',
        method: 'post',
        idempotent: true,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        body: { buckets },
        enabled:
            (options?.enabled ?? true) &&
            !!organizationId &&
            buckets.length > 0,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
    });
}

export type ClockReminderReportResponse = ClockReminderReport;

export type OutreachAnalyticsResponse = ApiResponseType<
    '/organizations/{organization_id}/outreaches/analytics',
    'get'
>;

export function useOutreachAnalytics(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: {
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
        outreachTypes?: Array<string>;
        statuses?: Array<OutreachStatus>;
        statusFilters?: Array<OutreachStatusFilter>;
        patientTags?: Array<string>;
        excludedPatientTags?: Array<string>;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
        enabled?: boolean;
    },
) {
    const queryParams: {
        care_coordinator_ids?: Array<string>;
        include_unassigned_care_coordinators?: boolean;
        outreach_types?: Array<OutreachType>;
        statuses?: Array<OutreachStatus>;
        status_filters?: Array<OutreachStatusFilter>;
        client_tags?: Array<string>;
        excluded_client_tags?: Array<string>;
        search?: string;
        date_from?: string;
        date_to?: string;
    } = {};

    if (options?.includeUnassignedCareCoordinators) {
        queryParams.care_coordinator_ids = options.careCoordinatorIds ?? [];
        queryParams.include_unassigned_care_coordinators = true;
    } else if (
        options?.careCoordinatorIds &&
        options.careCoordinatorIds.length > 0
    ) {
        queryParams.care_coordinator_ids = options.careCoordinatorIds;
    }

    if (options?.outreachTypes && options.outreachTypes.length > 0) {
        queryParams.outreach_types =
            options.outreachTypes as Array<OutreachType>;
    }

    if (options?.statuses && options.statuses.length > 0) {
        queryParams.statuses = options.statuses;
    }

    if (options?.statusFilters && options.statusFilters.length > 0) {
        queryParams.status_filters = options.statusFilters;
    }

    if (options?.patientTags && options.patientTags.length > 0) {
        queryParams.client_tags = options.patientTags;
    }

    if (
        options?.excludedPatientTags &&
        options.excludedPatientTags.length > 0
    ) {
        queryParams.excluded_client_tags = options.excludedPatientTags;
    }

    if (options?.search) {
        queryParams.search = options.search;
    }

    if (options?.dateFrom) {
        queryParams.date_from = options.dateFrom;
    }

    if (options?.dateTo) {
        queryParams.date_to = options.dateTo;
    }

    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/analytics' as const,
        method: 'get' as const,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response: OutreachAnalyticsResponse) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
    });
}

export function useClockReminderReport(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: {
        dateFrom?: string;
        dateTo?: string;
        enabled?: boolean;
    },
) {
    const queryParams: {
        date_from?: string;
        date_to?: string;
    } = {};

    if (options?.dateFrom) {
        queryParams.date_from = options.dateFrom;
    }

    if (options?.dateTo) {
        queryParams.date_to = options.dateTo;
    }

    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/clock-reminders/report' as const,
        method: 'get' as const,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response: ClockReminderReportResponse) => response,
        reason: traceParams.reason,
    });
}

export type HomeFeedResponse = ApiResponseType<
    '/organizations/{organization_id}/home/feed',
    'get'
>;
export type HomeFeedItem = HomeFeedResponse['outreaches'][number];
export type VoiceCallsResponse = ApiResponseType<
    '/organizations/{organization_id}/voice-calls',
    'get'
>;
export type VoiceCallListItem = VoiceCallsResponse['items'][number];

type VoiceCallsQueryParams = {
    readonly limit?: number;
    readonly cursor?: string;
    readonly direction?: 'inbound' | 'outbound';
    readonly caregiver_id?: string;
    readonly client_id?: string;
    readonly date_from?: string;
    readonly date_to?: string;
    readonly call_type?: 'client' | 'caregiver' | 'notification';
    readonly transferred_to_back_office?: boolean;
    readonly transfer_destination_id?: string;
    readonly voicemail?: boolean;
    readonly notes_needs_review?: boolean;
    readonly assignment?: 'assigned' | 'unassigned';
    readonly topic?: ActivityTopicFilter;
    readonly search?: string;
    readonly office_id?: Array<string>;
};

export function useHomeFeed(
    organizationId: string | null,
    traceParams: TraceParams & {
        readonly enabled?: boolean;
        readonly mode?: Mode;
        readonly refreshInterval?: number;
        readonly revalidateOnFocus?: boolean;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/home/feed',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (traceParams.enabled ?? true),
        mode: traceParams.mode,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
        refreshInterval: traceParams.refreshInterval ?? 0,
        revalidateOnFocus: traceParams.revalidateOnFocus ?? true,
        withRejectingRevalidation: true,
    });
}

export function useHomeOpenShifts(
    organizationId: string | null,
    traceParams: TraceParams,
    grouping: 'time' | 'client',
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/home/open-shifts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { grouping },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useExtensionInstallStats(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/home/extension-install-stats',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export type ExtensionResolveContextRequest = ApiBodyType<
    '/organizations/{organization_id}/extension/resolve-context',
    'post'
>;
export type ExtensionResolveContextResponse = ApiResponseType<
    '/organizations/{organization_id}/extension/resolve-context',
    'post'
>;

export function useExtensionResolveContext(
    organizationId: string | null,
    request: ExtensionResolveContextRequest | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/extension/resolve-context',
        method: 'post',
        idempotent: true,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        body: request ?? undefined,
        enabled: !!organizationId && !!request,
        select: (response) => response,
        reason: traceParams.reason,
        // A resolved context is identity-bearing data. Reusing the previous
        // EHR entity while a new key loads can attach the prior shift's
        // outreach action (or composer shift id) to the newly selected one.
        keepPreviousData: false,
        // Mounted 24/7 on a background EHR tab via the extension bridge. A real
        // EHR navigation re-fires this on its own (the page context is part of
        // the SWR key), so focus/reconnect revalidations add nothing — and on a
        // server-dead session they drive the resolve-context 401 storm.
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
}

export function useHomeInboundCalls(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: VoiceCallsQueryParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/inbound-calls',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: options,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
    });
}

export function useVoiceCalls(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: VoiceCallsQueryParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-calls',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: options,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: true,
    });
}

type InfiniteVoiceCallsFilters = Omit<
    VoiceCallsQueryParams,
    'limit' | 'cursor'
>;

export const VOICE_CALL_MAX_AUTOMATIC_SPARSE_PAGES = 2;

const isFirstVoiceCallsPageKey = (key: unknown) =>
    Array.isArray(key) && key[4] === null;

/**
 * Cursor-based infinite-scroll fetcher for the voice calls list.
 *
 * Accumulates every loaded page into a single `calls` array and exposes
 * `loadMore` for an intersection-observer trigger. Changing `filters` (a new
 * object reference with different values) starts a fresh paginated sequence.
 */
export function useInfiniteVoiceCalls(
    organizationId: string | null,
    traceParams: TraceParams,
    pageSize: number,
    filters: InfiniteVoiceCallsFilters,
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const filtersKey = JSON.stringify(filters);

    const getKey = (
        pageIndex: number,
        previousPageData: VoiceCallsResponse | null,
    ) => {
        if (!organizationId) {
            return null;
        }
        if (
            pageIndex > 0 &&
            previousPageData &&
            !previousPageData.next_cursor
        ) {
            return null;
        }
        const cursor =
            pageIndex === 0 ? null : (previousPageData?.next_cursor ?? null);
        return [
            'infinite-voice-calls',
            organizationId,
            pageSize,
            filtersKey,
            cursor,
            mode ?? null,
            traceParams.reason,
        ] as const;
    };

    // A page slot is empty whenever SWR has no cached data for its key, so the
    // page type has to admit `undefined`.
    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        VoiceCallsResponse | undefined,
        ApiError
    >(
        getKey,
        ([, orgId, pageLimit, , cursor]: [
            string,
            string,
            number,
            string,
            string | null,
            Mode | null,
            string,
        ]) => {
            return apiClient.get({
                url: '/organizations/{organization_id}/voice-calls',
                pathParams: { organization_id: orgId },
                queryParams: {
                    limit: pageLimit,
                    ...(cursor ? { cursor } : {}),
                    ...filters,
                },
                // Office filtering can be expensive for large organizations.
                // Repeating a timed-out list query amplifies database load.
                retry: false,
                reason: traceParams.reason,
            });
        },
        {
            // Infinite scroll: only fetch newly requested pages. Revalidating
            // the first page (or every loaded page) on each focus/websocket
            // event would refetch O(pages) on a deep scroll. Callers refresh
            // explicitly via the returned `mutate`.
            revalidateFirstPage: false,
            revalidateAll: false,
        },
    );

    // Reset to the first page whenever the query identity changes (filters,
    // organization, or mode). Without this, useSWRInfinite keeps the prior
    // `size`, leaving a stuck "load more" state when the new query has
    // fewer pages than the previous scroll depth.
    useEffect(() => {
        void setSize(1);
    }, [filtersKey, organizationId, mode, setSize]);

    const calls = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.flatMap((page) => page?.items ?? []);
    }, [data]);

    const isLoadingInitialData = !data && !error && !!organizationId;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const lastPage = data?.[data.length - 1];
    const continuationCursor = lastPage?.next_cursor ?? null;
    const hasRepeatedContinuationCursor =
        continuationCursor !== null &&
        !!data
            ?.slice(0, -1)
            .some((page) => page?.next_cursor === continuationCursor);
    const hasMore =
        continuationCursor !== null && !hasRepeatedContinuationCursor;
    const canSearchOlderCalls =
        hasMore &&
        calls.length < pageSize &&
        size >= VOICE_CALL_MAX_AUTOMATIC_SPARSE_PAGES;

    // A bounded office scan may return a partial (or empty) page plus a
    // continuation cursor when the selected office is sparse. Follow one
    // cursor automatically; older scans require an explicit user action.
    useEffect(() => {
        if (
            !hasMore ||
            calls.length >= pageSize ||
            size >= VOICE_CALL_MAX_AUTOMATIC_SPARSE_PAGES ||
            isLoadingMore ||
            isValidating
        ) {
            return;
        }
        void setSize((currentSize) => currentSize + 1);
    }, [
        calls.length,
        hasMore,
        isLoadingMore,
        isValidating,
        continuationCursor,
        pageSize,
        setSize,
        size,
    ]);

    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore) {
            setSize((currentSize) => currentSize + 1);
        }
    }, [hasMore, isLoadingMore, setSize]);

    const refreshFirstPage = useCallback(() => {
        if (!data) {
            return Promise.resolve(undefined);
        }
        // A `revalidate` predicate replaces SWR's own "cache is missing"
        // check, so a page with no cached data has to be refetched here or it
        // stays an empty slot in the page array.
        return mutate(data, {
            revalidate: (page, key) => !page || isFirstVoiceCallsPageKey(key),
        });
    }, [data, mutate]);

    const refreshCalls = useCallback(
        (callIds: ReadonlyArray<string>) => {
            if (!data) {
                return Promise.resolve(undefined);
            }
            const callIdSet = new Set(callIds);
            return mutate(data, {
                revalidate: (page, key) =>
                    !page ||
                    isFirstVoiceCallsPageKey(key) ||
                    page.items.some((call) => callIdSet.has(call.id)),
            });
        },
        [data, mutate],
    );

    return {
        calls,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        hasMore,
        canSearchOlderCalls,
        loadMore,
        refreshFirstPage,
        refreshCalls,
        isValidating,
    };
}

export function useExtensionConfig(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/extension/config',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}
export type OutreachType =
    | 'callout'
    | 'recurring_shift'
    | 'clock_in'
    | 'clock_out'
    | 'plan_of_care_duties'
    | 'shift_confirmation'
    | 'survey'
    | 'message'
    | 'shift_discovery';

export function useOutreachesBatch(
    organizationId: string | null,
    shiftIds: Array<string>,
    traceParams: TraceParams,
    options: {
        statuses?: Array<OutreachStatus>;
        outreachTypes: Array<OutreachType>;
        // Keep the previous batch rendered while a changed shift-id list
        // loads, so overlays built from this data grow instead of resetting
        // to empty on every page of a paged shift load.
        keepPreviousData?: boolean;
    },
    mode?: Mode,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/batch',
        method: 'post',
        idempotent: true,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        body: {
            shift_ids: shiftIds,
            statuses: options.statuses,
            outreach_types: options.outreachTypes,
            include_response_counts: false,
            include_display_status: false,
        },
        enabled: !!organizationId && shiftIds.length > 0,
        select: (response) => response?.outreaches || [],
        reason: traceParams.reason,
        keepPreviousData: options.keepPreviousData,
        mode,
    });
}

// Variant of useOutreachesBatch for the shifts-calendar overlay: also asks
// the API for per-outreach response counts (computed for active outreaches
// only) and returns the full batch response instead of just the outreach
// list, so chips can show outreach progress without a per-outreach fetch.
export function useOutreachesBatchWithResponseCounts(
    organizationId: string | null,
    shiftIds: Array<string>,
    traceParams: TraceParams,
    options: {
        outreachTypes: Array<OutreachType>;
        keepPreviousData?: boolean;
    },
    mode?: Mode,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/batch',
        method: 'post',
        idempotent: true,
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        body: {
            shift_ids: shiftIds,
            outreach_types: options.outreachTypes,
            include_response_counts: true,
            include_display_status: false,
        },
        enabled: !!organizationId && shiftIds.length > 0,
        select: (response) => response,
        reason: traceParams.reason,
        keepPreviousData: options.keepPreviousData,
        mode,
    });
}

export function useAxisCareServiceRates(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
    shiftIds?: Array<string>,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/axiscare-service-rate-info',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        queryParams:
            shiftIds && shiftIds.length > 0
                ? { shift_ids: shiftIds }
                : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useHHAExchangeModifyReasons(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/hhaexchange-modify-reasons',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useHHAExchangePayCodes(
    organizationId: string | null,
    outreachId: string | null,
    caregiverId: string | null,
    traceParams: TraceParams,
    shiftIds?: Array<string>,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/hhaexchange-pay-code-info',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        queryParams: caregiverId
            ? {
                  caregiver_id: caregiverId,
                  shift_ids:
                      shiftIds && shiftIds.length > 0 ? shiftIds : undefined,
              }
            : undefined,
        enabled: !!organizationId && !!outreachId && !!caregiverId,
        select: (response) => response,
        reason: traceParams.reason,
        // Each fetch fans out several HHAeXchange SOAP reads and the modal
        // blocks assignment while revalidating, so do not refetch just
        // because the coordinator tabbed away and back.
        revalidateOnFocus: false,
    });
}

export function useHHAExchangeAcceptanceNoteReasons(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
    shiftIds?: Array<string>,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/hhaexchange-acceptance-note-reasons',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        queryParams:
            shiftIds && shiftIds.length > 0
                ? { shift_ids: shiftIds }
                : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// Org-scoped variant for the outreach composer, where no callout exists yet.
export function useOrgHHAExchangeModifyReasons(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/hhaexchange-modify-reasons',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// Org-scoped AxisCare open-shift modification reasons for the outreach composer
// and assign/fill reason selector. Reads the reasons cached on the AxisCare
// data-source account (no live AxisCare call).
export function useOrgAxisCareModifyReasons(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/axiscare-modify-reasons',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useWellSkyClientRateInfo(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
    shiftId?: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/wellsky-client-rate-info',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        queryParams: { shift_id: shiftId ?? undefined },
        enabled: !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useWellSkyCaregiverRateInfo(
    organizationId: string | null,
    outreachId: string | null,
    clientRateId: string | null,
    caregiverId: string | null,
    traceParams: TraceParams,
    shiftId?: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/wellsky-caregiver-rate-info',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? {
                      organization_id: organizationId,
                      callout_id: outreachId,
                  }
                : undefined,
        enabled:
            !!organizationId && !!outreachId && !!clientRateId && !!caregiverId,
        queryParams:
            clientRateId && caregiverId
                ? {
                      client_rate_id: clientRateId,
                      caregiver_id: caregiverId,
                      ...(shiftId ? { shift_id: shiftId } : {}),
                  }
                : undefined,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export type InhouseRecommendationRequestParams = {
    readonly outreach_id?: string;
    readonly search?: string;
    readonly always_include_caregiver_ids?: Array<string>;
    readonly filter?: RecommendationFilterNode | null;
};

export {
    batchUpdateRecommendationCaregiverSelections,
    type RecommendationUnitType,
} from '@apps/web/lib/recommendation_selection_api';

type ScheduledOutreachMutationParams = {
    readonly organizationId: string;
    readonly outreachId: string;
};

type RescheduleScheduledOutreachParams = ScheduledOutreachMutationParams & {
    readonly startAt: string;
    readonly instructions?: string | null;
};

export async function rescheduleScheduledOutreach({
    organizationId,
    outreachId,
    startAt,
    instructions,
}: RescheduleScheduledOutreachParams) {
    return getApiClient().patch({
        url: '/organizations/{organization_id}/callouts/{callout_id}/scheduled-start',
        pathParams: { organization_id: organizationId, callout_id: outreachId },
        body: { start_at: startAt, instructions: instructions ?? null },
    });
}

export async function pauseScheduledOutreach({
    organizationId,
    outreachId,
}: ScheduledOutreachMutationParams) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/callouts/{callout_id}/scheduled-start/pause',
        pathParams: { organization_id: organizationId, callout_id: outreachId },
    });
}

export async function resumeScheduledOutreach({
    organizationId,
    outreachId,
}: ScheduledOutreachMutationParams) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/callouts/{callout_id}/scheduled-start/resume',
        pathParams: { organization_id: organizationId, callout_id: outreachId },
    });
}

export async function deleteScheduledOutreach({
    organizationId,
    outreachId,
}: ScheduledOutreachMutationParams) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/callouts/{callout_id}/scheduled-start/cancel',
        pathParams: { organization_id: organizationId, callout_id: outreachId },
    });
}

export function useDismissedRecommendationCaregivers(
    organizationId: string | null,
    outreachId: string | null | undefined,
    unitType: RecommendationUnitType,
    unitId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-recommendations/dismissed-caregivers',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            outreachId && unitId
                ? {
                      outreach_id: outreachId,
                      unit_type: unitType,
                      unit_id: unitId,
                  }
                : undefined,
        enabled: !!organizationId && !!outreachId && !!unitId,
        select: (response) =>
            response.dismissed_caregivers.map(
                (caregiver) => caregiver.caregiver_id,
            ),
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

// Every unit's dismissed caregivers for an outreach in one request. Lets the
// outreach detail page load all shifts' dismissed lists once instead of one
// request per shift (the per-shift hook above).
export function useOutreachDismissedCaregivers(
    organizationId: string | null,
    outreachId: string | null | undefined,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-recommendations/dismissed-caregivers/by-outreach',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: outreachId ? { outreach_id: outreachId } : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

// Every caregiver selection row on a v3-born outreach, in one request. The
// checkbox state, withdraw-vs-dismiss routing, and per-shift selection
// coverage all come from here; v2-born outreaches never call it.
export function useOutreachCaregiverSelections(
    organizationId: string | null,
    outreachId: string | null | undefined,
    traceParams: TraceParams,
    options?: {
        readonly enabled?: boolean;
        readonly mode?: Mode;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-recommendations/selections/by-outreach',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: outreachId ? { outreach_id: outreachId } : undefined,
        enabled: (options?.enabled ?? true) && !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
        mode: options?.mode,
    });
}

type SingleUnitDismissedCaregiversResponse = ApiResponseType<
    '/organizations/{organization_id}/caregiver-recommendations/dismissed-caregivers',
    'get'
>;
type OutreachDismissedCaregiversResponse = ApiResponseType<
    '/organizations/{organization_id}/caregiver-recommendations/dismissed-caregivers/by-outreach',
    'get'
>;
type DismissedCaregiversQuery = ReturnType<
    typeof useDismissedRecommendationCaregivers
>;

const EMPTY_DISMISSED_CAREGIVER_IDS: Array<string> = [];

/**
 * A single unit's dismissed caregivers, sliced from the one-request
 * by-outreach fetch above. Every recommendation table on the same outreach
 * (each shift on the detail page, each unit in the add-caregivers modal) calls
 * this with the same organization + outreach, so SWR dedups them to one shared
 * request. Exposes the same shape as the per-unit hook and adapts the table's
 * single-unit optimistic updater onto the shared multi-unit cache.
 */
export function useUnitDismissedCaregivers(
    organizationId: string | null,
    outreachId: string | null | undefined,
    unitType: RecommendationUnitType,
    unitId: string | null,
    traceParams: TraceParams,
): DismissedCaregiversQuery {
    const query = useOutreachDismissedCaregivers(
        organizationId,
        outreachId,
        traceParams,
    );
    const { data: response, mutate: mutateShared } = query;

    const data = useMemo(() => {
        if (response === undefined) {
            return undefined;
        }
        const unit = unitId
            ? response.units.find(
                  (candidate) =>
                      candidate.unit_type === unitType &&
                      candidate.unit_id === unitId,
              )
            : undefined;
        return unit
            ? unit.dismissed_caregivers.map(
                  (caregiver) => caregiver.caregiver_id,
              )
            : EMPTY_DISMISSED_CAREGIVER_IDS;
    }, [response, unitType, unitId]);

    // The table's optimistic updater reads/returns the single-unit response
    // shape; translate it onto this outreach's shared multi-unit cache.
    // mutate() with no updater revalidates the whole outreach fetch. Cast at
    // the boundary: the table ignores the return value and only calls
    // mutate(updater, opts) / mutate().
    const mutate = useCallback(
        ((
            updater?: unknown,
            options?: MutatorOptions<OutreachDismissedCaregiversResponse>,
        ) => {
            if (typeof updater !== 'function') {
                return mutateShared();
            }
            const unitUpdater = updater as (
                current: SingleUnitDismissedCaregiversResponse | undefined,
            ) => SingleUnitDismissedCaregiversResponse | undefined;
            return mutateShared((shared) => {
                if (!shared || !unitId) {
                    return shared;
                }
                const existing = shared.units.find(
                    (candidate) =>
                        candidate.unit_type === unitType &&
                        candidate.unit_id === unitId,
                );
                const updated = unitUpdater({
                    outreach_id: shared.outreach_id,
                    unit_type: unitType,
                    unit_id: unitId,
                    dismissed_caregivers: existing?.dismissed_caregivers ?? [],
                });
                if (!updated) {
                    return shared;
                }
                const updatedUnit = {
                    unit_type: unitType,
                    unit_id: unitId,
                    dismissed_caregivers: updated.dismissed_caregivers,
                };
                const units = existing
                    ? shared.units.map((candidate) =>
                          candidate.unit_type === unitType &&
                          candidate.unit_id === unitId
                              ? updatedUnit
                              : candidate,
                      )
                    : [...shared.units, updatedUnit];
                return { ...shared, units };
            }, options);
        }) as DismissedCaregiversQuery['mutate'],
        [mutateShared, unitType, unitId],
    );

    return {
        data,
        error: query.error,
        hasRequestFailed: query.hasRequestFailed,
        isLoading: query.isLoading,
        isValidating: query.isValidating,
        mutate,
    };
}

const ENTITY_FILTER_OPTIONS_SHIFT_PATH =
    '/organizations/{organization_id}/caregiver-recommendations/{shift_id}/filter-options';
const ENTITY_FILTER_OPTIONS_GROUP_PATH =
    '/organizations/{organization_id}/caregiver-recommendations/groups/{shift_group_id}/filter-options';

/**
 * Fetch the facet options + resolved default filters for the current outreach
 * entity (a single shift or a shift group). Drives the filter UI's available
 * values and initial selection.
 */
export function useEntityFilterOptions(
    organizationId: string | null,
    unitType: RecommendationUnitType,
    unitId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
) {
    const isGroup = unitType === 'shift_group';
    const shiftQuery = useApiQuery({
        path: ENTITY_FILTER_OPTIONS_SHIFT_PATH,
        method: 'get',
        pathParams:
            organizationId && unitId && !isGroup
                ? { organization_id: organizationId, shift_id: unitId }
                : undefined,
        enabled: !!organizationId && !!unitId && !isGroup,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
    // Shift groups resolve only through their owning outreach, so the group
    // endpoint requires outreach_id and the query stays disabled without it.
    const groupQuery = useApiQuery({
        path: ENTITY_FILTER_OPTIONS_GROUP_PATH,
        method: 'get',
        pathParams:
            organizationId && unitId && isGroup
                ? { organization_id: organizationId, shift_group_id: unitId }
                : undefined,
        queryParams: outreachId ? { outreach_id: outreachId } : undefined,
        enabled: !!organizationId && !!unitId && isGroup && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
    return isGroup ? groupQuery : shiftQuery;
}

export function useInhouseRecommendations(
    organizationId: string | null,
    shiftId: string | null,
    traceParams: TraceParams,
    queryParams?: InhouseRecommendationRequestParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-recommendations/{shift_id}',
        method: 'post',
        idempotent: true,
        pathParams:
            organizationId && shiftId
                ? { organization_id: organizationId, shift_id: shiftId }
                : undefined,
        body: queryParams ?? {},
        enabled: !!organizationId && !!shiftId,
        keepPreviousData: true,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

export function useGroupInhouseRecommendations(
    organizationId: string | null,
    shiftGroupId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
    queryParams?: InhouseRecommendationRequestParams,
) {
    // Outreach scoping rides along as the `outreach_id` request param so the
    // group endpoint can resolve the outreach's inline shift group.
    const requestParams: InhouseRecommendationRequestParams | undefined =
        outreachId
            ? { ...(queryParams ?? {}), outreach_id: outreachId }
            : queryParams;
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-recommendations/groups/{shift_group_id}',
        method: 'post',
        idempotent: true,
        pathParams:
            organizationId && shiftGroupId
                ? {
                      organization_id: organizationId,
                      shift_group_id: shiftGroupId,
                  }
                : undefined,
        body: requestParams ?? {},
        enabled: !!organizationId && !!shiftGroupId,
        keepPreviousData: true,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

export function useContactAttempts(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
    mode?: Mode,
    options: {
        readonly enabled?: boolean;
        readonly refreshIntervalMs?: number;
    } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/contact-attempts',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        enabled: (options.enabled ?? true) && !!organizationId && !!outreachId,
        select: (response) => response || [],
        reason: traceParams.reason,
        mode,
        refreshInterval: options.refreshIntervalMs,
    });
}

export function useOutreachRelatedInboundCalls(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/related-inbound-calls',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response || [],
        reason: traceParams.reason,
    });
}

export function useClientContactAttempts(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/callouts/{callout_id}/client-contact-attempts',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, callout_id: outreachId }
                : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response || [],
        reason: traceParams.reason,
    });
}

export function useSurveyOutreaches(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/survey-outreaches',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useSurveyOutreach(
    organizationId: string | null,
    surveyOutreachId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/survey-outreaches/{survey_outreach_id}',
        method: 'get',
        pathParams:
            organizationId && surveyOutreachId
                ? {
                      organization_id: organizationId,
                      survey_outreach_id: surveyOutreachId,
                  }
                : undefined,
        enabled: !!organizationId && !!surveyOutreachId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// The caregiver-preference endpoints answer 403 unless the organization has
// caregiver preference profiles enabled, so callers on shared pages pass an
// `enabled` gate instead of letting the page fetch and fail.
export function useRecurringOutreachSchedules(
    organizationId: string | null,
    kind: RecurringOutreachKind,
    traceParams: TraceParams,
    options: { readonly offset?: number; readonly limit?: number } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/recurring-outreach-schedules',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            kind,
            offset: options.offset ?? 0,
            limit: options.limit ?? 10,
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useCaregiverPreferenceSurveys(
    organizationId: string | null,
    traceParams: TraceParams,
    options: { readonly enabled: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-preference-surveys',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && options.enabled,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

const CAREGIVER_PREFERENCE_SURVEY_POLL_INTERVAL_MS = 10_000;

export function useCaregiverPreferenceSurvey(
    organizationId: string | null,
    surveyOutreachId: string | null,
    traceParams: TraceParams,
    options?: {
        readonly enabled?: boolean;
        readonly pollWhileActive?: boolean;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-preference-surveys/{outreach_id}',
        method: 'get',
        pathParams:
            organizationId && surveyOutreachId
                ? {
                      organization_id: organizationId,
                      outreach_id: surveyOutreachId,
                  }
                : undefined,
        enabled:
            !!organizationId &&
            !!surveyOutreachId &&
            (options?.enabled ?? true),
        select: (response) => response,
        reason: traceParams.reason,
        refreshInterval: options?.pollWhileActive
            ? (survey) => {
                  if (!survey) {
                      return CAREGIVER_PREFERENCE_SURVEY_POLL_INTERVAL_MS;
                  }
                  // A session can advance through inbound voice or SMS, so
                  // the detail page cannot rely on its own button actions to
                  // know when to refresh.
                  const activeSessionCount =
                      survey.invited_count +
                      survey.in_progress_count +
                      survey.deferred_count;
                  return activeSessionCount > 0
                      ? CAREGIVER_PREFERENCE_SURVEY_POLL_INTERVAL_MS
                      : 0;
              }
            : undefined,
    });
}

export function useCaregiverPreferenceSurveyTemplates(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-preference-survey-templates',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useCaregiverPreferenceSettings(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-preference-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (options?.enabled ?? true),
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useCaregiverPreferenceCoverage(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-preference-readiness',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useCaregiverSupply(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver-supply',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useMessageOutreaches(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/message-outreaches',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useMessageOutreach(
    organizationId: string | null,
    messageOutreachId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/message-outreaches/{message_outreach_id}',
        method: 'get',
        pathParams:
            organizationId && messageOutreachId
                ? {
                      organization_id: organizationId,
                      message_outreach_id: messageOutreachId,
                  }
                : undefined,
        enabled: !!organizationId && !!messageOutreachId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useClientNotificationHistory(
    organizationId: string | null,
    clientId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}/notification-history',
        method: 'get',
        pathParams:
            organizationId && clientId
                ? {
                      organization_id: organizationId,
                      client_id: clientId,
                  }
                : undefined,
        enabled: !!organizationId && !!clientId,
        select: (response) => response || [],
        reason: traceParams.reason,
    });
}

export function useOrganization(
    organizationId: string | null,
    traceParams: TraceParams,
    mode?: Mode,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        mode,
    });
}

export function useTemplateVariableManifest(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/template-variables',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useAdminTemplateVariableManifest(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/template-variables',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationMetrics(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/metrics',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 60_000,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useHistoricalMetrics(
    organizationId: string | null,
    startDate: string | null,
    endDate: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/metrics/historical',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            organizationId && startDate && endDate
                ? {
                      start_date: startDate,
                      end_date: endDate,
                  }
                : undefined,
        enabled: !!organizationId && !!startDate && !!endDate,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 300_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export type MetricsInsightsWindow = '24h' | '7d' | '30d' | '90d';

// Insights endpoints are org-admin-only; pass organizationId as null for
// non-admin viewers so the request never fires.
export function useSchedulingInsights(
    organizationId: string | null,
    window: MetricsInsightsWindow,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/metrics/insights/scheduling',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { window },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 300_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useCaregiverPerformanceInsights(
    organizationId: string | null,
    window: MetricsInsightsWindow,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/metrics/insights/people/caregivers',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { window },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 300_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useSchedulerActivityInsights(
    organizationId: string | null,
    window: MetricsInsightsWindow,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/metrics/insights/people/schedulers',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { window },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 300_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useSchedulerActivityReport(
    organizationId: string | null,
    schedulerUserId: string | null,
    startAt: string | null,
    endAt: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/metrics/insights/people/schedulers/{scheduler_user_id}/activity',
        method: 'get',
        pathParams:
            organizationId && schedulerUserId
                ? {
                      organization_id: organizationId,
                      scheduler_user_id: schedulerUserId,
                  }
                : undefined,
        queryParams:
            startAt && endAt
                ? {
                      start_at: startAt,
                      end_at: endAt,
                  }
                : undefined,
        enabled: !!organizationId && !!schedulerUserId && !!startAt && !!endAt,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

export type AuthorizationsFilters = {
    readonly locations?: ReadonlyArray<string>;
    readonly states?: ReadonlyArray<string>;
    readonly statuses?: ReadonlyArray<string>;
    readonly serviceLines?: ReadonlyArray<string>;
};

function authorizationsFilterQueryParams(filters: AuthorizationsFilters = {}) {
    return {
        ...(filters.locations && filters.locations.length > 0
            ? { locations: [...filters.locations] }
            : {}),
        ...(filters.states && filters.states.length > 0
            ? { states: [...filters.states] }
            : {}),
        ...(filters.statuses ? { statuses: [...filters.statuses] } : {}),
        ...(filters.serviceLines
            ? { service_lines: [...filters.serviceLines] }
            : {}),
    };
}

export function useAuthorizationsFilterOptions(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/authorizations/filter-options',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        dedupingInterval: 300_000,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAuthorizationsSummary(
    organizationId: string | null,
    startDate: string | null,
    endDate: string | null,
    traceParams: TraceParams,
    filters?: AuthorizationsFilters,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/authorizations/summary',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            organizationId && startDate && endDate
                ? {
                      start_date: startDate,
                      end_date: endDate,
                      ...authorizationsFilterQueryParams(filters),
                  }
                : undefined,
        enabled: !!organizationId && !!startDate && !!endDate,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 60_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAuthorizationsMonthlyRollup(
    organizationId: string | null,
    months: number,
    traceParams: TraceParams,
    filters?: AuthorizationsFilters,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/authorizations/monthly-rollup',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            months,
            ...authorizationsFilterQueryParams(filters),
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 300_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAuthorizationsLeaderboard(
    organizationId: string | null,
    leaderboardView: 'month' | 'days',
    traceParams: TraceParams,
    filters?: AuthorizationsFilters,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/authorizations/leaderboard',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: organizationId
            ? {
                  leaderboard_view: leaderboardView,
                  ...authorizationsFilterQueryParams(filters),
              }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 300_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAuthorizationsAttentionList(
    organizationId: string | null,
    attentionType: 'over_delivered' | 'under_scheduled' | null,
    startDate: string | null,
    endDate: string | null,
    page: number,
    limit: number,
    traceParams: TraceParams,
    filters?: AuthorizationsFilters,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/authorizations/attention-list',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            organizationId && attentionType && startDate && endDate
                ? {
                      type: attentionType,
                      start_date: startDate,
                      end_date: endDate,
                      page,
                      limit,
                      ...authorizationsFilterQueryParams(filters),
                  }
                : undefined,
        enabled:
            !!organizationId && !!attentionType && !!startDate && !!endDate,
        select: (response) => response,
        reason: traceParams.reason,
        dedupingInterval: 60_000,
        keepPreviousData: true,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useOrganizationCallRoutingDestinations(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/call-routing-destinations',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.call_routing_destinations,
    });
}

// Same path/key as useOrganizationCallRoutingDestinations (so no extra
// fetch), but exposes the full response for callers that need coverage_gaps
// and timezone alongside the destination list.
export function useOrganizationCallRoutingCoverage(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/call-routing-destinations',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

// The phone numbers Phoebe answers for an org: her own Phoebe numbers plus any
// external office lines that forward to her. Backed by the same forwarding
// endpoint the Phone numbers settings page uses (no extra fetch when both
// mount). Response exposes `phoebe_phone_numbers`, `forwardings`, and
// `can_manage_number_config`.
export function useAnsweringNumbers(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/phone-system/forwarding',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAgentPlaybookItems(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/agent-rules/playbooks',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useReliabilityPolicy(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/agent-rules/reliability-policy',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export type OutreachFilterEntityType =
    | 'caregiver'
    | 'coordinator'
    | 'client'
    | 'client_tag';

// Pass entityType null to fetch every entity type for the outreach type
// in one request; callers then split rules by entity_type client-side.
export function useOrganizationOutreachFilterRules(
    organizationId: string | null,
    outreachType: OutreachType,
    traceParams: TraceParams,
    entityType: OutreachFilterEntityType | null = 'caregiver',
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreach-filter-rules',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            outreach_type: outreachType,
            ...(entityType !== null ? { entity_type: entityType } : {}),
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useSyncLogs(
    organizationId?: string | null,
    params?: { page?: number; perPage?: number },
) {
    return useApiQuery({
        path: '/admin/sync-logs',
        method: 'get',
        queryParams: organizationId
            ? { ...params, organization_id: organizationId }
            : params,
        select: (response) => response.sync_logs,
    });
}

export function useSyncQueue() {
    return useApiQuery({
        path: '/admin/sync-queue',
        method: 'get',
        select: (response) => response.entries,
        refreshInterval: 5000,
    });
}

export function useAdminCoreOrganizationSyncBackfill(
    organizationId?: string,
    options?: { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/core-sync/backfill',
        method: 'get',
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: options?.enabled ?? true,
        select: (response) => response,
    });
}

type ActivityLogListResponse = ApiResponseType<'/admin/activity-logs', 'get'>;
type ActivityLogQueryParams = ApiQueryParamsType<'/admin/activity-logs', 'get'>;

export function useActivityLogs(params?: {
    organizationId?: string;
    entityType?: 'shift' | 'org_caregiver_record';
    entityId?: string;
    page?: number;
    perPage?: number;
}) {
    // Convert camelCase to snake_case for backend
    const queryParams: ActivityLogQueryParams = params
        ? {
              ...(params.organizationId
                  ? { organization_id: params.organizationId }
                  : {}),
              ...(params.entityType ? { entity_type: params.entityType } : {}),
              ...(params.entityId ? { entity_id: params.entityId } : {}),
              ...(params.page ? { page: params.page } : {}),
              per_page: params.perPage,
          }
        : {};

    return useApiQuery({
        path: '/admin/activity-logs',
        method: 'get',
        queryParams,
        select: (response) => response,
    });
}

export function useActivityLogsInfinite(params?: {
    organizationId?: string;
    entityType?: 'shift' | 'org_caregiver_record';
    entityId?: string;
    perPage?: number;
}) {
    const apiClient = getApiClient();

    // Convert camelCase to snake_case for backend
    const normalizedParams: ActivityLogQueryParams | undefined = params
        ? {
              ...(params.organizationId
                  ? { organization_id: params.organizationId }
                  : {}),
              ...(params.entityType ? { entity_type: params.entityType } : {}),
              ...(params.entityId ? { entity_id: params.entityId } : {}),
              per_page: params.perPage,
          }
        : undefined;

    const getKey = (
        pageIndex: number,
        previousPageData: ActivityLogListResponse | null,
    ) => {
        // If we've reached the end (no more data), stop
        if (previousPageData && previousPageData.activity_logs.length === 0) {
            return null;
        }

        return [
            'admin/activity-logs',
            pageIndex + 1,
            normalizedParams,
        ] as const;
    };

    const { data, error, size, setSize, mutate } = useSWRInfinite<
        ActivityLogListResponse,
        ApiError
    >(
        getKey,
        ([_url, page, queryParams]: [
            string,
            number,
            ActivityLogQueryParams | undefined,
        ]) => {
            const finalQueryParams: ActivityLogQueryParams = {
                page,
                per_page: queryParams?.per_page || 10,
                ...(queryParams?.organization_id && {
                    organization_id: queryParams.organization_id,
                }),
                ...(queryParams?.entity_type && {
                    entity_type: queryParams.entity_type,
                }),
                ...(queryParams?.entity_id && {
                    entity_id: queryParams.entity_id,
                }),
            };

            return apiClient.get({
                url: '/admin/activity-logs',
                queryParams: finalQueryParams,
            });
        },
        {
            revalidateFirstPage: false,
            revalidateAll: false,
        },
    );

    // Computed values
    const activityLogs = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.flatMap((page) => page.activity_logs);
    }, [data]);

    const isLoadingInitialData = !data && !error;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const isEmpty = data?.[0]?.activity_logs.length === 0;
    const lastPage = data?.[data.length - 1];
    const isReachingEnd =
        isEmpty ||
        (data && data.length > 0
            ? (lastPage?.activity_logs?.length || 0) < (params?.perPage || 10)
            : false);
    const total = data?.[0]?.total || 0;

    return {
        activityLogs,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        mutate,
        total,
        hasMore: !isReachingEnd,
    };
}

export function useLatestShiftsSync(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    const query = useDataSourceSyncState(organizationId, traceParams);
    const startedAt = query.data?.shifts.last_completed_started_at ?? null;
    return {
        ...query,
        data: startedAt ? { started_at: startedAt } : null,
    };
}

// Polls only while a sync is in flight so completion-detection sees the
// new SyncLog promptly. Idle callers (e.g. the shift list pane's
// "Last sync at" line) get a single fetch with no background polling —
// the timestamp almost never changes between scheduled worker runs, so
// constant polling per pane instance would be pure waste.
export function useDataSourceSyncState(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { readonly whileSyncing?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/sync-state',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        refreshInterval: options?.whileSyncing ? 5_000 : undefined,
    });
}

export function useShiftConfirmations(
    organizationId: string | null,
    traceParams: TraceParams,
    params?: {
        status?:
            | 'pending'
            | 'confirmed'
            | 'declined'
            | 'no_response'
            | 'cancelled';
        shift_id?: string;
        caregiver_id?: string;
        sent_after?: string;
        sent_before?: string;
        limit?: number;
        offset?: number;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shift-confirmations',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: params,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationCaregivers(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: {
        readonly search?: string;
        readonly per_page?: number;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregivers',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: options,
        enabled: !!organizationId,
        select: (response) => response.caregivers,
        reason: traceParams.reason,
    });
}

export function useOrganizationClientRecipients(
    organizationId: string | null,
    traceParams: TraceParams,
    options: {
        readonly search: string;
        readonly limit?: number;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients/recipient-search',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: options,
        enabled: !!organizationId && options.search.trim() !== '',
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationClientSearch(
    organizationId: string | null,
    traceParams: TraceParams,
    options: {
        readonly search: string;
        readonly limit?: number;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients/search',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: options,
        enabled: !!organizationId,
        select: (response) => response.clients,
        reason: traceParams.reason,
    });
}

const DEFAULT_CAREGIVERS_PAGE_SIZE = 50;
const EMPTY_OFFICE_IDS: ReadonlyArray<string> = [];

export function useInfiniteOrganizationCaregivers(
    organizationId: string | null,
    _traceParams: TraceParams,
    search?: string,
    options?: {
        enabled?: boolean;
        officeIds?: ReadonlyArray<string>;
        ehrCaregiverStatuses?: ReadonlyArray<
            'active' | 'inactive' | 'applicant'
        >;
        includeExtendedRoster?: boolean;
        pageSize?: number;
        excludeSkipListed?: boolean;
        revalidateAll?: boolean;
    },
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const pageSize = options?.pageSize ?? DEFAULT_CAREGIVERS_PAGE_SIZE;
    const enabled = options?.enabled ?? true;
    const officeIds = options?.officeIds ?? EMPTY_OFFICE_IDS;
    const ehrCaregiverStatuses = options?.ehrCaregiverStatuses ?? [];
    const includeExtendedRoster = options?.includeExtendedRoster ?? false;
    const excludeSkipListed = options?.excludeSkipListed ?? false;
    // Default false preserves existing behavior for every other caller.
    // The onboarding tab opts in so EVERY loaded page — not just page 1 —
    // is revalidated on focus/mount. Otherwise a caregiver added to the
    // skip list elsewhere would remain visible (and selectable / sendable)
    // on later pages of the infinite list until a full reload.
    const revalidateAll = options?.revalidateAll ?? false;
    const sortedOfficeIds = useMemo(
        () => [...officeIds].sort((a, b) => a.localeCompare(b)),
        [officeIds],
    );
    const officeIdsCacheKey = sortedOfficeIds.join(',');
    const ehrCaregiverStatusesCacheKey = [...ehrCaregiverStatuses]
        .sort((a, b) => a.localeCompare(b))
        .join(',');
    const caregiverQueryIdentity = useMemo(
        () =>
            JSON.stringify([
                organizationId ?? '',
                search ?? '',
                pageSize,
                mode ?? '',
                officeIdsCacheKey,
                ehrCaregiverStatusesCacheKey,
                includeExtendedRoster,
                excludeSkipListed,
            ]),
        [
            organizationId,
            search,
            pageSize,
            mode,
            officeIdsCacheKey,
            ehrCaregiverStatusesCacheKey,
            includeExtendedRoster,
            excludeSkipListed,
        ],
    );

    type ResponseType = ApiResponseType<
        '/organizations/{organization_id}/caregivers',
        'get'
    >;
    type PageData = ResponseType & { readonly queryIdentity: string };

    const getKey = (
        pageIndex: number,
        previousPageData: ResponseType | null,
    ) => {
        if (!organizationId || !enabled) {
            return null;
        }

        // Reached the end
        if (previousPageData && previousPageData.caregivers.length === 0) {
            return null;
        }

        return [
            'organization-caregivers',
            organizationId,
            pageIndex + 1,
            search || '',
            pageSize,
            mode,
            officeIdsCacheKey,
            ehrCaregiverStatusesCacheKey,
            includeExtendedRoster,
            excludeSkipListed,
            caregiverQueryIdentity,
        ] as const;
    };

    const { data, error, size, setSize, isLoading, isValidating, mutate } =
        useSWRInfinite<PageData, ApiError>(
            getKey,
            async ([
                _key,
                orgId,
                page,
                searchTerm,
                sizePerPage,
                _mode,
                officeIdsParam,
                ehrCaregiverStatusesParam,
                includeExtendedRosterParam,
                excludeSkipListedParam,
                queryIdentity,
            ]: [
                string,
                string,
                number,
                string,
                number,
                string | null,
                string,
                string,
                boolean,
                boolean,
                string,
            ]) => {
                const queryOfficeIds =
                    officeIdsParam.length > 0
                        ? officeIdsParam.split(',')
                        : undefined;
                const queryEhrCaregiverStatuses =
                    ehrCaregiverStatusesParam.length > 0
                        ? (ehrCaregiverStatusesParam.split(',') as Array<
                              'active' | 'inactive' | 'applicant'
                          >)
                        : undefined;
                const response = await apiClient.get({
                    url: '/organizations/{organization_id}/caregivers',
                    pathParams: { organization_id: orgId },
                    queryParams: {
                        page,
                        per_page: sizePerPage,
                        ...(searchTerm ? { search: searchTerm } : {}),
                        ...(queryOfficeIds
                            ? { office_id: queryOfficeIds }
                            : {}),
                        ...(queryEhrCaregiverStatuses
                            ? {
                                  ehr_caregiver_status:
                                      queryEhrCaregiverStatuses,
                              }
                            : {}),
                        ...(includeExtendedRosterParam
                            ? { include_extended_roster: true }
                            : {}),
                        ...(excludeSkipListedParam
                            ? { exclude_skip_listed: true }
                            : {}),
                    },
                });
                return { ...response, queryIdentity };
            },
            {
                revalidateAll,
                revalidateFirstPage: false,
                shouldRetryOnError: false,
            },
        );

    useEffect(() => {
        void setSize(1);
    }, [
        organizationId,
        search,
        pageSize,
        mode,
        officeIdsCacheKey,
        ehrCaregiverStatusesCacheKey,
        includeExtendedRoster,
        excludeSkipListed,
        setSize,
    ]);

    const hasStaleData =
        data !== undefined &&
        // Skip not-yet-fetched slots (undefined while a later page is loading),
        // which would otherwise throw reading queryIdentity off an undefined
        // page during pagination.
        data.some(
            (page) =>
                page !== undefined &&
                page.queryIdentity !== caregiverQueryIdentity,
        );

    const caregivers = data ? data.flatMap((page) => page.caregivers) : [];
    const isLoadingMore =
        isLoading ||
        (size > 0 && data && typeof data[size - 1] === 'undefined');
    const isReachingEnd =
        !data ||
        (data[data.length - 1]?.caregivers.length ?? 0) < pageSize ||
        (data[data.length - 1]?.page ?? 0) * pageSize >=
            (data[data.length - 1]?.total ?? 0);
    const total = data?.[0]?.total;

    return {
        caregivers,
        error,
        isLoading,
        hasStaleData,
        isValidating,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        mutate,
        total,
        pageSize,
    };
}

export function useOrganizationCaregiver(
    organizationId: string | null,
    caregiverId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregivers/{caregiver_id}',
        method: 'get',
        pathParams:
            organizationId && caregiverId
                ? { organization_id: organizationId, caregiver_id: caregiverId }
                : undefined,
        enabled: !!organizationId && !!caregiverId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationCaregiverShiftOutcomes(
    organizationId: string | null,
    caregiverId: string | null,
    startDate: string | null,
    endDate: string | null,
    limit: number,
    offset: number,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregivers/{caregiver_id}/shift-outcomes',
        method: 'get',
        pathParams:
            organizationId && caregiverId
                ? { organization_id: organizationId, caregiver_id: caregiverId }
                : undefined,
        queryParams:
            startDate && endDate
                ? {
                      start_date: startDate,
                      end_date: endDate,
                      limit,
                      offset,
                  }
                : undefined,
        enabled: !!organizationId && !!caregiverId && !!startDate && !!endDate,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

type LeaderboardResponseType = ApiResponseType<
    '/organizations/{organization_id}/caregiver_points/leaderboard',
    'get'
>;
type LeaderboardSortBy = 'caregiver_name' | 'total_points' | 'last_earned_at';
type LeaderboardSortDirection = 'asc' | 'desc';

export function useLeaderboardPages(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: {
        readonly limit?: number;
        readonly search?: string;
        readonly sortBy?: LeaderboardSortBy;
        readonly sortDirection?: LeaderboardSortDirection;
    },
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const limit = options?.limit ?? 200;
    const search = options?.search?.trim() ?? '';
    const sortBy = options?.sortBy ?? 'total_points';
    const sortDirection = options?.sortDirection ?? 'desc';

    const getKey = (
        pageIndex: number,
        previousPageData: LeaderboardResponseType | null,
    ) => {
        if (!organizationId) {
            return null;
        }
        if (
            pageIndex > 0 &&
            previousPageData &&
            !previousPageData.next_cursor
        ) {
            return null;
        }
        const cursor =
            pageIndex === 0 ? null : (previousPageData?.next_cursor ?? null);
        return [
            'organization-caregiver-points-leaderboard',
            organizationId,
            limit,
            search,
            sortBy,
            sortDirection,
            cursor,
            mode ?? null,
            traceParams.reason,
        ] as const;
    };

    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        LeaderboardResponseType,
        ApiError
    >(
        getKey,
        ([
            _key,
            orgId,
            pageLimit,
            pageSearch,
            pageSortBy,
            pageSortDirection,
            cursor,
        ]: [
            string,
            string,
            number,
            string,
            LeaderboardSortBy,
            LeaderboardSortDirection,
            string | null,
            Mode | null,
            string,
        ]) => {
            return apiClient.get({
                url: '/organizations/{organization_id}/caregiver_points/leaderboard',
                pathParams: { organization_id: orgId },
                queryParams: {
                    limit: pageLimit,
                    ...(cursor ? { cursor } : {}),
                    ...(pageSearch ? { search: pageSearch } : {}),
                    sort_by: pageSortBy,
                    sort_direction: pageSortDirection,
                },
                reason: traceParams.reason,
            });
        },
        {
            revalidateFirstPage: true,
            revalidateAll: true,
        },
    );

    const rows = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.flatMap((page) => page.rows);
    }, [data]);

    const isLoadingInitialData = !data && !error && !!organizationId;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const lastPage = data?.[data.length - 1];
    const hasMore = !!lastPage?.next_cursor;

    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore) {
            setSize((currentSize) => currentSize + 1);
        }
    }, [hasMore, isLoadingMore, setSize]);

    return {
        rows,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
        isValidating,
    };
}

export function useCaregiverPointEntries(
    organizationId: string | null,
    caregiverId: string | null,
    traceParams: TraceParams,
    options?: {
        readonly limit?: number;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregiver_points/caregivers/{caregiver_id}/entries',
        method: 'get',
        pathParams:
            organizationId && caregiverId
                ? { organization_id: organizationId, caregiver_id: caregiverId }
                : undefined,
        queryParams: options,
        enabled: !!organizationId && !!caregiverId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationVoiceCall(
    organizationId: string | null,
    voiceCallId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-calls/{voice_call_id}',
        method: 'get',
        pathParams:
            organizationId && voiceCallId
                ? {
                      organization_id: organizationId,
                      voice_call_id: voiceCallId,
                  }
                : undefined,
        enabled: !!organizationId && !!voiceCallId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useCaregiverMemory(
    organizationId: string | null,
    caregiverId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregivers/{caregiver_id}/memory',
        method: 'get',
        pathParams:
            organizationId && caregiverId
                ? { organization_id: organizationId, caregiver_id: caregiverId }
                : undefined,
        enabled: !!organizationId && !!caregiverId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useClientMemory(
    organizationId: string | null,
    clientId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}/memory',
        method: 'get',
        pathParams:
            organizationId && clientId
                ? { organization_id: organizationId, client_id: clientId }
                : undefined,
        enabled: !!organizationId && !!clientId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useCaregiverAvailabilityProfile(
    organizationId: string | null,
    caregiverId: string | null,
    startDate: string | null,
    endDate: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability-profile',
        method: 'get',
        pathParams:
            organizationId && caregiverId
                ? { organization_id: organizationId, caregiver_id: caregiverId }
                : undefined,
        queryParams:
            startDate && endDate
                ? { start_date: startDate, end_date: endDate }
                : undefined,
        enabled: !!organizationId && !!caregiverId && !!startDate && !!endDate,
        select: (response) => response,
        // The date range and caregiver identity are visible in the profile UI.
        // Retaining the prior result can therefore show another person or
        // month's schedule under the newly selected heading.
        keepPreviousData: false,
    });
}

export function useCaregiverSchedulingProfile(
    organizationId: string | null,
    caregiverId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/caregivers/{caregiver_id}/scheduling-profile',
        method: 'get',
        pathParams:
            organizationId && caregiverId
                ? { organization_id: organizationId, caregiver_id: caregiverId }
                : undefined,
        enabled: !!organizationId && !!caregiverId,
        select: (response) => response,
    });
}

const DEFAULT_CONVERSATIONS_PAGE_SIZE = 30;
const DEFAULT_CONVERSATION_MESSAGES_PAGE_SIZE = 30;

type OrganizationConversationResponse = ApiResponseType<
    '/organizations/{organization_id}/conversations/{conversation_id}',
    'get'
>;

function isPendingOutboundMessage(
    message: OrganizationConversationResponse['messages'][number],
) {
    return (
        message.role === 'assistant' &&
        (message.sent_status ?? 'sent') === 'pending'
    );
}

export function useOrganizationConversations(
    organizationId: string | null,
    _traceParams: TraceParams,
    search?: string,
    options?: {
        pageSize?: number;
        caregiverId?: string;
        contactPhoneNumber?: string;
        outgoingPhoneNumber?: string;
        hasPendingMessages?: boolean;
        sort?: 'pending_first';
        // Read-state filter: `unread` / `read` narrow by unread count; omit
        // to show both.
        readState?: 'unread' | 'read';
        officeIds?: ReadonlyArray<string>;
        includeUnassignedOffice?: boolean;
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
        // Conversation ownership queue filter (flag
        // `conversation_ownership_enabled`): a user UUID, the literal `me`,
        // or `unassigned`.
        assignee?: string;
        // Recency window in days: only conversations with activity newer
        // than this are returned. Omit (or null) for full history.
        activeWithinDays?: number | null;
        // When false, the query is paused (no fetch). Callers use this to wait
        // until the office scope is known before loading, so a scoped user
        // never briefly fetches the full org inbox.
        enabled?: boolean;
    },
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const pageSize = options?.pageSize ?? DEFAULT_CONVERSATIONS_PAGE_SIZE;
    const caregiverId = options?.caregiverId;
    const contactPhoneNumber = options?.contactPhoneNumber;
    const outgoingPhoneNumber = options?.outgoingPhoneNumber;
    const hasPendingMessages = options?.hasPendingMessages;
    const sort = options?.sort;
    const readState = options?.readState ?? null;
    const officeIdsKey = [...(options?.officeIds ?? [])].sort().join(',');
    const includeUnassignedOffice = options?.includeUnassignedOffice ?? false;
    const careCoordinatorIdsKey = [...(options?.careCoordinatorIds ?? [])]
        .sort()
        .join(',');
    const includeUnassignedCareCoordinators =
        options?.includeUnassignedCareCoordinators ?? false;
    const assignee = options?.assignee ?? null;
    const activeWithinDays = options?.activeWithinDays ?? null;
    const enabled = options?.enabled ?? true;

    const getKey = (
        pageIndex: number,
        previousPageData: OrganizationConversationsResponse | null,
    ) => {
        if (!organizationId || !enabled) {
            return null;
        }

        if (
            previousPageData &&
            previousPageData.conversations.length < pageSize
        ) {
            return null;
        }

        return [
            'organization-conversations',
            organizationId,
            pageIndex + 1,
            search ?? '',
            pageSize,
            mode,
            caregiverId ?? null,
            contactPhoneNumber ?? null,
            outgoingPhoneNumber ?? null,
            hasPendingMessages ?? null,
            sort ?? null,
            officeIdsKey,
            includeUnassignedOffice,
            careCoordinatorIdsKey,
            includeUnassignedCareCoordinators,
            readState,
            assignee,
            activeWithinDays,
        ] as const;
    };

    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        OrganizationConversationsResponse,
        ApiError
    >(
        getKey,
        async ([
            _key,
            orgId,
            page,
            searchTerm,
            perPage,
            _mode,
            caregiverIdParam,
            contactPhoneNumberParam,
            outgoingPhoneNumberParam,
            pendingFilter,
            sortParam,
            officeIdsParam,
            includeUnassignedOfficeParam,
            careCoordinatorIdsParam,
            includeUnassignedCareCoordinatorsParam,
            readStateParam,
            assigneeParam,
            activeWithinDaysParam,
        ]: [
            string,
            string,
            number,
            string,
            number,
            string | null,
            string | null,
            string | null,
            string | null,
            boolean | null,
            'pending_first' | null,
            string,
            boolean,
            string,
            boolean,
            'unread' | 'read' | null,
            string | null,
            number | null,
        ]) => {
            return apiClient.get({
                url: '/organizations/{organization_id}/conversations',
                pathParams: { organization_id: orgId },
                queryParams: {
                    page,
                    per_page: perPage,
                    ...(searchTerm ? { search: searchTerm } : {}),
                    ...(caregiverIdParam
                        ? { caregiver_id: caregiverIdParam }
                        : {}),
                    ...(contactPhoneNumberParam
                        ? { contact_phone_number: contactPhoneNumberParam }
                        : {}),
                    ...(outgoingPhoneNumberParam
                        ? { outgoing_phone_number: outgoingPhoneNumberParam }
                        : {}),
                    ...(pendingFilter !== null
                        ? { has_pending_messages: pendingFilter }
                        : {}),
                    ...(sortParam ? { sort: sortParam } : {}),
                    ...(officeIdsParam
                        ? { office_id: officeIdsParam.split(',') }
                        : {}),
                    ...(includeUnassignedOfficeParam
                        ? { include_unassigned_office: true }
                        : {}),
                    ...(includeUnassignedCareCoordinatorsParam
                        ? {
                              care_coordinator_ids: careCoordinatorIdsParam
                                  ? careCoordinatorIdsParam.split(',')
                                  : [],
                              include_unassigned_care_coordinators: true,
                          }
                        : careCoordinatorIdsParam
                          ? {
                                care_coordinator_ids:
                                    careCoordinatorIdsParam.split(','),
                            }
                          : {}),
                    ...(readStateParam === 'unread'
                        ? { unread_only: true }
                        : {}),
                    ...(readStateParam === 'read' ? { read_only: true } : {}),
                    ...(assigneeParam ? { assignee: assigneeParam } : {}),
                    ...(activeWithinDaysParam !== null
                        ? { active_within_days: activeWithinDaysParam }
                        : {}),
                },
            });
        },
        {
            // Both deliberate. `revalidateFirstPage` would refetch page one
            // alone on every revalidation, including each `setSize`, leaving
            // later pages at stale offsets — over a recency-ordered feed that
            // duplicates conversations across page boundaries and drops the
            // ones they displace. And with it off, `refreshInterval` is inert:
            // SWR's infinite fetcher decides per page whether to hit the
            // network, and a timer tick does not set the force flag `mutate()`
            // does, so a warm cache answers no for every page. Staleness is
            // bounded by `mutate()` in `useOrganizationConversationsRealtime`
            // instead, which refreshes every loaded page together. Callers that
            // use this hook directly rather than through that wrapper get no
            // floor — the removed `refreshInterval` never gave them one either,
            // for the reason above.
            revalidateFirstPage: false,
            shouldRetryOnError: false,
        },
    );

    const conversations = useMemo(() => {
        if (!data) {
            return [] as Array<OrganizationConversationMetadata>;
        }
        const seenIds = new Set<string>();
        const uniqueConversations: Array<OrganizationConversationMetadata> = [];
        for (const page of data) {
            for (const conversation of page.conversations) {
                if (seenIds.has(conversation.id)) {
                    continue;
                }
                seenIds.add(conversation.id);
                uniqueConversations.push(conversation);
            }
        }
        return uniqueConversations;
    }, [data]);

    const isLoadingInitialData = !data && !error;
    const lastFetchedPage = data?.[size - 1];
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && typeof lastFetchedPage === 'undefined');
    const isEmpty = data ? data[0]?.conversations.length === 0 : false;
    const lastPage = data?.[data.length - 1];
    const idsBeforeLastPage = new Set(
        (data ?? [])
            .slice(0, -1)
            .flatMap((page) =>
                page.conversations.map((conversation) => conversation.id),
            ),
    );
    const lastPageHasNewConversation = lastPage
        ? lastPage.conversations.some(
              (conversation) => !idsBeforeLastPage.has(conversation.id),
          )
        : false;
    const isReachingEnd = !!(
        isEmpty ||
        (lastPage ? lastPage.conversations.length < pageSize : false) ||
        (data && data.length > 1 && !lastPageHasNewConversation)
    );

    return {
        conversations,
        // The content search filled its candidate budget on the first page,
        // so threads that match only by message text may be missing.
        contentMatchTruncated: data?.[0]?.content_match_truncated ?? false,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        isValidating,
        isReachingEnd,
        size,
        setSize,
        mutate,
    };
}

export function useOrganizationUnreadCount(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/conversations/unread-count',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

export function useConversationActions(
    organizationId: string | null,
    conversationPageMutators: ReadonlyArray<ConversationPagesMutator>,
    mutateUnreadCount: () => Promise<unknown>,
) {
    const apiClient = getApiClient();
    const mutatorsRef = useRef(conversationPageMutators);
    mutatorsRef.current = conversationPageMutators;
    const mutateConversations = useCallback(
        () => Promise.all(mutatorsRef.current.map((mutate) => mutate())),
        [],
    );

    const markAsRead = useCallback(
        async (conversationId: string) => {
            if (!organizationId) {
                return;
            }
            await markConversationAsRead(
                apiClient,
                organizationId,
                conversationId,
                mutatorsRef.current,
            );
            // The list cache was patched optimistically, so deliberately skip a
            // full refetch that causes visible list jitter; throttled realtime
            // revalidation reconciles it with the server later.
            void mutateUnreadCount();
        },
        [organizationId, apiClient, mutateUnreadCount],
    );

    const markAsUnread = useCallback(
        async (conversationId: string) => {
            if (!organizationId) {
                return;
            }
            await apiClient.post({
                url: '/organizations/{organization_id}/conversations/{conversation_id}/mark-unread',
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: conversationId,
                },
            });
            await mutateConversations();
            void mutateUnreadCount();
        },
        [organizationId, apiClient, mutateConversations, mutateUnreadCount],
    );

    return { markAsRead, markAsUnread };
}

export function useOrganizationConversation(
    organizationId: string | null,
    conversationId: string | null,
    _traceParams: TraceParams,
    options?: {
        pageSize?: number;
        markAsRead?: boolean;
        autoLoadPages?: boolean;
        autoLoadUntilMessageId?: string | null;
    },
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const renderedTimelineSnapshotRef = useRef<{
        key: string;
        messages: OrganizationConversationResponse['messages'];
        calls: NonNullable<OrganizationConversationResponse['calls']>;
        ledgerEvents: NonNullable<
            OrganizationConversationResponse['ledger_events']
        >;
        ehrClockEvents: NonNullable<
            OrganizationConversationResponse['ehr_clock_events']
        >;
        messagePageNumbers: ReadonlyMap<string, number>;
        callPageNumbers: ReadonlyMap<string, number>;
        carriedMessageIds: ReadonlySet<string>;
        carriedCallIds: ReadonlySet<string>;
    } | null>(null);
    const pageAppliedMessageCountsRef = useRef<{
        key: string;
        counts: Map<number, number>;
    } | null>(null);

    const pageSize =
        options?.pageSize ?? DEFAULT_CONVERSATION_MESSAGES_PAGE_SIZE;
    const markAsRead = options?.markAsRead ?? true;
    const autoLoadPages = options?.autoLoadPages ?? false;
    const autoLoadUntilMessageId = options?.autoLoadUntilMessageId ?? null;
    const isRequestEnabled = Boolean(organizationId && conversationId);
    const timelineSnapshotKey = `${organizationId ?? ''}:${conversationId ?? ''}:${mode ?? ''}`;

    const getKey = (
        pageIndex: number,
        previousPageData: OrganizationConversationResponse | null,
    ) => {
        if (!organizationId || !conversationId) {
            return null;
        }

        if (previousPageData && previousPageData.messages.length < pageSize) {
            return null;
        }

        const shouldMarkAsRead = markAsRead && pageIndex === 0;

        return [
            'organization-conversation',
            organizationId,
            conversationId,
            pageIndex + 1,
            pageSize,
            shouldMarkAsRead,
            mode,
        ] as const;
    };

    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        OrganizationConversationResponse,
        ApiError
    >(
        getKey,
        async ([
            _key,
            organizationId,
            conversationId,
            page,
            perPage,
            shouldMarkAsRead,
            _mode,
        ]: [
            string,
            string,
            string,
            number,
            number,
            boolean,
            string | null,
        ]) => {
            return apiClient.get({
                url: '/organizations/{organization_id}/conversations/{conversation_id}',
                pathParams: {
                    organization_id: organizationId,
                    conversation_id: conversationId,
                },
                queryParams: {
                    page,
                    per_page: perPage,
                    mark_as_read: shouldMarkAsRead,
                },
            });
        },
        {
            revalidateFirstPage: false,
            shouldRetryOnError: false,
        },
    );

    const isLoadingInitialData = isRequestEnabled ? !data && !error : false;
    const lastFetchedPage = isRequestEnabled ? data?.[size - 1] : undefined;
    const isLoadingMore = isRequestEnabled
        ? isLoadingInitialData ||
          (size > 0 && typeof lastFetchedPage === 'undefined')
        : false;
    const isEmpty =
        isRequestEnabled && data ? data[0]?.messages.length === 0 : false;
    const lastPage = isRequestEnabled ? data?.[data.length - 1] : undefined;
    const isReachingEnd = isRequestEnabled
        ? Boolean(
              isEmpty ||
              (lastPage ? lastPage.messages.length < pageSize : false),
          )
        : true;

    useEffect(() => {
        if (!isRequestEnabled) {
            return;
        }

        if (
            !shouldAutoloadNextConversationPage({
                autoLoadAllPages: autoLoadPages,
                autoLoadUntilMessageId,
                pages: data,
                isReachingEnd,
                isValidating,
                loadedPageCount: data?.length ?? 0,
                requestedSize: size,
            })
        ) {
            return;
        }

        setSize((currentSize) => currentSize + 1);
    }, [
        autoLoadPages,
        autoLoadUntilMessageId,
        data,
        isRequestEnabled,
        isReachingEnd,
        isValidating,
        setSize,
        size,
    ]);

    const conversation =
        useMemo<OrganizationConversationResponse | null>(() => {
            if (!isRequestEnabled || !data || data.length === 0) {
                return null;
            }

            const basePage = data.find((page) => page !== null);
            if (!basePage) {
                return null;
            }

            const aggregatedMessages: OrganizationConversationResponse['messages'] =
                [];
            const aggregatedMessageIds = new Set<string>();
            const aggregatedCalls: OrganizationConversationResponse['calls'] =
                [];
            const aggregatedLedgerEvents: OrganizationConversationResponse['ledger_events'] =
                [];
            const aggregatedCallIds = new Set<string>();
            const aggregatedLedgerEventIds = new Set<string>();
            const aggregatedEhrClockEvents: OrganizationConversationResponse['ehr_clock_events'] =
                [];
            const aggregatedEhrClockEventIds = new Set<string>();
            const aggregatedMessagePageNumbers = new Map<string, number>();
            const aggregatedCallPageNumbers = new Map<string, number>();
            const carriedMessageIds = new Set<string>();
            const carriedCallIds = new Set<string>();

            for (let index = data.length - 1; index >= 0; index -= 1) {
                const page = data[index];
                if (!page) {
                    continue;
                }
                for (const message of page.messages) {
                    if (aggregatedMessageIds.has(message.id)) {
                        continue;
                    }
                    aggregatedMessageIds.add(message.id);
                    aggregatedMessagePageNumbers.set(message.id, index + 1);
                    aggregatedMessages.push(message);
                }
                for (const event of page.ledger_events ?? []) {
                    if (aggregatedLedgerEventIds.has(event.id)) {
                        continue;
                    }
                    aggregatedLedgerEventIds.add(event.id);
                    aggregatedLedgerEvents.push(event);
                }
                for (const call of page.calls ?? []) {
                    if (aggregatedCallIds.has(call.id)) {
                        continue;
                    }
                    aggregatedCallIds.add(call.id);
                    aggregatedCallPageNumbers.set(call.id, index + 1);
                    aggregatedCalls.push(call);
                }
                for (const event of page.ehr_clock_events ?? []) {
                    if (aggregatedEhrClockEventIds.has(event.id)) {
                        continue;
                    }
                    aggregatedEhrClockEventIds.add(event.id);
                    aggregatedEhrClockEvents.push(event);
                }
            }

            const previousTimeline =
                renderedTimelineSnapshotRef.current?.key === timelineSnapshotKey
                    ? renderedTimelineSnapshotRef.current
                    : null;
            // A cache refresh can briefly return an older first page. Keep
            // already-rendered timeline entries visible until all refreshed
            // pages catch up, while preferring current data for matching IDs.
            for (const message of previousTimeline?.messages ?? []) {
                if (aggregatedMessageIds.has(message.id)) {
                    continue;
                }
                aggregatedMessageIds.add(message.id);
                const previousPageNumber =
                    previousTimeline?.messagePageNumbers.get(message.id);
                if (previousPageNumber !== undefined) {
                    aggregatedMessagePageNumbers.set(
                        message.id,
                        previousPageNumber,
                    );
                }
                carriedMessageIds.add(message.id);
                aggregatedMessages.push(message);
            }
            for (const call of previousTimeline?.calls ?? []) {
                if (aggregatedCallIds.has(call.id)) {
                    continue;
                }
                aggregatedCallIds.add(call.id);
                const previousPageNumber =
                    previousTimeline?.callPageNumbers.get(call.id);
                if (previousPageNumber !== undefined) {
                    aggregatedCallPageNumbers.set(call.id, previousPageNumber);
                }
                carriedCallIds.add(call.id);
                aggregatedCalls.push(call);
            }
            for (const event of previousTimeline?.ledgerEvents ?? []) {
                if (aggregatedLedgerEventIds.has(event.id)) {
                    continue;
                }
                aggregatedLedgerEventIds.add(event.id);
                aggregatedLedgerEvents.push(event);
            }
            for (const event of previousTimeline?.ehrClockEvents ?? []) {
                if (aggregatedEhrClockEventIds.has(event.id)) {
                    continue;
                }
                aggregatedEhrClockEventIds.add(event.id);
                aggregatedEhrClockEvents.push(event);
            }
            aggregatedMessages.sort(
                (a, b) =>
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime(),
            );
            aggregatedCalls.sort(
                (a, b) =>
                    new Date(a.started_at ?? a.created_at).getTime() -
                    new Date(b.started_at ?? b.created_at).getTime(),
            );
            aggregatedLedgerEvents.sort(
                (a, b) =>
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime(),
            );
            aggregatedEhrClockEvents.sort(
                (a, b) =>
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime(),
            );
            renderedTimelineSnapshotRef.current = {
                key: timelineSnapshotKey,
                messages: aggregatedMessages,
                calls: aggregatedCalls,
                ledgerEvents: aggregatedLedgerEvents,
                ehrClockEvents: aggregatedEhrClockEvents,
                messagePageNumbers: aggregatedMessagePageNumbers,
                callPageNumbers: aggregatedCallPageNumbers,
                carriedMessageIds,
                carriedCallIds,
            };

            return {
                ...basePage,
                messages: aggregatedMessages,
                calls: aggregatedCalls,
                ledger_events: aggregatedLedgerEvents,
                ehr_clock_events: aggregatedEhrClockEvents,
            };
        }, [data, isRequestEnabled, timelineSnapshotKey]);

    const messages = useMemo<
        Array<OrganizationConversationResponse['messages'][number]>
    >(() => conversation?.messages ?? [], [conversation]);
    const revalidateRelevantPages = useCallback(
        (targets?: {
            readonly messageIds?: ReadonlyArray<string>;
            readonly callIds?: ReadonlyArray<string>;
            readonly newMessageCount?: number;
            readonly unappliedMessageCount?: number;
        }) => {
            const messageIds = new Set(targets?.messageIds ?? []);
            const callIds = new Set(targets?.callIds ?? []);
            const newestPageShift = Math.ceil(
                (targets?.newMessageCount ?? 0) / pageSize,
            );
            const unappliedMessageCount = targets?.unappliedMessageCount ?? 0;
            const relevantPageNumbers = new Set(
                Array.from(
                    { length: Math.max(1, newestPageShift + 1) },
                    (_, index) => index + 1,
                ),
            );
            const snapshot =
                renderedTimelineSnapshotRef.current?.key === timelineSnapshotKey
                    ? renderedTimelineSnapshotRef.current
                    : null;
            let appliedState = pageAppliedMessageCountsRef.current;
            if (appliedState?.key !== timelineSnapshotKey) {
                appliedState = {
                    key: timelineSnapshotKey,
                    counts: new Map<number, number>(),
                };
                pageAppliedMessageCountsRef.current = appliedState;
            }
            const addPageAndOlderNeighbors = (
                pageNumber: number | undefined,
                carriedFromSnapshot: boolean,
            ) => {
                if (pageNumber === undefined) {
                    return;
                }
                // A refreshed page has already absorbed earlier insertions.
                // Snapshot-only entries keep the full offset until the API
                // returns them on a current page again.
                const appliedMessageCount = carriedFromSnapshot
                    ? 0
                    : (appliedState.counts.get(pageNumber) ?? 0);
                const targetPageShift = Math.ceil(
                    Math.max(0, unappliedMessageCount - appliedMessageCount) /
                        pageSize,
                );
                for (let offset = 0; offset <= targetPageShift; offset += 1) {
                    relevantPageNumbers.add(pageNumber + offset);
                }
            };
            for (const messageId of messageIds) {
                addPageAndOlderNeighbors(
                    snapshot?.messagePageNumbers.get(messageId),
                    snapshot?.carriedMessageIds.has(messageId) ?? false,
                );
            }
            for (const callId of callIds) {
                addPageAndOlderNeighbors(
                    snapshot?.callPageNumbers.get(callId),
                    snapshot?.carriedCallIds.has(callId) ?? false,
                );
            }
            const revalidation = mutate((currentPages) => currentPages, {
                revalidate: (page, key) =>
                    (Array.isArray(key) &&
                        typeof key[3] === 'number' &&
                        relevantPageNumbers.has(key[3])) ||
                    (page?.messages.some((message) =>
                        messageIds.has(message.id),
                    ) ??
                        false) ||
                    (page?.calls?.some((call) => callIds.has(call.id)) ??
                        false),
            });
            return Promise.resolve(revalidation).then((result) => {
                const currentAppliedState = pageAppliedMessageCountsRef.current;
                if (currentAppliedState?.key !== timelineSnapshotKey) {
                    return result;
                }
                for (const pageNumber of relevantPageNumbers) {
                    currentAppliedState.counts.set(
                        pageNumber,
                        Math.max(
                            currentAppliedState.counts.get(pageNumber) ?? 0,
                            unappliedMessageCount,
                        ),
                    );
                }
                return result;
            });
        },
        [mutate, pageSize, timelineSnapshotKey],
    );
    const hasPendingOutboundMessages = useMemo(
        () => messages.some(isPendingOutboundMessage),
        [messages],
    );
    // Only the cached pages holding a pending message; a bare mutate() walks
    // every page and on long threads never finishes within a poll interval.
    const revalidatePendingOutboundPages = useCallback(
        () =>
            mutate((currentPages) => currentPages, {
                revalidate: (page) =>
                    page?.messages.some(isPendingOutboundMessage) ?? false,
            }),
        [mutate],
    );
    const firstPageKey = getKey(0, null);
    const cacheScope = firstPageKey ? JSON.stringify(firstPageKey) : null;

    return {
        conversation,
        messages,
        pages: isRequestEnabled ? data : undefined,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        isValidating,
        isReachingEnd,
        size,
        setSize,
        mutate,
        revalidateRelevantPages,
        hasPendingOutboundMessages,
        revalidatePendingOutboundPages,
        cacheScope,
    };
}

export function useOrganizationDataSourceAccountLocations(
    organizationId: string | null,
    accountId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/data-source-accounts/{account_id}/locations',
        method: 'get',
        pathParams:
            organizationId && accountId
                ? { organization_id: organizationId, account_id: accountId }
                : undefined,
        enabled: !!organizationId && !!accountId,
        select: (response) => response,
        reason: traceParams.reason,
        // Each fetch opens a live WellSky session and reads the tenant's
        // profile form, so changing browser focus must not repeat that work.
        revalidateOnFocus: false,
    });
}

export function useOrganizationDataSourceAccountTags(
    organizationId: string | null,
    accountId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/data-source-accounts/{account_id}/tags',
        method: 'get',
        pathParams:
            organizationId && accountId
                ? { organization_id: organizationId, account_id: accountId }
                : undefined,
        enabled: !!organizationId && !!accountId,
        select: (response) => response.tags,
        reason: traceParams.reason,
        // Each fetch opens a live WellSky session, so do not re-run it every
        // time the admin tabs back to the window while the modal is open.
        revalidateOnFocus: false,
    });
}

export function useOrganizationDataSourceAccountOffices(
    organizationId: string | null,
    accountId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/data-source-accounts/{account_id}/offices',
        method: 'get',
        pathParams:
            organizationId && accountId
                ? { organization_id: organizationId, account_id: accountId }
                : undefined,
        enabled: !!organizationId && !!accountId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useShiftConfirmation(
    organizationId: string | null,
    confirmationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shift-confirmations/{confirmation_id}',
        method: 'get',
        pathParams:
            organizationId && confirmationId
                ? {
                      organization_id: organizationId,
                      confirmation_id: confirmationId,
                  }
                : undefined,
        enabled: !!organizationId && !!confirmationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOrganizationPendingMessagesCount(
    organizationId: string,
    traceParams: TraceParams,
    options?: {
        conversationId?: string;
        domain?: StagedMessageDomain;
        domainId?: string;
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/conversations/pending-messages-count',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: {
            ...(options?.conversationId && {
                conversation_id: options.conversationId,
            }),
            ...(options?.domain && { domain: options.domain }),
            ...(options?.domainId && { domain_id: options.domainId }),
            // When includeUnassigned is true, always pass care_coordinator_ids (even if empty)
            // to ensure proper filtering. Otherwise only pass if there are actual IDs.
            ...(options?.includeUnassignedCareCoordinators
                ? { care_coordinator_ids: options.careCoordinatorIds ?? [] }
                : options?.careCoordinatorIds &&
                  options.careCoordinatorIds.length > 0 && {
                      care_coordinator_ids: options.careCoordinatorIds,
                  }),
            ...(options?.includeUnassignedCareCoordinators && {
                include_unassigned_care_coordinators: true,
            }),
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

export function useOrganizationPendingMessages(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: {
        conversationId?: string;
        domain?: StagedMessageDomain;
        domainId?: string;
        careCoordinatorIds?: Array<string>;
        includeUnassignedCareCoordinators?: boolean;
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/conversations/pending-messages',
        method: 'get',
        pathParams: organizationId
            ? {
                  organization_id: organizationId,
              }
            : undefined,
        queryParams: {
            ...(options?.conversationId && {
                conversation_id: options.conversationId,
            }),
            ...(options?.domain && { domain: options.domain }),
            ...(options?.domainId && { domain_id: options.domainId }),
            // When includeUnassigned is true, always pass care_coordinator_ids (even if empty)
            // to ensure proper filtering. Otherwise only pass if there are actual IDs.
            ...(options?.includeUnassignedCareCoordinators
                ? { care_coordinator_ids: options.careCoordinatorIds ?? [] }
                : options?.careCoordinatorIds &&
                  options.careCoordinatorIds.length > 0 && {
                      care_coordinator_ids: options.careCoordinatorIds,
                  }),
            ...(options?.includeUnassignedCareCoordinators && {
                include_unassigned_care_coordinators: true,
            }),
        },
        enabled: !!organizationId,
        select: (response) => response.staged_messages || [],
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

// Admin Shifts hooks
export function useAdminShifts(
    params?: {
        page?: number;
        per_page?: number;
        organization_id?: string;
        status?: string;
        search?: string;
        mode?: Mode;
    },
    options?: {
        enabled?: boolean;
    },
) {
    return useApiQuery({
        path: '/admin/shifts',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
    });
}

export function useAdminShift(shiftId: string | null, mode?: Mode) {
    return useApiQuery({
        path: '/admin/shifts/{shift_id}',
        method: 'get',
        pathParams: shiftId ? { shift_id: shiftId } : undefined,
        queryParams: mode ? { mode } : undefined,
        enabled: !!shiftId,
        select: (response) => response,
    });
}

// Admin Caregivers hooks
export function useAdminCaregivers(params?: {
    page?: number;
    per_page?: number;
    organization_id?: string;
    mode?: Mode;
    status?: string;
    search?: string;
    tags?: Array<string>;
    roles?: Array<string>;
    office_name?: string;
}) {
    return useApiQuery({
        path: '/admin/caregivers',
        method: 'get',
        queryParams: params,
        mode: params?.mode,
        select: (response) => response,
    });
}

export function useAdminCaregiverFilters(
    organizationId: string | null,
    mode?: Mode | null,
) {
    return useApiQuery({
        path: '/admin/caregivers/filters/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: mode ? { mode } : undefined,
        mode: mode ?? undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminCaregiver(caregiverId: string | null) {
    return useApiQuery({
        path: '/admin/caregivers/{caregiver_id}',
        method: 'get',
        pathParams: caregiverId ? { caregiver_id: caregiverId } : undefined,
        enabled: !!caregiverId,
        select: (response) => response,
    });
}

export function useAdminOrgDistributionsStatic(
    organizationId: string | null,
    mode: Mode,
) {
    return useApiQuery({
        path: '/admin/org-distributions/{organization_id}/static',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { mode },
        mode,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrgDistributionsWindowed(
    organizationId: string | null,
    mode: Mode,
    window: components['schemas']['DistributionWindow'],
) {
    return useApiQuery({
        path: '/admin/org-distributions/{organization_id}/windowed',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { mode, window },
        mode,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminOrgDistributionsMap(
    organizationId: string | null,
    mode: Mode,
    window: components['schemas']['DistributionWindow'],
) {
    return useApiQuery({
        path: '/admin/org-distributions/{organization_id}/map',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { mode, window },
        mode,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

// Admin Care Coordinator hooks
export function useAdminCareCoordinators(params?: {
    page?: number;
    per_page?: number;
    organization_id?: string;
    status?: string;
    search?: string;
}) {
    return useApiQuery({
        path: '/admin/care-coordinators',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useAdminCareCoordinator(careCoordinatorId: string | null) {
    return useApiQuery({
        path: '/admin/care-coordinators/{care_coordinator_id}',
        method: 'get',
        pathParams: careCoordinatorId
            ? { care_coordinator_id: careCoordinatorId }
            : undefined,
        enabled: !!careCoordinatorId,
        select: (response) => response,
    });
}

// Admin Clients hooks
export function useAdminClients(params?: {
    page?: number;
    per_page?: number;
    organization_id?: string;
    mode?: Mode;
    status?: string;
    search?: string;
    office_id?: string;
    office_name?: string;
}) {
    return useApiQuery({
        path: '/admin/clients',
        method: 'get',
        queryParams: params,
        mode: params?.mode,
        select: (response) => response,
    });
}

export function useAdminClientOffices(
    organizationId: string | null,
    mode?: Mode | null,
) {
    return useApiQuery({
        path: '/admin/clients/offices/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: mode ? { mode } : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAdminClient(clientId: string | null) {
    return useApiQuery({
        path: '/admin/clients/{client_id}',
        method: 'get',
        pathParams: clientId ? { client_id: clientId } : undefined,
        enabled: !!clientId,
        select: (response) => response,
    });
}

// Admin Revenue hooks
export function useAdminRevenueAccounts(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: RevenueAccountStatus;
    organization_id?: string;
    customer_account_id?: string;
}) {
    return useApiQuery({
        path: '/admin/revenue/accounts',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useAdminRevenueCustomerAccounts() {
    return useApiQuery({
        path: '/admin/revenue/customer-accounts',
        method: 'get',
        select: (response) => response,
    });
}

export function useAdminRevenueAccount(accountId: string | null) {
    return useApiQuery({
        path: '/admin/revenue/accounts/{account_id}',
        method: 'get',
        pathParams: accountId ? { account_id: accountId } : undefined,
        enabled: !!accountId,
        select: (response) => response,
    });
}

export function useAdminRevenueSummary() {
    return useApiQuery({
        path: '/admin/revenue/summary',
        method: 'get',
        select: (response) => response,
    });
}

// Admin VOIP dashboard hooks
export type AdminOrganizationVoipSetup = ApiResponseType<
    '/admin/voip/organizations/{organization_id}',
    'get'
>;

export function useAdminVoipAccounts(params?: {
    search?: string;
    phone_provider?: VoipPhoneProvider;
    integration_type?: VoipIntegrationType;
    cx_owner?: VoipCxOwner;
    eng_owner?: VoipEngOwner;
}) {
    return useApiQuery({
        path: '/admin/voip/accounts',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useAdminOrganizationVoipSetup(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/voip/organizations/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'admin-organization-voip-setup',
    });
}

// Admin Sales hooks
export function useAdminSalesAgreements(params?: {
    page?: number;
    per_page?: number;
    search?: string;
    status?: AgreementStatus;
    organization_id?: string;
}) {
    return useApiQuery({
        path: '/admin/sales/agreements',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useAdminSalesPandaDocTemplates() {
    return useApiQuery({
        path: '/admin/sales/pandadoc/templates',
        method: 'get',
        select: (response) => response,
    });
}

export function useAdminSalesPandaDocCatalog() {
    return useApiQuery({
        path: '/admin/sales/pandadoc/catalog',
        method: 'get',
        select: (response) => response,
    });
}

// Admin Personal Access Tokens hooks
export function useAdminPersonalAccessTokens() {
    return useApiQuery({
        path: '/admin/personal-access-tokens',
        method: 'get',
        select: (response) => response,
    });
}

// Admin Sync Settings hooks
export function useAdminSyncSettings(params?: {
    search?: string;
    data_source_type?: DataSourceType;
}) {
    return useApiQuery({
        path: '/admin/sync-settings',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useUpdateAdminSyncSettings() {
    const apiClient = getApiClient();

    const updateSyncSettings = useCallback(
        async (dataSourceAccountId: string, data: AdminSyncSettingsUpdate) => {
            return apiClient.put({
                url: '/admin/sync-settings/{data_source_account_id}',
                pathParams: { data_source_account_id: dataSourceAccountId },
                body: data,
            });
        },
        [apiClient],
    );

    return { updateSyncSettings };
}

const AXISCARE_MODIFICATION_REASONS_TIMEOUT_MS = 120_000;

export function useFetchAxisCareModificationReasons() {
    const apiClient = getApiClient();

    const fetchAxisCareModificationReasons = useCallback(
        async (dataSourceAccountId: string) => {
            return apiClient.get({
                url: '/admin/sync-settings/{data_source_account_id}/axiscare/modification-reasons',
                pathParams: {
                    data_source_account_id: dataSourceAccountId,
                },
                timeoutMs: AXISCARE_MODIFICATION_REASONS_TIMEOUT_MS,
            });
        },
        [apiClient],
    );

    return { fetchAxisCareModificationReasons };
}

export function useAdminOutreaches(
    params: {
        page?: number;
        perPage?: number;
        organizationId?: string;
        outreachTypes?: Array<components['schemas']['OutreachType']>;
        statuses?: Array<components['schemas']['OutreachStatus']>;
        agentStates?: Array<components['schemas']['OutreachAgentState']>;
        shiftCount?: number;
        shiftCountComparison?: components['schemas']['ShiftCountComparison'];
        isUrgent?: boolean;
        search?: string;
    } = {},
    options: { keepPreviousData?: boolean } = {},
) {
    type OutreachQueryParams = ApiQueryParamsType<'/admin/outreaches', 'get'>;

    let queryParams: OutreachQueryParams | undefined;
    if (params) {
        const candidate: OutreachQueryParams = {
            ...(params.page !== undefined ? { page: params.page } : {}),
            ...(params.perPage !== undefined
                ? { per_page: params.perPage }
                : {}),
            ...(params.organizationId !== undefined
                ? { organization_id: params.organizationId }
                : {}),
            ...(params.outreachTypes !== undefined &&
            params.outreachTypes.length > 0
                ? { outreach_types: params.outreachTypes }
                : {}),
            ...(params.statuses !== undefined && params.statuses.length > 0
                ? { statuses: params.statuses }
                : {}),
            ...(params.agentStates !== undefined &&
            params.agentStates.length > 0
                ? { agent_states: params.agentStates }
                : {}),
            ...(params.shiftCount !== undefined
                ? { shift_count: params.shiftCount }
                : {}),
            ...(params.shiftCountComparison !== undefined
                ? { shift_count_comparison: params.shiftCountComparison }
                : {}),
            ...(params.isUrgent !== undefined
                ? { is_urgent: params.isUrgent }
                : {}),
            ...(params.search !== undefined ? { search: params.search } : {}),
        };
        if (Object.keys(candidate).length > 0) {
            queryParams = candidate;
        }
    }

    return useApiQuery<
        '/admin/outreaches',
        'get',
        AdminOutreachListResponse,
        AdminOutreachListResponse
    >({
        path: '/admin/outreaches',
        method: 'get',
        queryParams,
        select: (response) => response,
        keepPreviousData: options.keepPreviousData,
    });
}

export function useAdminOutreachWork(
    params: {
        since?: string;
        organizationId?: string;
        limit?: number;
    } = {},
    options: { refreshIntervalMs?: number } = {},
) {
    type OutreachWorkQueryParams = ApiQueryParamsType<
        '/admin/outreaches/callout-work',
        'get'
    >;
    type OutreachWorkResponse = ApiResponseType<
        '/admin/outreaches/callout-work',
        'get'
    >;

    const candidate: OutreachWorkQueryParams = {
        ...(params.since !== undefined ? { since: params.since } : {}),
        ...(params.organizationId !== undefined
            ? { organization_id: params.organizationId }
            : {}),
        ...(params.limit !== undefined ? { limit: params.limit } : {}),
    };
    const queryParams =
        Object.keys(candidate).length > 0 ? candidate : undefined;

    return useApiQuery<
        '/admin/outreaches/callout-work',
        'get',
        OutreachWorkResponse,
        OutreachWorkResponse
    >({
        path: '/admin/outreaches/callout-work',
        method: 'get',
        queryParams,
        select: (response) => response,
        refreshInterval: options.refreshIntervalMs,
    });
}

// Admin Outbox hooks
export function useAdminOutboxEntries(
    params: {
        page?: number;
        perPage?: number;
        organizationId?: string;
        channel?: components['schemas']['OutgoingChannel'];
        status?: components['schemas']['OutgoingStatus'];
    } = {},
    options: { keepPreviousData?: boolean } = {},
) {
    type OutboxQueryParams = ApiQueryParamsType<'/admin/outbox', 'get'>;

    let queryParams: OutboxQueryParams | undefined;
    if (params) {
        const candidate: OutboxQueryParams = {
            ...(params.page !== undefined ? { page: params.page } : {}),
            ...(params.perPage !== undefined
                ? { per_page: params.perPage }
                : {}),
            ...(params.organizationId !== undefined
                ? { organization_id: params.organizationId }
                : {}),
            ...(params.channel !== undefined
                ? { channel: params.channel }
                : {}),
            ...(params.status !== undefined ? { status: params.status } : {}),
        };
        if (Object.keys(candidate).length > 0) {
            queryParams = candidate;
        }
    }

    return useApiQuery<
        '/admin/outbox',
        'get',
        AdminOutboxEntryListResponse,
        AdminOutboxEntryListResponse
    >({
        path: '/admin/outbox',
        method: 'get',
        queryParams,
        select: (response) => response,
        keepPreviousData: options.keepPreviousData,
    });
}

export function useAdminAutoOutreachBoard(
    params: {
        organizationId?: string;
        hours?: number;
    } = {},
    options: { refreshIntervalMs?: number } = {},
) {
    const adminMode = useAdminMode();
    type BoardQueryParams = ApiQueryParamsType<
        '/admin/auto-callout-board',
        'get'
    >;

    let queryParams: BoardQueryParams | undefined;
    const candidate: BoardQueryParams = {
        mode: adminMode,
        ...(params.organizationId !== undefined
            ? { organization_id: params.organizationId }
            : {}),
        ...(params.hours !== undefined ? { hours: params.hours } : {}),
    };
    if (Object.keys(candidate).length > 0) {
        queryParams = candidate;
    }

    return useApiQuery<
        '/admin/auto-callout-board',
        'get',
        GetAutoCalloutBoardResponse,
        GetAutoCalloutBoardResponse
    >({
        path: '/admin/auto-callout-board',
        method: 'get',
        queryParams,
        mode: adminMode,
        select: (response) => response,
        refreshInterval: options.refreshIntervalMs,
    });
}

// The sections the dashboard can request. Each tab fetches only the sections it
// renders, so a tab switch fires a smaller, cheaper query. Derived from the
// generated query param so it can't drift from the backend enum.
export type OutreachObservabilitySection = NonNullable<
    ApiQueryParamsType<'/admin/outreach-observability', 'get'>['sections']
>[number];

export function useAdminOutreachObservability(
    params: {
        sections: Array<OutreachObservabilitySection>;
        organizationIds?: Array<string>;
        window?: OutreachWindowSelection;
        urgency?: OutreachUrgencyFilter;
        weeklyWeeks?: number;
    },
    options: { enabled?: boolean; refreshIntervalMs?: number } = {},
) {
    type ObservabilityQueryParams = ApiQueryParamsType<
        '/admin/outreach-observability',
        'get'
    >;

    const queryParams: ObservabilityQueryParams = {
        sections: params.sections,
        ...(params.organizationIds && params.organizationIds.length > 0
            ? { organization_id: params.organizationIds }
            : {}),
        // A preset selection maps to `window`; a custom range maps to the
        // explicit bound params instead.
        ...(params.window === undefined
            ? {}
            : typeof params.window === 'string'
              ? { window: params.window }
              : {
                    window_start: params.window.start,
                    window_end: params.window.end,
                }),
        ...(params.urgency !== undefined ? { urgency: params.urgency } : {}),
        ...(params.weeklyWeeks !== undefined
            ? { weekly_weeks: params.weeklyWeeks }
            : {}),
    };

    return useApiQuery<
        '/admin/outreach-observability',
        'get',
        GetOutreachObservabilityResponse,
        GetOutreachObservabilityResponse
    >({
        path: '/admin/outreach-observability',
        method: 'get',
        queryParams,
        enabled: options.enabled ?? true,
        select: (response) => response,
        refreshInterval: options.refreshIntervalMs,
        keepPreviousData: true,
    });
}

export function useAdminOutreachTv(
    params: { organizationIds?: Array<string> } = {},
    options: { refreshIntervalMs?: number } = {},
) {
    type TvQueryParams = ApiQueryParamsType<
        '/admin/outreach-observability/tv',
        'get'
    >;

    const queryParams: TvQueryParams =
        params.organizationIds && params.organizationIds.length > 0
            ? { organization_id: params.organizationIds }
            : {};

    return useApiQuery<
        '/admin/outreach-observability/tv',
        'get',
        GetOutreachTvResponse,
        GetOutreachTvResponse
    >({
        path: '/admin/outreach-observability/tv',
        method: 'get',
        queryParams,
        select: (response) => response,
        refreshInterval: options.refreshIntervalMs,
        keepPreviousData: true,
    });
}

type KpiRunsQueryParams = ApiQueryParamsType<
    '/admin/outreach-observability/kpi-runs',
    'get'
>;

// Derived from the generated schema so the frontend can't drift from the
// backend KpiRunMetric Literal. This is the full metric union (every drillable
// KPI); the sparkline tiles use a narrower subset defined in kpi_sparkline.tsx.
export type KpiRunMetric = NonNullable<KpiRunsQueryParams['metric']>;

export function useAdminOutreachKpiRuns(
    params: {
        metric: KpiRunMetric;
        organizationIds?: Array<string>;
        // Server-side narrowing passed straight through as query params. `day`
        // scopes to one ET calendar day; `window` scopes to the current live
        // window — preset or custom range — so a window-scoped tile's dialog
        // counts the same window as the number that opened it (mutually
        // exclusive with `day`); the count filters narrow the runs.
        day?: string;
        window?: OutreachWindowSelection;
        // Trailing N-ET-day window (matches the model_ab section window).
        days?: number;
        // Cohort model (stamped model coalesced to the default); keeps only
        // runs whose cohort matches, for the Model A/B drill-down.
        model?: string;
        suggestedCaregiverCount?: number;
        coveredShiftCountMin?: number;
        coveredShiftCountMax?: number;
        schedulerFollowUpCountMin?: number;
        schedulerFollowUpCountMax?: number | null;
        urgency?: OutreachUrgencyFilter;
    },
    enabled: boolean,
) {
    const queryParams: KpiRunsQueryParams = {
        metric: params.metric,
        ...(params.organizationIds && params.organizationIds.length > 0
            ? { organization_id: params.organizationIds }
            : {}),
        ...(params.day !== undefined ? { day: params.day } : {}),
        ...(params.window === undefined
            ? {}
            : typeof params.window === 'string'
              ? { window: params.window }
              : {
                    window_start: params.window.start,
                    window_end: params.window.end,
                }),
        ...(params.days !== undefined ? { days: params.days } : {}),
        ...(params.model !== undefined ? { model: params.model } : {}),
        ...(params.suggestedCaregiverCount !== undefined
            ? { suggested_caregiver_count: params.suggestedCaregiverCount }
            : {}),
        ...(params.coveredShiftCountMin !== undefined
            ? { covered_shift_count_min: params.coveredShiftCountMin }
            : {}),
        ...(params.coveredShiftCountMax !== undefined
            ? { covered_shift_count_max: params.coveredShiftCountMax }
            : {}),
        ...(params.schedulerFollowUpCountMin !== undefined
            ? {
                  scheduler_follow_up_count_min:
                      params.schedulerFollowUpCountMin,
              }
            : {}),
        ...(params.schedulerFollowUpCountMax !== undefined &&
        params.schedulerFollowUpCountMax !== null
            ? {
                  scheduler_follow_up_count_max:
                      params.schedulerFollowUpCountMax,
              }
            : {}),
        ...(params.urgency !== undefined ? { urgency: params.urgency } : {}),
    };
    return useApiQuery({
        path: '/admin/outreach-observability/kpi-runs',
        method: 'get',
        queryParams,
        enabled,
        select: (response) => response,
    });
}

type JudgeRunsQueryParams = ApiQueryParamsType<
    '/admin/outreach-observability/judge-runs',
    'get'
>;

// On-demand drill-down for judge-flagged / graded runs (opened from the hero
// judge-flags tile and the Quality tab). Only fetches while `enabled`.
export function useAdminOutreachJudgeRuns(
    organizationIds: Array<string>,
    enabled: boolean,
    filters?: {
        readonly grade?: string;
        readonly flaggedOnly?: boolean;
        readonly day?: string;
        readonly urgency?: OutreachUrgencyFilter;
    },
) {
    const queryParams: JudgeRunsQueryParams = {
        ...(organizationIds.length > 0
            ? { organization_id: organizationIds }
            : {}),
        ...(filters?.grade !== undefined ? { grade: filters.grade } : {}),
        ...(filters?.flaggedOnly !== undefined
            ? { flagged_only: filters.flaggedOnly }
            : {}),
        ...(filters?.day !== undefined ? { day: filters.day } : {}),
        ...(filters?.urgency !== undefined ? { urgency: filters.urgency } : {}),
    };
    return useApiQuery({
        path: '/admin/outreach-observability/judge-runs',
        method: 'get',
        queryParams,
        enabled,
        select: (response) => response,
    });
}

type ToolRunsQueryParams = ApiQueryParamsType<
    '/admin/outreach-observability/tool-runs',
    'get'
>;

// On-demand drill-down for a per-tool row (opened in the tool drawer): the
// slowest calls of one tool over the window. Only fetches while `enabled`.
export function useAdminOutreachToolRuns(
    toolName: string | null,
    organizationIds: Array<string>,
    enabled: boolean,
    urgency?: OutreachUrgencyFilter,
) {
    const queryParams: ToolRunsQueryParams | undefined =
        toolName === null
            ? undefined
            : {
                  tool_name: toolName,
                  ...(organizationIds.length > 0
                      ? { organization_id: organizationIds }
                      : {}),
                  ...(urgency !== undefined ? { urgency } : {}),
              };
    return useApiQuery({
        path: '/admin/outreach-observability/tool-runs',
        method: 'get',
        queryParams,
        enabled: enabled && toolName !== null,
        select: (response) => response,
    });
}

type InFlightRunsQueryParams = ApiQueryParamsType<
    '/admin/outreach-observability/in-flight-runs',
    'get'
>;

// On-demand drill-down for the live board: currently running outreach runs,
// oldest first. `age_s` advances against request time, so poll while open.
export function useAdminOutreachInFlightRuns(
    organizationIds: Array<string>,
    enabled: boolean,
    urgency?: OutreachUrgencyFilter,
) {
    const queryParams: InFlightRunsQueryParams = {
        ...(organizationIds.length > 0
            ? { organization_id: organizationIds }
            : {}),
        ...(urgency !== undefined ? { urgency } : {}),
    };
    return useApiQuery({
        path: '/admin/outreach-observability/in-flight-runs',
        method: 'get',
        queryParams,
        enabled,
        select: (response) => response,
        refreshInterval: enabled ? 30_000 : undefined,
    });
}

export function useAdminVoiceCall(voiceCallId: string | null) {
    return useApiQuery({
        path: '/admin/voice-calls/{voice_call_id}',
        method: 'get',
        pathParams: voiceCallId ? { voice_call_id: voiceCallId } : undefined,
        enabled: !!voiceCallId,
        select: (response) => response,
    });
}

export function useAdminAmdLiveSummary(days: number) {
    return useApiQuery({
        path: '/admin/voice-calls/amd-live',
        method: 'get',
        queryParams: { days },
        select: (response) => response,
        refreshInterval: 60_000,
    });
}

export function useOrganizationDataSourceAccounts(
    organizationId: string | null,
    traceParams: TraceParams & { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/data-source-accounts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId && (traceParams.enabled ?? true),
        select: (response) => response.data_source_accounts,
        reason: traceParams.reason,
    });
}

export function useAdminMessageQueueStatus() {
    return useApiQuery({
        path: '/admin/message-queue/status',
        method: 'get',
        select: (response) => response,
    });
}

export function useAdminDlqStreams() {
    return useApiQuery({
        path: '/admin/message-queue/dlq/streams',
        method: 'get',
        select: (response) => response.streams,
    });
}

export function useAdminDlqMessages(
    messageType: string | null,
    limit: number = 50,
) {
    return useApiQuery({
        path: '/admin/message-queue/dlq/messages',
        method: 'get',
        queryParams: messageType
            ? { message_type: messageType, limit }
            : undefined,
        enabled: !!messageType,
        select: (response) => response.messages,
    });
}

export function useAdminOrganizationDataSourceAccounts(
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/data-source-accounts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.data_source_accounts,
    });
}

export function useAdminGenerationsPortalLogins(
    organizationId: string | null,
    accountId: string,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/data-source-accounts/{account_id}/portal-logins',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId, account_id: accountId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.portal_logins,
    });
}

export function useOrganizationPhoneNumbers(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/phone-numbers',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.phone_numbers,
    });
}

export function usePhoneNumberEmergencyAddressRequirement(
    organizationId: string | null,
    candidate: {
        readonly mode: Mode;
        readonly voiceEnabled: boolean;
        readonly externalSmsAccountId?: string;
        readonly smsProvider?: components['schemas']['SmsProvider'];
    },
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/phone-numbers/emergency-address-requirement',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            mode: candidate.mode,
            voice_enabled: candidate.voiceEnabled,
            external_sms_account_id: candidate.externalSmsAccountId,
            sms_provider: candidate.smsProvider,
        },
        enabled: organizationId !== null,
        select: (response) => response,
    });
}

export function useOnboardingSettings(
    organizationId: string | null,
    options?: { readonly refreshInterval?: number },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/onboarding-settings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        refreshInterval: options?.refreshInterval,
    });
}

export function useVoiceCallRating(
    organizationId: string | null,
    voiceCallId: string | null,
    isAdmin: boolean = false,
    traceParams: TraceParams & {
        readonly initialData?: ApiResponseType<
            '/organizations/{organization_id}/voice-calls/{voice_call_id}/rating',
            'get'
        >;
    },
) {
    const shouldRevalidatePerCall = traceParams.initialData === undefined;
    // Use admin endpoint for admins, org endpoint for regular users
    const path = isAdmin
        ? '/admin/voice-calls/{voice_call_id}/rating'
        : '/organizations/{organization_id}/voice-calls/{voice_call_id}/rating';

    const pathParams = isAdmin
        ? voiceCallId
            ? { voice_call_id: voiceCallId }
            : undefined
        : organizationId && voiceCallId
          ? {
                organization_id: organizationId,
                voice_call_id: voiceCallId,
            }
          : undefined;

    return useApiQuery({
        path,
        method: 'get',
        pathParams,
        enabled: isAdmin ? !!voiceCallId : !!organizationId && !!voiceCallId,
        select: (response) => response,
        reason: traceParams.reason,
        fallbackData: traceParams.initialData,
        revalidateOnMount: shouldRevalidatePerCall,
        revalidateOnFocus: shouldRevalidatePerCall,
        revalidateOnReconnect: shouldRevalidatePerCall,
    });
}

// Every internal (admin) review of a voice call with reviewer names, newest
// first, for the QA detail view's reviewer notes list.
export function useVoiceCallInternalReviews(
    voiceCallId: string | null,
    reason: string,
) {
    return useApiQuery({
        path: '/admin/voice-calls/{voice_call_id}/reviews',
        method: 'get',
        pathParams: voiceCallId ? { voice_call_id: voiceCallId } : undefined,
        enabled: !!voiceCallId,
        select: (response) => response,
        reason,
    });
}

// Map each SMS engine run in a conversation to its trigger message (the inbound
// caregiver message it answered) + the current user's rating, so the
// training-mode thread can thumb that message like the admin feedback surface.
export function useSmsRunRatings(
    organizationId: string | null,
    conversationId: string | null,
    enabled: boolean,
    reason: string,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/message-ratings/conversations/{conversation_id}/run-ratings',
        method: 'get',
        pathParams:
            organizationId && conversationId
                ? {
                      organization_id: organizationId,
                      conversation_id: conversationId,
                  }
                : undefined,
        enabled: enabled && !!organizationId && !!conversationId,
        select: (response) => response,
        reason,
    });
}

export function useVoiceCallUserRatings(
    organizationId: string | null,
    voiceCallId: string | null,
    isAdmin: boolean = false,
    traceParams: TraceParams,
) {
    // Use admin endpoint for admins, org endpoint for regular users
    const path = isAdmin
        ? '/admin/voice-calls/{voice_call_id}/user-ratings'
        : '/organizations/{organization_id}/voice-calls/{voice_call_id}/user-ratings';

    const pathParams = isAdmin
        ? voiceCallId
            ? { voice_call_id: voiceCallId }
            : undefined
        : organizationId && voiceCallId
          ? {
                organization_id: organizationId,
                voice_call_id: voiceCallId,
            }
          : undefined;

    return useApiQuery({
        path,
        method: 'get',
        pathParams,
        enabled: isAdmin ? !!voiceCallId : !!organizationId && !!voiceCallId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

// Admin Data Quality hooks
export function useAdminDataQualitySummary() {
    return useApiQuery({
        path: '/admin/data-quality/summary',
        method: 'get',
        select: (response) => response,
    });
}

export function useAdminDataQualityIssues(
    params?: ApiQueryParamsType<'/admin/data-quality/issues', 'get'>,
) {
    return useApiQuery({
        path: '/admin/data-quality/issues',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useAdminQualityScoreboard(
    params?: ApiQueryParamsType<'/admin/quality-scoreboard', 'get'>,
) {
    return useApiQuery({
        path: '/admin/quality-scoreboard',
        method: 'get',
        queryParams: params,
        mode: 'live',
        select: (response) => response,
        keepPreviousData: true,
    });
}

export function useAdminLlmSpend(
    params: ApiQueryParamsType<'/admin/llm-spend', 'get'>,
    options: { enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/admin/llm-spend',
        method: 'get',
        queryParams: params,
        mode: 'live',
        enabled: options.enabled,
        select: (response) => response,
        keepPreviousData: true,
    });
}

export function useAdminShiftAssignmentFailureSummary(
    params?: ApiQueryParamsType<
        '/admin/shift-assignment-health/summary',
        'get'
    >,
) {
    return useApiQuery({
        path: '/admin/shift-assignment-health/summary',
        method: 'get',
        queryParams: params,
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminShiftAssignmentFailureFilterOptions() {
    return useApiQuery({
        path: '/admin/shift-assignment-health/filter-options',
        method: 'get',
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminShiftAssignmentFailures(
    params?: ApiQueryParamsType<
        '/admin/shift-assignment-health/failures',
        'get'
    >,
) {
    return useApiQuery({
        path: '/admin/shift-assignment-health/failures',
        method: 'get',
        queryParams: params,
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminShiftAssignmentFailureTrend(
    params?: ApiQueryParamsType<'/admin/shift-assignment-health/trend', 'get'>,
) {
    return useApiQuery({
        path: '/admin/shift-assignment-health/trend',
        method: 'get',
        queryParams: params,
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminImessageThreads(
    params?: ApiQueryParamsType<'/admin/imessage/threads', 'get'>,
) {
    return useApiQuery({
        path: '/admin/imessage/threads',
        method: 'get',
        queryParams: params,
        refreshInterval: 15000,
        keepPreviousData: true,
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminImessageSourceNumbers(enabled: boolean = true) {
    return useApiQuery({
        path: '/admin/imessage/source-numbers',
        method: 'get',
        enabled,
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminImessageFilterOptions() {
    return useApiQuery({
        path: '/admin/imessage/filter-options',
        method: 'get',
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminImessageThread(threadId: string | null) {
    return useApiQuery({
        path: '/admin/imessage/threads/{thread_id}',
        method: 'get',
        pathParams: threadId ? { thread_id: threadId } : undefined,
        enabled: !!threadId,
        refreshInterval: 15000,
        mode: 'live',
        select: (response) => response,
    });
}

export function useAdminImessageStaffDiagnostics(
    userId: string | null,
    organizationId: string | null,
) {
    return useApiQuery({
        path: '/admin/imessage/staff/{user_id}/diagnostics',
        method: 'get',
        pathParams: userId ? { user_id: userId } : undefined,
        queryParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!userId && !!organizationId,
        mode: 'live',
        select: (response) => response,
    });
}

// Keep the old name as an alias for backward compatibility
export const useAdminVoiceCallRating = useVoiceCallRating;

export function useAdminMessageRatings(
    conversationId: string | null,
    isAdmin: boolean = false,
) {
    return useApiQuery({
        path: '/admin/message-ratings/{conversation_id}/ratings',
        method: 'get',
        pathParams: conversationId
            ? { conversation_id: conversationId }
            : undefined,
        enabled: isAdmin && !!conversationId,
        select: (response) => response,
    });
}

export function useAdminMessageRating(
    messageId: string | null,
    isAdmin: boolean = false,
) {
    const pathParams = isAdmin
        ? messageId
            ? { message_id: messageId }
            : undefined
        : undefined;

    return useApiQuery({
        path: '/admin/message-ratings/{message_id}/rating',
        method: 'get',
        pathParams,
        enabled: isAdmin && !!messageId,
        select: (response) => response,
    });
}

export function useAdminMessageUserRating(
    messageId: string | null,
    isAdmin: boolean = false,
) {
    const pathParams = isAdmin
        ? messageId
            ? { message_id: messageId }
            : undefined
        : undefined;

    return useApiQuery({
        path: '/admin/message-ratings/{message_id}/user-ratings',
        method: 'get',
        pathParams,
        enabled: isAdmin && !!messageId,
        select: (response) => response,
    });
}

export function useStagedMessageRatings(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/message-ratings',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useStagedMessageRating(
    organizationId: string | null,
    stagedMessageId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/message-ratings/{staged_message_id}/rating',
        method: 'get',
        pathParams:
            organizationId && stagedMessageId
                ? {
                      organization_id: organizationId,
                      staged_message_id: stagedMessageId,
                  }
                : undefined,
        enabled: !!organizationId && !!stagedMessageId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useStagedMessageUserRatings(
    organizationId: string | null,
    stagedMessageId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/message-ratings/{staged_message_id}/user-ratings',
        method: 'get',
        pathParams:
            organizationId && stagedMessageId
                ? {
                      organization_id: organizationId,
                      staged_message_id: stagedMessageId,
                  }
                : undefined,
        enabled: !!organizationId && !!stagedMessageId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useOutreachSmsPreview(
    organizationId: string | null,
    outreachId: string | null,
    traceParams: TraceParams,
    mode?: Mode,
    options: { readonly enabled?: boolean } = {},
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/joined/{outreach_id}/sms-preview',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? { organization_id: organizationId, outreach_id: outreachId }
                : undefined,
        enabled: (options.enabled ?? true) && !!organizationId && !!outreachId,
        select: (response) => response,
        reason: traceParams.reason,
        mode,
    });
}

export function useOutreachTemplates(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/templates',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.templates,
        reason: traceParams.reason,
    });
}

export function useOrganizationTemplates(
    organizationId: string | null,
    traceParams: TraceParams,
    types?: Array<
        | 'agent_instructions'
        | 'callout'
        | 'callout_multi_shift'
        | 'callout_multi_shift_item'
        | 'caregiver_birthday'
        | 'caregiver_clean_record'
        | 'caregiver_client_milestone'
        | 'caregiver_monthly_check_in'
        | 'completion_cancelled'
        | 'completion_filled'
        | 'completion_no_longer_available'
        | 'monthly_caregiver_stats'
        | 'shift_confirmation_needs_review_reply'
    >,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/templates',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: types ? { types } : undefined,
        enabled: !!organizationId,
        select: (response) => response.templates,
        reason: traceParams.reason,
    });
}

export type BlockedCaregiverInfo = {
    readonly caregiver_id: string;
    readonly phone_number: string;
    readonly error_code: string;
    readonly provider: string;
};

type BlockedCaregiversResponse = ApiResponseType<
    '/organizations/{organization_id}/blocked-phones/blocked-caregivers',
    'post'
>;

export function useBlockedCaregivers(
    organizationId: string | null,
    caregiverIds: Array<string> | null,
) {
    const apiClient = getApiClient();

    const key =
        organizationId && caregiverIds && caregiverIds.length > 0
            ? ['blocked-caregivers', organizationId, caregiverIds.join(',')]
            : null;

    const fetchBlockedCaregivers = useCallback(async () => {
        if (!organizationId || !caregiverIds || caregiverIds.length === 0) {
            return { blocked_caregivers: [] };
        }

        return apiClient.post({
            url: '/organizations/{organization_id}/blocked-phones/blocked-caregivers',
            pathParams: { organization_id: organizationId },
            body: { caregiver_ids: caregiverIds },
        });
    }, [apiClient, organizationId, caregiverIds]);

    const { data, error, isValidating, mutate } = useSWR<
        BlockedCaregiversResponse,
        ApiError
    >(key, fetchBlockedCaregivers, { revalidateOnFocus: false });

    const isLoading =
        !!organizationId &&
        !!caregiverIds &&
        caregiverIds.length > 0 &&
        !data &&
        !error;

    return {
        data: data?.blocked_caregivers ?? [],
        error,
        isLoading,
        isValidating,
        mutate,
    };
}

export function useOrganizationCareCoordinators(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { includeInactive?: boolean },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/care-coordinators',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            options?.includeInactive !== undefined
                ? { include_inactive: options.includeInactive }
                : undefined,
        enabled: !!organizationId,
        select: (response) => response.care_coordinators,
        reason: traceParams.reason,
    });
}

export function useCallRoutingCareCoordinators(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/call-routing-care-coordinators',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.care_coordinators,
        reason: traceParams.reason,
    });
}

export function useMyCareCoordinator(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/care-coordinators/me',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.care_coordinator,
        reason: traceParams.reason,
    });
}

export function useClientCareCoordinators(
    organizationId: string | null,
    clientId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}/care-coordinators',
        method: 'get',
        pathParams:
            organizationId && clientId
                ? { organization_id: organizationId, client_id: clientId }
                : undefined,
        enabled: !!organizationId && !!clientId,
        select: (response) => response.care_coordinators,
        reason: traceParams.reason,
    });
}

export function useCareCoordinatorLinkSuggestions(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/care-coordinators/link-suggestions',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.suggestions,
        reason: traceParams.reason,
    });
}

export function useOrganizationClients(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { includeInactive?: boolean; clientIds?: Array<string> },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            include_inactive: options?.includeInactive,
            client_ids: options?.clientIds,
        },
        enabled:
            !!organizationId &&
            (options?.clientIds === undefined || options.clientIds.length > 0),
        select: (response) => response.clients,
        reason: traceParams.reason,
    });
}

// Single-client detail — unlike the list endpoint this hydrates
// per-tag overrides (override + effective importance) for the chips.
export function useOrganizationClient(
    organizationId: string | null,
    clientId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/clients/{client_id}',
        method: 'get',
        pathParams:
            organizationId && clientId
                ? { organization_id: organizationId, client_id: clientId }
                : undefined,
        enabled: !!organizationId && !!clientId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

export function useVoiceCallFilterParticipants(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-calls/filter-participants',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
    });
}

const DEFAULT_CLIENTS_PAGE_SIZE = 50;

export function useInfiniteOrganizationClients(
    organizationId: string | null,
    traceParams: TraceParams,
    search?: string,
    options?: { pageSize?: number; includeInactive?: boolean },
) {
    const pageSize = options?.pageSize ?? DEFAULT_CLIENTS_PAGE_SIZE;
    const [size, setSize] = useState(1);

    const {
        data: clients,
        error,
        isLoading,
        isValidating,
        mutate,
    } = useOrganizationClients(organizationId, traceParams, {
        includeInactive: options?.includeInactive,
    });

    const filteredClients = useMemo(() => {
        if (!clients) {
            return [];
        }
        if (!search) {
            return clients;
        }
        const normalized = search.toLowerCase();
        return clients.filter((client) => {
            const first = client.first_name ?? '';
            const last = client.last_name ?? '';
            const preferred = client.preferred_name ?? '';
            const fullName = `${first} ${last} ${preferred}`.toLowerCase();
            return fullName.includes(normalized);
        });
    }, [clients, search]);

    const pagedClients = useMemo(
        () => filteredClients.slice(0, size * pageSize),
        [filteredClients, size, pageSize],
    );

    const isLoadingMore = isLoading;
    const isReachingEnd =
        !clients || pagedClients.length >= filteredClients.length;

    return {
        clients: pagedClients,
        error,
        isLoading,
        isValidating,
        isLoadingMore,
        isReachingEnd,
        size,
        setSize,
        mutate,
    };
}

type BatchRecordingUrlsResponse = ApiResponseType<
    '/organizations/{organization_id}/voice-calls/recording-urls',
    'post'
>;

const RECORDING_URLS_BATCH_SIZE = 50;

export function useVoiceCallRecordingUrls(
    organizationId: string | null,
    voiceCallIds: Array<string> | null,
) {
    const apiClient = getApiClient();

    const key =
        organizationId && voiceCallIds && voiceCallIds.length > 0
            ? [
                  'voice-call-recording-urls',
                  organizationId,
                  voiceCallIds.join(','),
              ]
            : null;

    const fetchRecordingUrls = useCallback(async () => {
        if (!organizationId || !voiceCallIds || voiceCallIds.length === 0) {
            return { recording_urls: {} };
        }

        // Split into batches of 50
        const batches: Array<Array<string>> = [];
        for (
            let i = 0;
            i < voiceCallIds.length;
            i += RECORDING_URLS_BATCH_SIZE
        ) {
            batches.push(voiceCallIds.slice(i, i + RECORDING_URLS_BATCH_SIZE));
        }

        // Fetch all batches in parallel
        const results = await Promise.all(
            batches.map((batch) =>
                apiClient.post({
                    url: '/organizations/{organization_id}/voice-calls/recording-urls',
                    pathParams: { organization_id: organizationId },
                    body: { voice_call_ids: batch },
                }),
            ),
        );

        // Merge all results into a single object
        const mergedUrls: Record<string, string> = {};
        for (const result of results) {
            Object.assign(mergedUrls, result.recording_urls);
        }

        return { recording_urls: mergedUrls };
    }, [apiClient, organizationId, voiceCallIds]);

    const { data, error, isValidating, mutate } = useSWR<
        BatchRecordingUrlsResponse,
        ApiError
    >(key, fetchRecordingUrls, { revalidateOnFocus: false });

    const isLoading =
        !!organizationId &&
        !!voiceCallIds &&
        voiceCallIds.length > 0 &&
        !data &&
        !error;

    return {
        data: data?.recording_urls ?? {},
        error,
        isLoading,
        isValidating,
        mutate,
    };
}

export function useShiftSearchFilterValues(
    organizationId: string | null,
    traceParams: TraceParams,
    options?: { clientIds?: Array<string> },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/shifts/search/filters',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            options?.clientIds && options.clientIds.length > 0
                ? { client_ids: options.clientIds }
                : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: traceParams.reason,
        revalidateOnFocus: false,
    });
}

export type CompletionTemplateType =
    | 'completion_cancelled'
    | 'completion_filled'
    | 'completion_no_longer_available';

const COMPLETION_TEMPLATE_TYPES: Array<CompletionTemplateType> = [
    'completion_cancelled',
    'completion_filled',
    'completion_no_longer_available',
];

export function useCompletionTemplates(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useOrganizationTemplates(
        organizationId,
        traceParams,
        COMPLETION_TEMPLATE_TYPES,
    );
}

// SMS Engine Runs hooks

type SmsEngineRunsQueryParams = ApiQueryParamsType<
    '/admin/sms-engine-runs',
    'get'
>;

export function useSmsEngineRuns(
    params?: SmsEngineRunsQueryParams,
    options?: { enabled?: boolean; keepPreviousData?: boolean },
) {
    return useApiQuery({
        path: '/admin/sms-engine-runs',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: options?.keepPreviousData ?? true,
    });
}

export function useSmsEngineRun(runId: string | null) {
    return useApiQuery({
        path: '/admin/sms-engine-runs/{run_id}',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: !!runId,
        select: (response) => response,
    });
}

type SmsEngineRunStatsQueryParams = ApiQueryParamsType<
    '/admin/sms-engine-runs/stats',
    'get'
>;

export function useSmsEngineRunStats(
    params?: SmsEngineRunStatsQueryParams,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/sms-engine-runs/stats',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
    });
}

// Voice QA hooks

export function useVoiceLatency(
    options: { readonly refreshIntervalMs?: number } = {},
) {
    return useApiQuery({
        path: '/admin/voice-latency',
        method: 'get',
        select: (response) => response,
        refreshInterval: options.refreshIntervalMs,
    });
}

type VoiceQAQueryParams = ApiQueryParamsType<'/admin/voice-qa', 'get'>;
type VoiceQAStatsQueryParams = ApiQueryParamsType<
    '/admin/voice-qa/stats',
    'get'
>;

export function useVoiceQACalls(
    params?: VoiceQAQueryParams,
    options?: {
        enabled?: boolean;
        keepPreviousData?: boolean;
        refreshIntervalMs?: number;
    },
) {
    return useApiQuery({
        path: '/admin/voice-qa',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: options?.keepPreviousData ?? false,
        refreshInterval: options?.refreshIntervalMs,
    });
}

export function useVoiceQAStats(
    params: VoiceQAStatsQueryParams,
    options?: { readonly enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-qa/stats',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
    });
}

/**
 * How often the admin QA workload panel re-reads the pending backlog.
 *
 * The backlog moves on the scale of a dispatcher tick and a critic call, not
 * a page render, so a minute is frequent enough to watch an incident and slow
 * enough that an open panel does not become a load source of its own.
 */
export const VOICE_QA_WORKLOAD_HEALTH_REFRESH_INTERVAL_MS = 60_000;

export function useVoiceQAWorkloadHealth(options?: {
    readonly enabled?: boolean;
}) {
    return useApiQuery({
        path: '/admin/voice-qa/workload-health',
        method: 'get',
        enabled: options?.enabled ?? true,
        select: (response) => response,
        refreshInterval: VOICE_QA_WORKLOAD_HEALTH_REFRESH_INTERVAL_MS,
        keepPreviousData: true,
    });
}

export function useVoiceQATaxonomy() {
    return useApiQuery({
        path: '/admin/voice-qa/taxonomy',
        method: 'get',
        select: (response) => response,
    });
}

export function useVoiceQACall(callId: string | null) {
    return useApiQuery({
        path: '/admin/voice-qa/{call_id}',
        method: 'get',
        pathParams: callId ? { call_id: callId } : undefined,
        enabled: !!callId,
        select: (response) => response,
    });
}

export function useVoiceQALogfireTimeline(
    callId: string | null,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-qa/{call_id}/developer-timeline',
        method: 'get',
        pathParams: callId ? { call_id: callId } : undefined,
        enabled: !!callId && (options?.enabled ?? true),
        select: (response) => response,
    });
}

export function useVoiceQABraintrustTrace(
    callId: string | null,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-qa/{call_id}/braintrust-trace',
        method: 'get',
        pathParams: callId ? { call_id: callId } : undefined,
        enabled: !!callId && (options?.enabled ?? true),
        select: (response) => response,
    });
}

export function useVoiceQABraintrustSpan(
    callId: string | null,
    rowId: string | null,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-qa/{call_id}/braintrust-trace/spans/{row_id}',
        method: 'get',
        pathParams:
            callId && rowId ? { call_id: callId, row_id: rowId } : undefined,
        enabled: !!callId && !!rowId && (options?.enabled ?? true),
        select: (response) => response,
    });
}

type VoiceQAShadowStatsQueryParams = ApiQueryParamsType<
    '/admin/voice-qa/shadow-comparison/stats',
    'get'
>;

export function useVoiceQAShadowStats(
    params?: VoiceQAShadowStatsQueryParams,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-qa/shadow-comparison/stats',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
    });
}

type VoiceQAShadowComparisonQueryParams = ApiQueryParamsType<
    '/admin/voice-qa/shadow-comparison',
    'get'
>;

export function useVoiceQAShadowComparison(
    params?: VoiceQAShadowComparisonQueryParams,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-qa/shadow-comparison',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
    });
}

// Voice eval explorer hooks

type VoiceEvalRunsQueryParams = ApiQueryParamsType<
    '/admin/voice-evals/runs',
    'get'
>;

export function useVoiceEvalRuns(
    params?: VoiceEvalRunsQueryParams,
    options?: { enabled?: boolean },
) {
    return useApiQuery({
        path: '/admin/voice-evals/runs',
        method: 'get',
        queryParams: params,
        enabled: options?.enabled ?? true,
        select: (response) => response,
        keepPreviousData: true,
    });
}

type VoiceEvalRunStatsQueryParams = ApiQueryParamsType<
    '/admin/voice-evals/runs/stats',
    'get'
>;

export function useVoiceEvalRunStats(params?: VoiceEvalRunStatsQueryParams) {
    return useApiQuery({
        path: '/admin/voice-evals/runs/stats',
        method: 'get',
        queryParams: params,
        select: (response) => response,
        keepPreviousData: true,
    });
}

type VoiceEvalTrendsQueryParams = ApiQueryParamsType<
    '/admin/voice-evals/trends',
    'get'
>;

export function useVoiceEvalTrends(params?: VoiceEvalTrendsQueryParams) {
    return useApiQuery({
        path: '/admin/voice-evals/trends',
        method: 'get',
        queryParams: params,
        select: (response) => response,
    });
}

export function useVoiceEvalRun(
    runId: string | null,
    options?: {
        refreshInterval?: number;
        pollUntilTerminal?: boolean;
    },
) {
    return useApiQuery({
        path: '/admin/voice-evals/runs/{run_id}',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: !!runId,
        select: (response) => response,
        refreshInterval: options?.pollUntilTerminal
            ? (latestData) =>
                  latestData &&
                  NONTERMINAL_EXECUTION_STATUSES.has(
                      latestData.run.execution_status,
                  )
                      ? (options.refreshInterval ?? 5_000)
                      : 0
            : options?.refreshInterval,
    });
}

export function useVoiceEvalCatalog() {
    return useApiQuery({
        path: '/admin/voice-evals/catalog',
        method: 'get',
        select: (response) => response,
    });
}

export function useVoiceEvalCaseHistory(registryId: string | null) {
    return useApiQuery({
        path: '/admin/voice-evals/case-history',
        method: 'get',
        queryParams: registryId ? { registry_id: registryId } : undefined,
        enabled: !!registryId,
        select: (response) => response,
    });
}

export function useVoiceEvalRunComparison(
    baseRunId: string | null,
    candidateRunId: string | null,
) {
    return useApiQuery({
        path: '/admin/voice-evals/compare',
        method: 'get',
        queryParams:
            baseRunId && candidateRunId
                ? {
                      base_run_id: baseRunId,
                      candidate_run_id: candidateRunId,
                  }
                : undefined,
        enabled: !!baseRunId && !!candidateRunId,
        select: (response) => response,
    });
}

export function useVoiceEvalCaseComparison(
    baseRunId: string | null,
    candidateRunId: string | null,
    registryId: string | null,
) {
    return useApiQuery({
        path: '/admin/voice-evals/compare/case',
        method: 'get',
        queryParams:
            baseRunId && candidateRunId && registryId
                ? {
                      base_run_id: baseRunId,
                      candidate_run_id: candidateRunId,
                      registry_id: registryId,
                  }
                : undefined,
        enabled: !!baseRunId && !!candidateRunId && !!registryId,
        select: (response) => response,
    });
}

type VoiceEvalBaselineComparisonParams = {
    readonly window_days?: number;
};

export function useVoiceEvalBaselineComparison(
    candidateRunId: string | null,
    params?: VoiceEvalBaselineComparisonParams,
) {
    return useApiQuery({
        path: '/admin/voice-evals/compare-to-baseline',
        method: 'get',
        queryParams: candidateRunId
            ? {
                  candidate_run_id: candidateRunId,
                  ...params,
              }
            : undefined,
        enabled: !!candidateRunId,
        select: (response) => response,
    });
}

export function useVoiceEvalCaseResult(
    runId: string | null,
    caseResultId: string | null,
) {
    return useApiQuery({
        path: '/admin/voice-evals/runs/{run_id}/cases/{case_result_id}',
        method: 'get',
        pathParams:
            runId && caseResultId
                ? { run_id: runId, case_result_id: caseResultId }
                : undefined,
        enabled: !!runId && !!caseResultId,
        select: (response) => response,
    });
}

export function useAdminVoiceCallRecordingUrls(
    voiceCallIds: Array<string> | null,
    options: {
        readonly refreshInterval?: number;
        readonly pollUntilResolved?: boolean;
    } = {},
) {
    const apiClient = getApiClient();

    const key =
        voiceCallIds && voiceCallIds.length > 0
            ? ['admin-voice-call-recording-urls', voiceCallIds.join(',')]
            : null;

    const fetchRecordingUrls = useCallback(async () => {
        if (!voiceCallIds || voiceCallIds.length === 0) {
            return { recording_urls: {} };
        }

        const batches: Array<Array<string>> = [];
        for (
            let i = 0;
            i < voiceCallIds.length;
            i += RECORDING_URLS_BATCH_SIZE
        ) {
            batches.push(voiceCallIds.slice(i, i + RECORDING_URLS_BATCH_SIZE));
        }

        const results = await Promise.all(
            batches.map((batch) =>
                apiClient.post({
                    url: '/admin/voice-qa/recording-urls',
                    body: { voice_call_ids: batch },
                }),
            ),
        );

        const mergedUrls: Record<string, string> = {};
        for (const result of results) {
            Object.assign(mergedUrls, result.recording_urls);
        }

        return { recording_urls: mergedUrls };
    }, [apiClient, voiceCallIds]);

    const { data, error, isValidating, mutate } = useSWR<
        BatchRecordingUrlsResponse,
        ApiError
    >(key, fetchRecordingUrls, {
        revalidateOnFocus: false,
        refreshInterval: options.pollUntilResolved
            ? (latestData?: BatchRecordingUrlsResponse) => {
                  if (!voiceCallIds || voiceCallIds.length === 0) {
                      return 0;
                  }

                  const recordingUrls = latestData?.recording_urls ?? {};
                  return voiceCallIds.every(
                      (voiceCallId) => !!recordingUrls[voiceCallId],
                  )
                      ? 0
                      : (options.refreshInterval ?? 5000);
              }
            : options.refreshInterval,
    });

    const isLoading =
        !!voiceCallIds && voiceCallIds.length > 0 && !data && !error;

    return {
        data: data?.recording_urls ?? {},
        error,
        isLoading,
        isValidating,
        mutate,
    };
}

type SmsEngineRunsByMessageBody = ApiBodyType<
    '/admin/sms-engine-runs/by-message',
    'post'
>;

type SmsEngineRunByMessageResponse =
    components['schemas']['SmsEngineRunByMessageResponse'];

export function useSmsEngineRunsByMessageIds(
    messageIds: Array<string> | null,
    enabled: boolean,
    reason: string,
) {
    const normalizedMessageIds = useMemo(() => {
        if (!messageIds || messageIds.length === 0) {
            return [];
        }
        return Array.from(new Set(messageIds)).sort();
    }, [messageIds]);

    const shouldFetch = enabled && normalizedMessageIds.length > 0;

    const body: SmsEngineRunsByMessageBody | undefined = shouldFetch
        ? {
              message_ids: normalizedMessageIds,
          }
        : undefined;

    const { data, error, isLoading, isValidating, mutate } = useApiQuery({
        path: '/admin/sms-engine-runs/by-message',
        method: 'post',
        idempotent: true,
        body,
        enabled: shouldFetch,
        reason,
        select: (response: SmsEngineRunByMessageResponse) => response,
        keepPreviousData: true,
        revalidateOnFocus: false,
    });

    const runIdByMessageId = useMemo(() => {
        const map: Record<string, string> = {};
        data?.items.forEach((item) => {
            map[item.message_id] = item.run_id;
        });
        return map;
    }, [data?.items]);

    return { runIdByMessageId, error, isLoading, isValidating, mutate };
}

/**
 * Resolve the single ephemeral caregiver-agent run backing a conversation.
 *
 * The caregiver agent keeps one run per conversation, so this is a
 * conversation-level link rather than a per-message one.
 */
export function useCaregiverAgentRunForConversation(
    conversationId: string | null,
    enabled: boolean,
) {
    const shouldFetch = enabled && Boolean(conversationId);

    const { data, error, isLoading } = useApiQuery({
        path: '/admin/sms-engine-runs/caregiver-agent-run/by-conversation',
        method: 'get',
        queryParams: conversationId
            ? { conversation_id: conversationId }
            : undefined,
        enabled: shouldFetch,
        reason: 'caregiver-agent-run-for-conversation',
        select: (response) => response,
        revalidateOnFocus: false,
    });

    return { caregiverAgentRunId: data?.run_id ?? null, error, isLoading };
}

// Admin agent hooks

type AgentTracesQueryParams = ApiQueryParamsType<'/admin/agent-traces', 'get'>;
type AgentTraceRunsQueryParams = ApiQueryParamsType<
    '/admin/agent-traces/runs',
    'get'
>;
type AgentTraceRunsQuery = NonNullable<AgentTraceRunsQueryParams>;
export type AdminAgentTraceRunsParams = {
    readonly organization_id?: AgentTraceRunsQuery['organization_id'];
    readonly organization_ids?: AgentTraceRunsQuery['organization_ids'];
    readonly exclude_organization_ids?: AgentTraceRunsQuery['exclude_organization_ids'];
    readonly statuses?: AgentTraceRunsQuery['statuses'];
    readonly run_type?: AgentTraceRunsQuery['run_type'];
    readonly created_by_user_id?: AgentTraceRunsQuery['created_by_user_id'];
    readonly client_surface?: AgentTraceRunsQuery['client_surface'];
    readonly limit?: AgentTraceRunsQuery['limit'];
};
type AdminAgentRunsQueryParams = ApiQueryParamsType<'/admin/agent/runs', 'get'>;
type AdminAgentRunChildrenQueryParams = ApiQueryParamsType<
    '/admin/agent/runs/{run_id}/children',
    'get'
>;
type AdminAgentMissingToolsQueryParams = ApiQueryParamsType<
    '/admin/agent/missing-tools',
    'get'
>;
type AdminAgentToolTelemetryQueryParams = ApiQueryParamsType<
    '/admin/agent/tool-telemetry',
    'get'
>;
type AdminSavedInvestigationsQueryParams = ApiQueryParamsType<
    '/admin/agent/saved-investigations',
    'get'
>;
type AgentTraceAnnotationGroupsQueryParams = ApiQueryParamsType<
    '/admin/agent-traces/annotation-groups',
    'get'
>;
type AgentTraceAnnotationTracesQueryParams = ApiQueryParamsType<
    '/admin/agent-traces/annotation-traces',
    'get'
>;

export function useAdminAgentRuns(
    params: AdminAgentRunsQueryParams | undefined,
    enabled = true,
) {
    return useApiQuery({
        path: '/admin/agent/runs',
        method: 'get',
        queryParams: params,
        enabled: enabled && params !== undefined,
        select: (response) => response,
        keepPreviousData: true,
    });
}

export function useAdminAgentRun(
    runId: string | null,
    options?: { readonly mode?: Mode },
) {
    // Admin-scoped surfaces (the dock) pass `mode: useAdminMode()`
    // so the SWR key and outgoing X-Mode header follow the admin
    // toggle instead of the customer-app `modeStore`. Omitting the
    // option preserves the legacy default for pre-existing callers.
    return useApiQuery({
        path: '/admin/agent/runs/{run_id}',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: !!runId,
        mode: options?.mode,
        select: (response) => response,
    });
}

export function useAdminAgentRunChildren(
    parentRunId: string | null,
    {
        activity = 'all',
        cursor = null,
        enabled = true,
        limit = 20,
    }: {
        readonly activity?: NonNullable<AdminAgentRunChildrenQueryParams>['activity'];
        readonly cursor?:
            | components['schemas']['AgentTraceRunCursorRead']
            | null;
        readonly enabled?: boolean;
        readonly limit?: number;
    } = {},
) {
    const queryParams: AdminAgentRunChildrenQueryParams = {
        activity,
        limit,
        after_created_at: cursor?.created_at,
        after_run_id: cursor?.run_id,
    };

    return useApiQuery({
        path: '/admin/agent/runs/{run_id}/children',
        method: 'get',
        pathParams: parentRunId ? { run_id: parentRunId } : undefined,
        queryParams,
        enabled: enabled && !!parentRunId,
        select: (response) => response,
    });
}

export function useAdminAgentAnswerFeedback(runId: string | null) {
    return useApiQuery({
        path: '/admin/agent/runs/{run_id}/answer-feedback',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: !!runId,
        select: (response) => response,
    });
}

export function useAdminAgentRunInvestigationTasks(
    runId: string | null,
    {
        enabled = true,
    }: {
        readonly enabled?: boolean;
    } = {},
) {
    return useApiQuery({
        path: '/admin/agent/runs/{run_id}/investigation-tasks',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: enabled && !!runId,
        select: (response) => response,
    });
}

export function useAdminSavedInvestigations(
    params: AdminSavedInvestigationsQueryParams | undefined,
    enabled = true,
) {
    return useApiQuery({
        path: '/admin/agent/saved-investigations',
        method: 'get',
        queryParams: params,
        enabled: enabled && params !== undefined,
        select: (response) => response,
    });
}

export function useAdminSavedInvestigation(
    savedInvestigationId: string | null,
) {
    return useApiQuery({
        path: '/admin/agent/saved-investigations/{saved_investigation_id}',
        method: 'get',
        pathParams: savedInvestigationId
            ? { saved_investigation_id: savedInvestigationId }
            : undefined,
        enabled: !!savedInvestigationId,
        select: (response) => response,
    });
}

export function useAdminAgentRunArtifact(
    runId: string | null,
    artifactId: string | null,
) {
    return useApiQuery({
        path: '/admin/agent/runs/{run_id}/artifacts/{artifact_id}',
        method: 'get',
        pathParams:
            runId && artifactId
                ? { run_id: runId, artifact_id: artifactId }
                : undefined,
        enabled: !!runId && !!artifactId,
        select: (response) => response,
    });
}

export function useAdminAgentRunArtifacts(runId: string | null) {
    return useApiQuery({
        path: '/admin/agent/runs/{run_id}/artifacts',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: !!runId,
        select: (response) => response,
    });
}

export function useAdminAgentSourceHealth() {
    return useApiQuery({
        path: '/admin/agent/source-health',
        method: 'get',
        select: (response) => response,
        dedupingInterval: 60_000,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAdminAgentCapabilities() {
    return useApiQuery({
        path: '/admin/agent/capabilities',
        method: 'get',
        select: (response) => response,
        dedupingInterval: 60_000,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAdminAgentMissingTools(
    params?: AdminAgentMissingToolsQueryParams,
) {
    return useApiQuery({
        path: '/admin/agent/missing-tools',
        method: 'get',
        queryParams: params,
        select: (response) => response,
        keepPreviousData: true,
        dedupingInterval: 60_000,
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });
}

export function useAdminAgentToolTelemetry(
    params?: AdminAgentToolTelemetryQueryParams,
) {
    return useApiQuery({
        path: '/admin/agent/tool-telemetry',
        method: 'get',
        queryParams: params,
        select: (response) => response,
        keepPreviousData: true,
    });
}

// Agent Traces hooks

type AgentRunDurationTrendsQueryParams = ApiQueryParamsType<
    '/admin/agent-traces/duration-trends',
    'get'
>;

export function useAdminAgentRunDurationTrends(
    params?: AgentRunDurationTrendsQueryParams,
) {
    // Follow the admin toggle, not the customer app's persisted mode, so
    // admin agent-trace data is scoped consistently across the section.
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/agent-traces/duration-trends',
        method: 'get',
        queryParams: params,
        mode: adminMode,
        select: (response) => response,
        // The outreach-observability urgency selector changes this cohort.
        // Retaining the prior response would briefly mix duration data from
        // the previous cohort with the newly selected dashboard metrics.
        keepPreviousData: false,
    });
}

export function useAdminAgentTraces(
    params?: AgentTracesQueryParams,
    enabled = true,
    keepPreviousData = true,
) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/agent-traces',
        method: 'get',
        queryParams: params,
        mode: adminMode,
        enabled,
        select: (response) => response,
        keepPreviousData,
    });
}

export function useAdminAgentTraceRuns(
    params: AdminAgentTraceRunsParams = {},
    {
        cursor = null,
        enabled = true,
    }: {
        readonly cursor?: AgentTraceRunCursorRead | null;
        readonly enabled?: boolean;
    } = {},
) {
    const adminMode = useAdminMode();
    const queryParams = {
        ...params,
        ...(cursor
            ? {
                  after_created_at: cursor.created_at,
                  after_run_id: cursor.run_id,
              }
            : {}),
    } satisfies NonNullable<AgentTraceRunsQueryParams>;

    return useApiQuery({
        path: '/admin/agent-traces/runs',
        method: 'get',
        queryParams,
        mode: adminMode,
        enabled,
        select: (response) => response,
        keepPreviousData: false,
    });
}

export function useAdminAgentTraceAnnotationGroups(
    params?: AgentTraceAnnotationGroupsQueryParams,
    enabled = true,
) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/agent-traces/annotation-groups',
        method: 'get',
        queryParams: params,
        mode: adminMode,
        enabled,
        select: (response) => response,
        keepPreviousData: true,
    });
}

export function useAdminAgentTracesByAnnotation(
    params?: AgentTraceAnnotationTracesQueryParams,
    enabled = true,
    keepPreviousData = true,
) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/agent-traces/annotation-traces',
        method: 'get',
        queryParams: params,
        mode: adminMode,
        enabled,
        select: (response) => response,
        keepPreviousData,
    });
}

export function useAdminAgentTrace(conversationId: string | null) {
    // Drive the request off the admin panel's own mode toggle. Without this,
    // the request inherits the customer app's persisted mode, so a user left
    // in sandbox mode would send `X-Mode: sandbox` and RLS mode-isolation
    // would hide the live conversation row — surfacing as "Trace not found".
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/agent-traces/{conversation_id}',
        method: 'get',
        pathParams: conversationId
            ? { conversation_id: conversationId }
            : undefined,
        mode: adminMode,
        enabled: !!conversationId,
        select: (response) => response,
    });
}

// Cheap header-level trace view (PHO-14248): conversation row +
// linked run + tool-call count only. Fires first; the trace detail
// page gates the heavy `useAdminAgentTrace` read on this summary
// settling, so the header and the dock's trace chip populate without
// waiting for (or competing with) full turn/event payloads.
export function useAdminAgentTraceSummary(conversationId: string | null) {
    const adminMode = useAdminMode();

    return useApiQuery({
        path: '/admin/agent-traces/{conversation_id}/summary',
        method: 'get',
        pathParams: conversationId
            ? { conversation_id: conversationId }
            : undefined,
        mode: adminMode,
        enabled: !!conversationId,
        select: (response) => response,
    });
}

type AdminCallsQueryParams = ApiQueryParamsType<'/admin/calls', 'get'>;

export function useAdminCalls(
    params?: AdminCallsQueryParams,
    enabled = true,
    keepPreviousData = true,
) {
    return useApiQuery({
        path: '/admin/calls',
        method: 'get',
        queryParams: params,
        enabled,
        select: (response) => response,
        keepPreviousData,
    });
}

export function useAdminCall(callId: string | null) {
    return useApiQuery({
        path: '/admin/calls/{call_id}',
        method: 'get',
        pathParams: callId ? { call_id: callId } : undefined,
        enabled: !!callId,
        select: (response) => response,
    });
}

export async function dispatchAdminMeetingBot(
    meetingUrl: string,
): Promise<ApiResponseType<'/admin/calls/dispatch-bot', 'post'>> {
    return getApiClient().post({
        url: '/admin/calls/dispatch-bot',
        body: { meeting_url: meetingUrl },
    });
}

// The global call-tag vocabulary (most-used first), for tag pickers.
export function useAdminCallTagVocabulary() {
    return useApiQuery({
        path: '/admin/call-tags',
        method: 'get',
        select: (response) => response.tags,
    });
}

// Enqueue an analysis run for a call. Idempotent: the worker skips calls
// that are already analyzed or duplicates of an analyzed sibling. Unlike the
// backfill script, a button-triggered analysis is live — it posts to the
// type-routed call feed like tick-driven analyses do.
export async function analyzeAdminCall(callId: string) {
    return getApiClient().post({
        url: '/admin/calls/{call_id}/analyze',
        pathParams: { call_id: callId },
        body: { post_to_slack: true, force: false },
    });
}

export async function addAdminCallTag(
    callId: string,
    body: ApiBodyType<'/admin/calls/{call_id}/tags', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/calls/{call_id}/tags',
        pathParams: { call_id: callId },
        body,
    });
}

export async function setAdminCallTags(
    callId: string,
    body: ApiBodyType<'/admin/calls/{call_id}/tags', 'put'>,
) {
    return getApiClient().put({
        url: '/admin/calls/{call_id}/tags',
        pathParams: { call_id: callId },
        body,
    });
}

export async function removeAdminCallTag(callId: string, tagSlug: string) {
    return getApiClient().delete({
        url: '/admin/calls/{call_id}/tags/{tag_slug}',
        pathParams: { call_id: callId, tag_slug: tagSlug },
    });
}

export async function updateAgentTraceOwner(
    conversationId: string,
    body: ApiBodyType<'/admin/agent-traces/{conversation_id}/owner', 'put'>,
) {
    return getApiClient().put({
        url: '/admin/agent-traces/{conversation_id}/owner',
        pathParams: { conversation_id: conversationId },
        body,
    });
}

export async function assignAgentTraceOwners(
    body: ApiBodyType<'/admin/agent-traces/owner-assignments', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/agent-traces/owner-assignments',
        body,
    });
}

export async function assignVoiceQaOwners(
    body: ApiBodyType<'/admin/voice-qa/owner-assignments', 'post'>,
) {
    return getApiClient().post({
        url: '/admin/voice-qa/owner-assignments',
        body,
    });
}

export function useAdminAgentTraceReviewStatus(workflowId: string | null) {
    return useApiQuery({
        path: '/admin/agent-traces/review/{workflow_id}/status',
        method: 'get',
        pathParams: workflowId ? { workflow_id: workflowId } : undefined,
        enabled: !!workflowId,
        select: (response) => response,
        refreshInterval: (status) =>
            workflowId && (!status || status.status === 'running') ? 2_000 : 0,
    });
}

export function useDryRunReport(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/dry-run-report',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            start_date: startDate,
            end_date: endDate,
        },
        enabled: !!organizationId && !!startDate && !!endDate,
        select: (response) => response,
        keepPreviousData: true,
    });
}

export function useAutoDraftDryRuns(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/auto-draft-dry-runs',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

export function useAutoDraftDryRun(
    organizationId: string | null,
    runId: string | null,
    options?: {
        readonly refreshInterval?: number | ((latestData: unknown) => number);
    },
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/auto-draft-dry-runs/{run_id}',
        method: 'get',
        pathParams:
            organizationId && runId
                ? { organization_id: organizationId, run_id: runId }
                : undefined,
        enabled: !!organizationId && !!runId,
        select: (response) => response,
        refreshInterval: options?.refreshInterval,
    });
}

export function useAutoDraftPlaygroundShifts(organizationId: string | null) {
    return useApiQuery({
        path: '/organizations/{organization_id}/auto-draft-dry-runs/playground-shifts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

// PHO-9323 notification follows & outreach subscribers hooks.

export function useNotificationFollows(
    organizationId: string | null,
    reason?: string,
    memberUserId?: string,
) {
    return useApiQuery({
        ...notificationFollowsRequest(organizationId, memberUserId),
        method: 'get',
        enabled: !!organizationId,
        select: (response) => response,
        reason,
    });
}

export type EventRead = components['schemas']['EventRead'];
type EventListResponse = components['schemas']['EventListResponse'];

export function useEntityEvents(params: {
    readonly organizationId: string | null;
    readonly entityType: string | null;
    readonly entityId: string | null;
    readonly pageSize?: number;
    readonly includeRelated?: boolean;
    // Pass 'admin' from admin-panel surfaces so Phoebe staff who aren't
    // OrgMemberProfile members of the target org still get results.
    readonly scope?: 'org' | 'admin';
}) {
    const apiClient = getApiClient();
    const pageSize = params.pageSize ?? 50;
    const includeRelated = params.includeRelated ?? true;
    const scope = params.scope ?? 'org';
    const enabled =
        !!params.organizationId && !!params.entityType && !!params.entityId;

    const getKey = (
        pageIndex: number,
        previousPageData: EventListResponse | null,
    ) => {
        if (!enabled) {
            return null;
        }
        if (
            pageIndex > 0 &&
            previousPageData &&
            !previousPageData.next_cursor
        ) {
            return null;
        }
        const cursor =
            pageIndex === 0 ? null : (previousPageData?.next_cursor ?? null);
        return [
            'events',
            scope,
            params.organizationId,
            params.entityType,
            params.entityId,
            includeRelated,
            pageSize,
            cursor,
        ] as const;
    };

    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        EventListResponse,
        ApiError
    >(
        getKey,
        ([
            _url,
            requestScope,
            organizationId,
            entityType,
            entityId,
            related,
            limit,
            cursor,
        ]: [
            string,
            'org' | 'admin',
            string,
            string,
            string,
            boolean,
            number,
            string | null,
        ]) =>
            requestScope === 'admin'
                ? apiClient.get({
                      url: '/admin/organizations/{organization_id}/activity',
                      pathParams: { organization_id: organizationId },
                      queryParams: {
                          entity_type: entityType,
                          entity_id: entityId,
                          include_related: related,
                          limit,
                          ...(cursor ? { cursor } : {}),
                      },
                  })
                : apiClient.get({
                      url: '/organizations/{organization_id}/activity',
                      pathParams: { organization_id: organizationId },
                      queryParams: {
                          entity_type: entityType,
                          entity_id: entityId,
                          include_related: related,
                          limit,
                          ...(cursor ? { cursor } : {}),
                      },
                  }),
        {
            // Events are insert-only with stable (occurred_at, id) cursors,
            // so refetching each loaded page with its stored cursor is safe —
            // new events only show up on page 1, and later pages stay
            // consistent because their cursors still point at the same rows.
            revalidateFirstPage: false,
            revalidateAll: true,
        },
    );

    const events = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.flatMap((page) => page.events);
    }, [data]);

    const isLoadingInitialData = !data && !error && enabled;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const lastPage = data?.[data.length - 1];
    const hasMore = !!lastPage?.next_cursor;

    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore) {
            setSize(size + 1);
        }
    }, [hasMore, isLoadingMore, setSize, size]);

    return {
        events,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
        isValidating,
    };
}

export function useFeatureFlagLastChanges(flagKey: string | null) {
    return useApiQuery({
        path: '/admin/feature-flags/{flag_key}/last-changes',
        method: 'get',
        pathParams: flagKey ? { flag_key: flagKey } : undefined,
        enabled: !!flagKey,
        select: (response) => response,
        reason: 'admin-feature-flag-last-changes',
        dedupingInterval: 60_000,
        revalidateOnFocus: false,
    });
}

type FeatureFlagHistoryResponse =
    components['schemas']['FeatureFlagHistoryResponse'];

export function useFeatureFlagHistory(params: {
    readonly flagKey: string | null;
    readonly organizationId?: string | null;
    readonly pageSize?: number;
}) {
    const apiClient = getApiClient();
    const pageSize = params.pageSize ?? 50;
    const enabled = !!params.flagKey;

    const getKey = (
        pageIndex: number,
        previousPageData: FeatureFlagHistoryResponse | null,
    ) => {
        if (!enabled) {
            return null;
        }
        if (
            pageIndex > 0 &&
            previousPageData &&
            !previousPageData.next_cursor
        ) {
            return null;
        }
        const cursor =
            pageIndex === 0 ? null : (previousPageData?.next_cursor ?? null);
        return [
            'feature-flag-history',
            params.flagKey,
            params.organizationId ?? null,
            pageSize,
            cursor,
        ] as const;
    };

    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        FeatureFlagHistoryResponse,
        ApiError
    >(
        getKey,
        ([_url, flagKey, organizationId, limit, cursor]: [
            string,
            string,
            string | null,
            number,
            string | null,
        ]) =>
            apiClient.get({
                url: '/admin/feature-flags/{flag_key}/history',
                pathParams: { flag_key: flagKey },
                queryParams: {
                    limit,
                    ...(organizationId
                        ? { organization_id: organizationId }
                        : {}),
                    ...(cursor ? { cursor } : {}),
                },
            }),
        {
            // FF-change events are insert-only with stable (occurred_at, id)
            // cursors; new changes only appear on page 1.
            revalidateFirstPage: false,
            revalidateAll: true,
        },
    );

    const changes = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.flatMap((page) => page.changes);
    }, [data]);

    const isLoadingInitialData = !data && !error && enabled;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const lastPage = data?.[data.length - 1];
    const hasMore = !!lastPage?.next_cursor;

    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore) {
            setSize(size + 1);
        }
    }, [hasMore, isLoadingMore, setSize, size]);

    return {
        changes,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        hasMore,
        loadMore,
        mutate,
        isValidating,
    };
}

export function useOutreachSubscribers(
    organizationId: string | null,
    outreachId: string | null,
    reason?: string,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/outreaches/{outreach_id}/subscribers',
        method: 'get',
        pathParams:
            organizationId && outreachId
                ? {
                      organization_id: organizationId,
                      outreach_id: outreachId,
                  }
                : undefined,
        enabled: !!organizationId && !!outreachId,
        select: (response) => response,
        reason,
    });
}

const ACTIVITY_FEED_COUNTS_REFRESH_INTERVAL_MS = 30_000;
type ActivityEventCountsOptions = {
    readonly refreshIntervalMs?: number | null;
    readonly revalidateOnFocus?: boolean;
    readonly createdAfter?: string;
    readonly createdBefore?: string;
};

type ActivityFeedView =
    | 'open'
    | 'addressed'
    | 'unread_for_me'
    | 'action_needed'
    | 'all_history';
type ActivityFeedKindCountsView = ActivityFeedView;
const ACTIVITY_EVENTS_PATH = '/organizations/{organization_id}/activity-events';
type ActivityEventsResponse = ApiResponseType<
    typeof ACTIVITY_EVENTS_PATH,
    'get'
>;
type ActivityEventsQueryParams = ApiQueryParamsType<
    typeof ACTIVITY_EVENTS_PATH,
    'get'
>;

// User-selectable sort orders for the activity feed. Mirrors the API's
// `sort` query param. `priority` is the historical default.
export type ActivityFeedSort = 'priority' | 'recent' | 'kind';

// Priority levels, low to high. Mirrors the API's `min_priority` query
// param used to keep the feed to higher-signal events.
export type ActivityFeedPriority = 'low' | 'normal' | 'high' | 'urgent';
export type ActivityVoiceCallFilter =
    | 'inbound'
    | 'outbound'
    | 'transferred'
    | 'ringcentral'
    | 'goto'
    | 'voicemail'
    | 'ringcentral_voicemail'
    | 'goto_voicemail'
    | 'client'
    | 'caregiver';
export type ActivityTopicFilter =
    | 'open_shifts'
    | 'clock_in_out'
    | 'messages'
    | 'care_or_client'
    | 'schedule_or_availability'
    | 'human_request'
    | 'wrong_contact_or_noise'
    | 'other';

type ActivityEventsQueryOptions = {
    readonly view: ActivityFeedView;
    readonly kinds?: ReadonlyArray<string>;
    readonly sort?: ActivityFeedSort;
    readonly minPriority?: ActivityFeedPriority;
    readonly createdAfter?: string;
    readonly createdBefore?: string;
    readonly actionRequired?: boolean;
    readonly voiceCallFilters?: ReadonlyArray<ActivityVoiceCallFilter>;
    readonly topics?: ReadonlyArray<ActivityTopicFilter>;
    readonly transferDestinationIds?: ReadonlyArray<string>;
    readonly officeIds?: ReadonlyArray<string>;
    readonly organizationFilterIds?: ReadonlyArray<string>;
    readonly careCoordinatorIds?: ReadonlyArray<string>;
    readonly clientTags?: ReadonlyArray<string>;
    readonly excludedClientTags?: ReadonlyArray<string>;
    readonly assignedToUserIds?: ReadonlyArray<string>;
    readonly unassigned?: boolean;
    readonly search?: string;
    readonly cursor?: string;
    readonly limit?: number;
};

function buildActivityEventsQueryParams(
    options: ActivityEventsQueryOptions,
): ActivityEventsQueryParams {
    return {
        view: options.view,
        ...(options.kinds && options.kinds.length > 0
            ? { kinds: [...options.kinds] }
            : {}),
        ...(options.sort ? { sort: options.sort } : {}),
        ...(options.minPriority ? { min_priority: options.minPriority } : {}),
        ...(options.createdAfter
            ? { created_after: options.createdAfter }
            : {}),
        ...(options.actionRequired !== undefined
            ? { action_required: options.actionRequired }
            : {}),
        ...(options.voiceCallFilters && options.voiceCallFilters.length > 0
            ? { voice_call_filter: [...options.voiceCallFilters] }
            : {}),
        ...(options.topics && options.topics.length > 0
            ? { topic: [...options.topics] }
            : {}),
        ...(options.transferDestinationIds &&
        options.transferDestinationIds.length > 0
            ? { transfer_destination_id: [...options.transferDestinationIds] }
            : {}),
        ...(options.officeIds && options.officeIds.length > 0
            ? { office_id: [...options.officeIds] }
            : {}),
        ...(options.organizationFilterIds &&
        options.organizationFilterIds.length > 0
            ? { organization_filter_id: [...options.organizationFilterIds] }
            : {}),
        ...(options.careCoordinatorIds && options.careCoordinatorIds.length > 0
            ? { care_coordinator_id: [...options.careCoordinatorIds] }
            : {}),
        ...(options.clientTags && options.clientTags.length > 0
            ? { client_tags: [...options.clientTags] }
            : {}),
        ...(options.excludedClientTags && options.excludedClientTags.length > 0
            ? { excluded_client_tags: [...options.excludedClientTags] }
            : {}),
        ...(options.assignedToUserIds && options.assignedToUserIds.length > 0
            ? { assigned_to_user_id: [...options.assignedToUserIds] }
            : {}),
        ...(options.unassigned ? { unassigned: true } : {}),
        ...(options.createdBefore
            ? { created_before: options.createdBefore }
            : {}),
        ...(options.search ? { search: options.search } : {}),
        ...(options.cursor ? { cursor: options.cursor } : {}),
        ...(options.limit !== undefined ? { limit: options.limit } : {}),
    };
}

export function buildActivityEventsExportUrl(
    organizationId: string,
    options: Omit<ActivityEventsQueryOptions, 'cursor' | 'limit'>,
): string {
    const params = buildActivityEventsQueryParams(options);
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (Array.isArray(value)) {
            for (const item of value) {
                search.append(key, String(item));
            }
        } else if (value !== undefined) {
            search.append(key, String(value));
        }
    }
    const url = new URL(
        `/organizations/${organizationId}/activity-events/export`,
        getRuntimeConfig().apiUrl,
    );
    url.search = search.toString();
    return url.toString();
}

export function useActivityEvents(
    organizationId: string | null,
    options: ActivityEventsQueryOptions,
) {
    return useApiQuery({
        path: ACTIVITY_EVENTS_PATH,
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: buildActivityEventsQueryParams(options),
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'activity-events',
        // Intentionally not using keepPreviousData: when the user
        // switches view (open -> addressed -> unread_for_me) the SWR
        // key changes, and showing the previous view's rows under the
        // new tab while the fetch resolves is semantically wrong (open
        // items appearing under "Recently addressed").
    });
}

export function useInfiniteActivityEvents(
    organizationId: string | null,
    options: Omit<ActivityEventsQueryOptions, 'cursor' | 'limit'> & {
        readonly initialLimit: number;
        readonly pageSize: number;
    },
) {
    const apiClient = getApiClient();
    const mode = useMode();
    const { initialLimit, pageSize, ...activityEventsOptions } = options;
    const optionsKey = JSON.stringify(activityEventsOptions);

    const getKey = (
        pageIndex: number,
        previousPageData: ActivityEventsResponse | null,
    ) => {
        if (!organizationId) {
            return null;
        }
        if (
            pageIndex > 0 &&
            previousPageData &&
            !previousPageData.next_cursor
        ) {
            return null;
        }
        const cursor =
            pageIndex === 0 ? null : (previousPageData?.next_cursor ?? null);
        const limit = pageIndex === 0 ? initialLimit : pageSize;
        return [
            'infinite-activity-events',
            organizationId,
            optionsKey,
            cursor,
            limit,
            mode ?? null,
        ] as const;
    };

    const { data, error, size, setSize, mutate, isValidating } = useSWRInfinite<
        ActivityEventsResponse,
        ApiError
    >(
        getKey,
        ([_key, orgId, _optionsKey, cursor, limit]: [
            string,
            string,
            string,
            string | null,
            number,
            Mode | null,
        ]) => {
            return apiClient.get({
                url: ACTIVITY_EVENTS_PATH,
                pathParams: { organization_id: orgId },
                queryParams: buildActivityEventsQueryParams({
                    ...activityEventsOptions,
                    ...(cursor ? { cursor } : {}),
                    limit,
                }),
                reason: 'activity-events',
            });
        },
        {
            // The activity inbox often remounts after a row was changed from
            // another surface, such as the Home card. Revalidate the first page
            // so cached "needs attention" rows cannot linger after mark-done.
            revalidateFirstPage: true,
            revalidateAll: false,
        },
    );
    // No setSize(1) reset on key change: SWR Infinite already resets the page
    // size when the page-1 key changes (persistSize defaults to false), and
    // setSize always triggers an extra revalidation — with the initial fetch
    // that double-fetched page 1 on every mount and filter change.

    const events = useMemo(() => {
        if (!data) {
            return [];
        }
        return data.flatMap((page) => page.items);
    }, [data]);

    const isLoadingInitialData = !data && !error && !!organizationId;
    const isLoadingMore =
        isLoadingInitialData ||
        (size > 0 && !!data && typeof data[size - 1] === 'undefined');
    const lastPage = data?.[data.length - 1];
    const hasMore = !!lastPage?.next_cursor;

    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore) {
            setSize((currentSize) => currentSize + 1);
        }
    }, [hasMore, isLoadingMore, setSize]);

    return {
        events,
        error,
        isLoading: isLoadingInitialData,
        isLoadingMore,
        isValidating,
        hasMore,
        loadMore,
        mutate,
    };
}

export function useActivityEvent(
    organizationId: string | null,
    eventId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/activity-events/{event_id}',
        method: 'get',
        pathParams:
            organizationId && eventId
                ? { organization_id: organizationId, event_id: eventId }
                : undefined,
        enabled: !!organizationId && !!eventId,
        select: (response) => response,
        reason: 'activity-event',
    });
}

// Resolves the organization (and mode) that owns an activity event, scoped to
// the viewer's memberships. Used to widen the Activity multi-org filter scope
// when a deep link points at an event in an organization not currently shown.
export function useActivityEventOrganizationContext(eventId: string | null) {
    return useApiQuery({
        path: '/users/me/resource-context/{resource_type}/{resource_id}',
        method: 'get',
        pathParams: eventId
            ? { resource_type: 'activity_event', resource_id: eventId }
            : undefined,
        enabled: !!eventId,
        select: (response) => response,
        reason: 'activity-event-organization-context',
    });
}

export function useActivityEventKindCounts(
    organizationId: string | null,
    view: ActivityFeedKindCountsView,
    minPriority?: ActivityFeedPriority,
    officeIds?: ReadonlyArray<string>,
    actionRequired?: boolean,
    organizationFilterIds?: ReadonlyArray<string>,
    careCoordinatorIds?: ReadonlyArray<string>,
    createdAfter?: string,
    createdBefore?: string,
    assignedToUserIds?: ReadonlyArray<string>,
    unassigned?: boolean,
    clientTags?: ReadonlyArray<string>,
    excludedClientTags?: ReadonlyArray<string>,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/activity-events/kind-counts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            view,
            ...(minPriority ? { min_priority: minPriority } : {}),
            ...(officeIds && officeIds.length > 0
                ? { office_id: [...officeIds] }
                : {}),
            ...(organizationFilterIds && organizationFilterIds.length > 0
                ? { organization_filter_id: [...organizationFilterIds] }
                : {}),
            ...(careCoordinatorIds && careCoordinatorIds.length > 0
                ? { care_coordinator_id: [...careCoordinatorIds] }
                : {}),
            ...(assignedToUserIds && assignedToUserIds.length > 0
                ? { assigned_to_user_id: [...assignedToUserIds] }
                : {}),
            ...(unassigned ? { unassigned: true } : {}),
            ...(clientTags && clientTags.length > 0
                ? { client_tags: [...clientTags] }
                : {}),
            ...(excludedClientTags && excludedClientTags.length > 0
                ? { excluded_client_tags: [...excludedClientTags] }
                : {}),
            ...(actionRequired !== undefined
                ? { action_required: actionRequired }
                : {}),
            ...(createdAfter ? { created_after: createdAfter } : {}),
            ...(createdBefore ? { created_before: createdBefore } : {}),
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'activity-event-kind-counts',
    });
}

export function useActivityEventCounts(
    organizationId: string | null,
    officeIds?: ReadonlyArray<string>,
    reason = 'activity-event-counts',
    options: ActivityEventCountsOptions = {},
    organizationFilterIds?: ReadonlyArray<string>,
    careCoordinatorIds?: ReadonlyArray<string>,
    assignedToUserIds?: ReadonlyArray<string>,
    unassigned?: boolean,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/activity-events/counts',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(officeIds && officeIds.length > 0
                ? { office_id: [...officeIds] }
                : {}),
            ...(organizationFilterIds && organizationFilterIds.length > 0
                ? { organization_filter_id: [...organizationFilterIds] }
                : {}),
            ...(careCoordinatorIds && careCoordinatorIds.length > 0
                ? { care_coordinator_id: [...careCoordinatorIds] }
                : {}),
            ...(assignedToUserIds && assignedToUserIds.length > 0
                ? { assigned_to_user_id: [...assignedToUserIds] }
                : {}),
            ...(unassigned ? { unassigned: true } : {}),
            ...(options.createdAfter
                ? { created_after: options.createdAfter }
                : {}),
            ...(options.createdBefore
                ? { created_before: options.createdBefore }
                : {}),
        },
        enabled: !!organizationId,
        select: (response) => response,
        reason,
        refreshInterval:
            options.refreshIntervalMs === null
                ? undefined
                : (options.refreshIntervalMs ??
                  ACTIVITY_FEED_COUNTS_REFRESH_INTERVAL_MS),
        ...(options.revalidateOnFocus === undefined
            ? {}
            : { revalidateOnFocus: options.revalidateOnFocus }),
        withRejectingRevalidation: true,
    });
}

export function useVoiceCallNotes(
    organizationId: string | null,
    voiceCallId: string | null,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/voice-calls/{voice_call_id}/notes',
        method: 'get',
        pathParams:
            organizationId && voiceCallId
                ? {
                      organization_id: organizationId,
                      voice_call_id: voiceCallId,
                  }
                : undefined,
        enabled: !!organizationId && !!voiceCallId,
        select: (response) => response,
        reason: 'voice-call-notes',
    });
}

export async function resolveGenerationsConfirmedScheduleApproval(
    organizationId: string,
    activityEventId: string,
    body: ApiBodyType<
        '/organizations/{organization_id}/activity-events/{activity_event_id}/generations-confirmed-schedule-approval',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/activity-events/{activity_event_id}/generations-confirmed-schedule-approval',
        pathParams: {
            organization_id: organizationId,
            activity_event_id: activityEventId,
        },
        body,
        reason: 'generations-confirmed-schedule-approval',
    });
}

export async function linkVoiceCallCallerReview(
    organizationId: string,
    voiceCallId: string,
    body: ApiBodyType<
        '/organizations/{organization_id}/voice-calls/{voice_call_id}/caller-review/link',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/voice-calls/{voice_call_id}/caller-review/link',
        pathParams: {
            organization_id: organizationId,
            voice_call_id: voiceCallId,
        },
        body,
        reason: 'voice-call-caller-review-link',
    });
}

export async function dismissVoiceCallCallerReview(
    organizationId: string,
    voiceCallId: string,
    body: ApiBodyType<
        '/organizations/{organization_id}/voice-calls/{voice_call_id}/caller-review/dismiss',
        'post'
    >,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/voice-calls/{voice_call_id}/caller-review/dismiss',
        pathParams: {
            organization_id: organizationId,
            voice_call_id: voiceCallId,
        },
        body,
        reason: 'voice-call-caller-review-dismiss',
    });
}

export async function markActivityEventAddressed(
    organizationId: string,
    eventId: string,
    note?: string,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/activity-events/{event_id}/addressed',
        pathParams: {
            organization_id: organizationId,
            event_id: eventId,
        },
        body: { note: note ?? null },
        reason: 'activity-event-mark-addressed',
    });
}

const MAX_BULK_ACTIVITY_EVENT_IDS = 100;

function chunkActivityEventIds(
    eventIds: ReadonlyArray<string>,
): Array<Array<string>> {
    const uniqueEventIds = Array.from(new Set(eventIds));
    const chunks: Array<Array<string>> = [];
    for (
        let index = 0;
        index < uniqueEventIds.length;
        index += MAX_BULK_ACTIVITY_EVENT_IDS
    ) {
        chunks.push(
            uniqueEventIds.slice(index, index + MAX_BULK_ACTIVITY_EVENT_IDS),
        );
    }
    return chunks;
}

export type ActivityEventBulkUpdateResult = {
    readonly successfulEventIds: Array<string>;
    readonly failedEventIds: Array<string>;
};

export type ActivityEventOrganizationRef = {
    readonly id: string;
    readonly organization_id: string;
};

async function updateActivityEventIdChunks(
    eventIds: ReadonlyArray<string>,
    updateChunk: (eventIdChunk: Array<string>) => Promise<unknown>,
): Promise<ActivityEventBulkUpdateResult> {
    const eventIdChunks = chunkActivityEventIds(eventIds);
    const results = await Promise.allSettled(
        eventIdChunks.map((eventIdChunk) =>
            Promise.resolve().then(() => updateChunk(eventIdChunk)),
        ),
    );
    const successfulEventIds: Array<string> = [];
    const failedEventIds: Array<string> = [];
    results.forEach((result, index) => {
        const eventIdChunk = eventIdChunks[index] ?? [];
        if (result.status === 'fulfilled') {
            successfulEventIds.push(...eventIdChunk);
        } else {
            failedEventIds.push(...eventIdChunk);
        }
    });
    return { successfulEventIds, failedEventIds };
}

export async function markActivityEventsAddressed(
    organizationId: string,
    eventIds: ReadonlyArray<string>,
    note?: string,
) {
    const apiClient = getApiClient();
    return updateActivityEventIdChunks(eventIds, (eventIdChunk) =>
        apiClient.post({
            url: '/organizations/{organization_id}/activity-events/addressed',
            pathParams: { organization_id: organizationId },
            body: {
                event_ids: eventIdChunk,
                note: note ?? null,
            },
            reason: 'activity-events-mark-addressed',
        }),
    );
}

export async function markActivityEventsUnaddressed(
    organizationId: string,
    eventIds: ReadonlyArray<string>,
) {
    const apiClient = getApiClient();
    return updateActivityEventIdChunks(eventIds, (eventIdChunk) =>
        apiClient.delete({
            url: '/organizations/{organization_id}/activity-events/addressed',
            pathParams: { organization_id: organizationId },
            body: { event_ids: eventIdChunk },
            reason: 'activity-events-mark-unaddressed',
        }),
    );
}

async function updateActivityEventsByOrganization(
    events: ReadonlyArray<ActivityEventOrganizationRef>,
    updateOrganization: (
        organizationId: string,
        eventIds: ReadonlyArray<string>,
    ) => Promise<ActivityEventBulkUpdateResult>,
): Promise<ActivityEventBulkUpdateResult> {
    const uniqueEvents = Array.from(
        new Map(events.map((event) => [event.id, event])).values(),
    );
    const eventIdsByOrganization = new Map<string, Array<string>>();
    uniqueEvents.forEach((event) => {
        const organizationEventIds =
            eventIdsByOrganization.get(event.organization_id) ?? [];
        organizationEventIds.push(event.id);
        eventIdsByOrganization.set(event.organization_id, organizationEventIds);
    });
    const organizationUpdates = [...eventIdsByOrganization].map(
        ([organizationId, eventIds]) => ({
            eventIds,
            promise: updateOrganization(organizationId, eventIds),
        }),
    );
    const results = await Promise.allSettled(
        organizationUpdates.map((update) => update.promise),
    );
    const successfulEventIds = new Set<string>();
    const failedEventIds = new Set<string>();
    results.forEach((result, index) => {
        const organizationEventIds = organizationUpdates[index]?.eventIds ?? [];
        if (result.status === 'fulfilled') {
            result.value.successfulEventIds.forEach((eventId) =>
                successfulEventIds.add(eventId),
            );
            result.value.failedEventIds.forEach((eventId) =>
                failedEventIds.add(eventId),
            );
        } else {
            organizationEventIds.forEach((eventId) =>
                failedEventIds.add(eventId),
            );
        }
    });
    return {
        successfulEventIds: uniqueEvents
            .map((event) => event.id)
            .filter((eventId) => successfulEventIds.has(eventId)),
        failedEventIds: uniqueEvents
            .map((event) => event.id)
            .filter((eventId) => failedEventIds.has(eventId)),
    };
}

export async function markActivityEventsAddressedByOrganization(
    events: ReadonlyArray<ActivityEventOrganizationRef>,
    note?: string,
) {
    return updateActivityEventsByOrganization(
        events,
        (organizationId, eventIds) =>
            markActivityEventsAddressed(organizationId, eventIds, note),
    );
}

export async function markActivityEventsUnaddressedByOrganization(
    events: ReadonlyArray<ActivityEventOrganizationRef>,
) {
    return updateActivityEventsByOrganization(
        events,
        markActivityEventsUnaddressed,
    );
}

export async function markActivityEventUnaddressed(
    organizationId: string,
    eventId: string,
) {
    return getApiClient().delete({
        url: '/organizations/{organization_id}/activity-events/{event_id}/addressed',
        pathParams: {
            organization_id: organizationId,
            event_id: eventId,
        },
        reason: 'activity-event-mark-unaddressed',
    });
}

export async function markActivityEventRead(
    organizationId: string,
    eventId: string,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/activity-events/{event_id}/read',
        pathParams: {
            organization_id: organizationId,
            event_id: eventId,
        },
        body: undefined,
        reason: 'activity-event-mark-read',
    });
}

export async function markActivityEventUnread(
    organizationId: string,
    eventId: string,
) {
    return getApiClient().post({
        url: '/organizations/{organization_id}/activity-events/{event_id}/unread',
        pathParams: {
            organization_id: organizationId,
            event_id: eventId,
        },
        body: undefined,
        reason: 'activity-event-mark-unread',
    });
}

// Per-org tag overrides for the Settings → Tag Matching tab. Lists
// canonical (Phoebe-default) groups + the agency's overrides for each
// tag the agency uses. See services/api/routes/organizations/
// tag_overrides.py for backend contract.
export function useOrgTagOverrides(
    organizationId: string | null,
    traceParams: TraceParams,
) {
    return useApiQuery({
        path: '/organizations/{organization_id}/tag-overrides',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response.rows,
        reason: traceParams.reason,
    });
}

// Per-org account-health metrics (daily rows + pooled window summary + SMS
// response by workflow). Backed by the LIVE-only Postgres metric functions.
export function useAccountHealthMetrics(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/admin/account-health-metrics/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate },
        enabled: !!organizationId,
        select: (response) => response,
    });
}

// Raw counts for the admin Report Generator (LIVE-only, org-local dates).
export function useAdminOrganizationReport(
    organizationId: string | null,
    startDate: string,
    endDate: string,
    compare = false,
) {
    return useApiQuery({
        path: '/admin/organization-report/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate, compare },
        enabled: !!organizationId,
        keepPreviousData: false,
        select: (response) => response,
    });
}

// Across-orgs account-health: one pooled row per org plus a grand total.
export function useAcrossOrgsAccountHealthMetrics(
    startDate: string,
    endDate: string,
    enabled = true,
    organizationIds?: ReadonlyArray<string>,
) {
    return useApiQuery({
        path: '/admin/account-health-metrics',
        method: 'get',
        queryParams: {
            start_date: startDate,
            end_date: endDate,
            // Scope the fleet-wide computation to the displayed orgs; the
            // unscoped query times out on production data.
            ...(organizationIds && organizationIds.length > 0
                ? { organization_ids: [...organizationIds] }
                : {}),
        },
        enabled,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Daily ROI-report metric series for one org (LIVE-only, org-local days);
// the window totals match the customer-facing ROI report.
export function useOrganizationRoiMetrics(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/admin/roi-metrics/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Daily engagement series (caregiver engagement, scheduler adoption,
// Phoebe chats, web/scheduler-admin active users, and callout starts) for one
// org (LIVE-only, org-local dates).
export function useOrganizationEngagementMetrics(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/admin/engagement-metrics/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Request-grain judge output aggregates (relationships, use-case→outcome
// flow, independent failure dimensions, workflow edges).
export function useEpisodeRequestItems(
    startTs: string,
    endTs: string,
    // Viewer IANA timezone: aligns the workflow daily buckets with the
    // picked calendar days.
    tz: string,
) {
    return useApiQuery({
        path: '/admin/episode-classification/request-items',
        method: 'get',
        queryParams: { start_ts: startTs, end_ts: endTs, tz },
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Newest judged trigger messages with scheduler, snippets, and chips —
// the per-message review table on the Agent Analysis page.
export function useEpisodeRecentRequests(
    startTs: string,
    endTs: string,
    limit: number,
) {
    return useApiQuery({
        path: '/admin/episode-classification/recent-requests',
        method: 'get',
        queryParams: { start_ts: startTs, end_ts: endTs, limit },
        keepPreviousData: true,
        refreshInterval: 60_000,
        select: (response) => response,
    });
}

// Fleet-wide agent usage & gap analysis across all non-internal orgs
// (same shape as the per-org endpoint, LIVE-only, org-local dates).
export function useGlobalAgentAnalysis(startDate: string, endDate: string) {
    return useApiQuery({
        path: '/admin/agent-analysis',
        method: 'get',
        queryParams: { start_date: startDate, end_date: endDate },
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Per-org agent usage & gap analysis (ask→outcome flow, gap evidence,
// says-vs-does examples) from the LLM-judge reviews (LIVE-only,
// org-local dates).
export function useOrganizationAgentAnalysis(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/admin/agent-analysis/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Recent structured unmet-demand items for an org, matching the issues
// surfaced to the internal #product-agent Slack channel.
export function useOrganizationUnmetDemand(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/agent-analysis/{organization_id}/unmet-demand',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

// Quick Health daily series across all orgs with in-window rows or open
// alerts (LIVE-only, org-local dates).
export function useQuickHealthMetrics(startDate: string, endDate: string) {
    return useApiQuery({
        path: '/admin/quick-health-metrics',
        method: 'get',
        queryParams: { start_date: startDate, end_date: endDate },
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Single-org Quick Health series; returns ramp anchors even when the org has
// no scored rows in the window.
export function useOrganizationQuickHealthMetrics(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/admin/quick-health-metrics/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Per-organization VOIP health for the admin account page: daily call
// volume, transfer signals, AI QA judge verdicts, and the transfer
// destination distribution (LIVE-only).
export function useOrganizationVoipHealthMetrics(
    organizationId: string | null,
    startDate: string,
    endDate: string,
) {
    return useApiQuery({
        path: '/admin/voip-health-metrics/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { start_date: startDate, end_date: endDate },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Cross-organization voice config matrix plus the branch runtime kill-switch
// state. Only orgs with voice settings or branches are returned (LIVE-only).
export function useAdminVoiceConfigOrganizations() {
    return useApiQuery({
        path: '/admin/voice-config/organizations',
        method: 'get',
        select: (response) => response,
    });
}

// One org's effective voice config with per-field provenance, quality-mode
// state, and a lightweight summary of its branches.
export function useAdminOrganizationVoiceConfig(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/voice-config',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
    });
}

// Full branch rows (overrides, traffic, lifecycle timestamps, 7-day call
// counts) for the branches tab.
export function useAdminVoiceConfigBranches(
    organizationId: string | null,
    includeArchived: boolean,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/voice-config/branches',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { include_archived: includeArchived },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Server-computed diff of one branch's overrides against the organization's
// current main configuration.
export function useAdminVoiceConfigBranchDiff(
    organizationId: string | null,
    branchId: string | null,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/voice-config/branches/{branch_id}/diff',
        method: 'get',
        pathParams:
            organizationId && branchId
                ? { organization_id: organizationId, branch_id: branchId }
                : undefined,
        enabled: !!organizationId && !!branchId,
        select: (response) => response,
    });
}

// Per-cohort call and latency rollups over the last `days` days; the row with
// a null `branch_id` is the organization's main configuration.
export function useAdminVoiceConfigBranchMetrics(
    organizationId: string | null,
    days: number,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/voice-config/branches/metrics',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { days },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

export function useAdminSandboxOrganizations() {
    return useApiQuery({
        path: '/admin/sandbox',
        method: 'get',
        select: (response) => response,
        reason: 'admin-sandbox-organizations',
        refreshInterval: 30_000,
    });
}

export function useAdminSandboxOrganization(organizationId: string | null) {
    return useApiQuery({
        path: '/admin/sandbox/{organization_id}',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'admin-sandbox-organization',
    });
}

export function useAdminSandboxTelemetry(params: {
    readonly days: 1 | 7 | 30;
    readonly organizationId?: string | null;
}) {
    const { days, organizationId } = params;
    return useApiQuery({
        path: '/admin/sandbox/telemetry',
        method: 'get',
        queryParams: {
            days,
            organization_id: organizationId ?? undefined,
        },
        select: (response) => response,
        reason: 'admin-sandbox-telemetry',
        keepPreviousData: true,
    });
}

export type AdminSandboxCallSort = 'created_at' | 'cost_usd' | 'duration_ms';

export function useAdminSandboxCalls(params: {
    readonly organizationId: string | null;
    readonly limit?: number;
    readonly days?: number;
    readonly sort?: AdminSandboxCallSort;
}) {
    const { organizationId, limit, days, sort } = params;
    const queryParams: {
        limit?: number;
        days?: number;
        sort?: AdminSandboxCallSort;
    } = {};
    if (limit !== undefined) {
        queryParams.limit = limit;
    }
    if (days !== undefined) {
        queryParams.days = days;
    }
    if (sort !== undefined) {
        queryParams.sort = sort;
    }
    return useApiQuery({
        path: '/admin/sandbox/{organization_id}/calls',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams:
            Object.keys(queryParams).length > 0 ? queryParams : undefined,
        enabled: !!organizationId,
        select: (response) => response,
        reason: 'admin-sandbox-calls',
    });
}

// Bucketed agent-response latency per cohort. The server picks hourly buckets
// for short windows and daily buckets otherwise.
export function useAdminVoiceConfigLatencyTimeseries(
    organizationId: string | null,
    days: number,
) {
    return useApiQuery({
        path: '/admin/organizations/{organization_id}/voice-config/latency-timeseries',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: { days },
        enabled: !!organizationId,
        keepPreviousData: true,
        select: (response) => response,
    });
}

// Per-turn caller-stop to agent-audio latency for one call. The summary is
// the stamped call metadata when present, otherwise recomputed on read.
export function useAdminVoiceCallLatency(callId: string | null) {
    return useApiQuery({
        path: '/admin/voice/calls/{call_id}/latency',
        method: 'get',
        pathParams: callId ? { call_id: callId } : undefined,
        enabled: !!callId,
        select: (response) => response,
    });
}

// Admin scratchpad SWR hooks — canonical entry point for feature callers.
// Implementations live in the feature module so tests and internal wiring
// stay colocated, but the vault UI is expected to reach them through the
// central data-fetching file per repo convention.
export {
    type AdminScratchpadIndexParams,
    useAdminAgentScratchpadIndex,
    useAdminAgentScratchpadNote,
    useAdminAgentScratchpadNoteRevisions,
    useAdminAgentScratchpadNotesForRun,
} from '@apps/web/features/admin_agent_scratchpad/api/queries';

export function useAdminSandboxRunFiles(runId: string | null) {
    return useApiQuery({
        path: '/admin/sandbox/runs/{run_id}/files',
        method: 'get',
        pathParams: runId ? { run_id: runId } : undefined,
        enabled: !!runId,
        select: (response) => response,
        reason: 'admin-sandbox-run-files',
    });
}
